import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  Activity,
  AlertTriangle,
  Briefcase,
  Calculator,
  CheckCircle2,
  Database,
  DollarSign,
  Download,
  Gauge,
  History,
  Lock,
  RefreshCw,
  ShieldCheck,
  SlidersHorizontal,
  UploadCloud,
  User,
  XCircle
} from 'lucide-react';
import apiService, { apiActor } from '../services/apiService';
import {
  FORECAST_USERS,
  STATUS_LABELS,
  getActiveForecastUser,
  setActiveForecastUser
} from './forecasting/forecastUsers';
import {
  ADJUSTMENT_YEARS,
  DEFAULT_FEE_DRIVERS,
  DEFAULT_WORKFORCE_YEARS,
  DEFAULT_YEAR_ADJUSTMENTS,
  WORKFORCE_YEAR_DEFS,
  YEAR_LEVER_DEFS,
  applyYearAdjustments,
  buildNewProductionMonthly,
  ftpRateFor,
  splitNiiFtp
} from './forecasting/forecastAdjustments';
import ForecastOp03Suite from './forecasting/ForecastOp03Suite';
import ForecastContextNotes from './forecasting/ForecastContextNotes';
import {
  ForecastChangeDrawer,
  ForecastReadinessBadge,
  ForecastValidationList,
  ForecastWorkspaceShell,
  WORKSPACE_READINESS,
  useUnsavedChangesWarning,
  useWorkspaceStage
} from './forecasting/ForecastWorkspace';
import ForecastPromptBox from './forecasting/ForecastPromptBox';
import ForecastHireRows from './forecasting/ForecastHireRows';
import ForecastNiiBuildup from './forecasting/ForecastNiiBuildup';
import ForecastTimeMatrix from './forecasting/ForecastTimeMatrix';
import ForecastAllocation from './forecasting/ForecastAllocation';
import NumericInput from './forecasting/NumericInput';
import ForecastHrRoster, { DEFAULT_HR_PLAN, rosterCompByYear } from './forecasting/ForecastHrRoster';
import ForecastGrowthMatrix from './forecasting/ForecastGrowthMatrix';
import RegionalWorkspaceNotices from './forecasting/stages/RegionalWorkspaceNotices';
import RegionalBaselineStage from './forecasting/stages/RegionalBaselineStage';
import RegionalContextStage from './forecasting/stages/RegionalContextStage';
import RegionalLeversStage from './forecasting/stages/RegionalLeversStage';
import RegionalAiPlanningTools from './forecasting/RegionalAiPlanningTools';
import RegionalWorkforceStage from './forecasting/stages/RegionalWorkforceStage';
import RegionalResultsStage from './forecasting/stages/RegionalResultsStage';
import RegionalReviewStage from './forecasting/stages/RegionalReviewStage';
import { evaluateQualityGates } from './forecasting/forecastQualityGates';
import {
  FORECAST_IMPACT_LINE_DEFS,
  FORECAST_LEVER_PRIMARY_FIELDS,
  buildAiForecastProvenanceByAccount,
  buildForecastImpactDiff,
  buildDriverViewPlanSnapshot,
  buildDriverViewDeltas,
  buildDriverViewNarrativeTriggers,
  calculateInputProgress,
  calculateHydratedLiveImpactSummary,
  resolveForecastProvenance,
  detectMacroScenarioStaleness,
  buildMacroVariableDiff
} from './forecasting/forecastImpact';
import ForecastImpactPreviewTable from './forecasting/ForecastImpactPreviewTable';
import {
  DEFAULT_QUARTERLY_GROWTH,
  describeQuarterlyGrowthConfig,
  hasQuarterlyGrowthChanges,
  normalizeQuarterlyGrowth,
  remainingYearQuarterMonthCounts,
  resolveQuarterValues,
  summarizeQuarterlyGrowthProfile
} from './forecasting/forecastQuarterlyGrowth';
import { PROFILE_TO_QUARTER_PRESET } from './forecasting/forecastSeasonality';
import {
  BASE_CREDIT_LOSS_RATE,
  FORECAST_YEARS,
  MACRO_SENSITIVITY_COEFFICIENTS,
  MONTH_LABELS,
  buildAnnualFromMonthly,
  buildMonthlyProjection,
  deriveBridgeOpeningPortfolio,
  deriveLatestActualMonth
} from './forecasting/forecastProjection';

const MARKETS = [
  {
    id: 'ga-atlanta',
    region: 'Georgia',
    market: 'Atlanta',
    planningEntity: 'PLN_GA_ATLANTA',
    financeLead: 'Jordan Avery'
  },
  {
    id: 'tn-nashville',
    region: 'Tennessee',
    market: 'Nashville',
    planningEntity: 'PLN_TN_NASHVILLE',
    financeLead: 'Priya Raman'
  },
  {
    id: 'fl-tampa',
    region: 'Florida',
    market: 'Tampa Bay',
    planningEntity: 'PLN_FL_TAMPA_BAY',
    financeLead: 'Marcus Hill'
  },
  {
    id: 'specialty-healthcare',
    region: 'Specialty Bank',
    market: 'Healthcare Vertical',
    planningEntity: 'PLN_SPEC_HEALTHCARE',
    financeLead: 'Elena Morris'
  }
];

const FPNA_USERS = [
  'Jordan Avery - FP&A',
  'Priya Raman - FP&A',
  'Marcus Hill - FP&A',
  'Elena Morris - FP&A'
];

const FIELD_LABELS = {
  grossHires: 'Gross hires',
  expectedDepartures: 'Expected departures',
  vacancyOverride: 'Vacancy override',
  attritionOverride: 'Attrition override',
  rateShockBps: 'Rate shock',
  gdpShock: 'GLP / GDP shock',
  creditStressBps: 'Credit stress',
  liquidityRunoffPct: 'Liquidity runoff',
  expenseShockPct: 'Key vendor inflation',
  wageShockPct: 'Wage pressure',
  depositBetaShockPct: 'Deposit beta / pricing lag',
  loanGrowthLimiterPct: 'Loan growth limiter',
  existingProducerFte: 'Existing producer FTE',
  supportFte: 'Support FTE',
  vacancyFactorPct: 'Existing workforce vacancy',
  meritIncreasePct: 'Merit increase',
  incentiveAccrualPct: 'Incentive accrual',
  productivityLiftPct: 'Productivity lift',
  loanRetentionPct: 'Loan book retention',
  depositRetentionPct: 'Deposit book retention'
};

const OFFICER_TYPES = [
  {
    id: 'commercial',
    type: 'Financial Advisor - Commercial / Middle Market',
    attrition: 8.5,
    vacancy: 6.5,
    rampMonths: 18,
    annualLoan: 18,
    annualDeposit: 13,
    annualFee: 0.48,
    annualExpense: 0.36,
    lineMix: 28,
    lineUtilization: 42,
    loanSpreadBps: 255,
    lineSpreadBps: 210
  },
  {
    id: 'privateWealth',
    type: 'Financial Advisor - Private Wealth',
    attrition: 7.2,
    vacancy: 5.5,
    rampMonths: 24,
    annualLoan: 9,
    annualDeposit: 18,
    annualFee: 0.78,
    annualExpense: 0.42,
    lineMix: 12,
    lineUtilization: 35,
    loanSpreadBps: 210,
    lineSpreadBps: 175
  },
  {
    id: 'treasury',
    type: 'Treasury Sales Officer',
    attrition: 6.8,
    vacancy: 5,
    rampMonths: 15,
    annualLoan: 1.5,
    annualDeposit: 24,
    annualFee: 0.64,
    annualExpense: 0.31,
    lineMix: 8,
    lineUtilization: 30,
    loanSpreadBps: 185,
    lineSpreadBps: 160
  },
  {
    id: 'wealthAdvisor',
    type: 'Wealth Advisor',
    attrition: 7.8,
    vacancy: 6,
    rampMonths: 24,
    annualLoan: 2.5,
    annualDeposit: 11,
    annualFee: 1.12,
    annualExpense: 0.46,
    lineMix: 5,
    lineUtilization: 25,
    loanSpreadBps: 175,
    lineSpreadBps: 145
  },
  {
    id: 'officeLeader',
    type: 'Office Leader / Branch Leader',
    attrition: 5.5,
    vacancy: 4.2,
    rampMonths: 12,
    annualLoan: 4,
    annualDeposit: 22,
    annualFee: 0.26,
    annualExpense: 0.39,
    lineMix: 18,
    lineUtilization: 36,
    loanSpreadBps: 205,
    lineSpreadBps: 165
  }
];

const INITIAL_MARKET_INPUTS = {
  commercial: { grossHires: 4, expectedDepartures: 1, vacancyOverride: 7.5, attritionOverride: 8.8 },
  privateWealth: { grossHires: 2, expectedDepartures: 0, vacancyOverride: 5.8, attritionOverride: 7.4 },
  treasury: { grossHires: 1, expectedDepartures: 0, vacancyOverride: 4.9, attritionOverride: 6.8 },
  wealthAdvisor: { grossHires: 2, expectedDepartures: 1, vacancyOverride: 6.2, attritionOverride: 7.8 },
  officeLeader: { grossHires: 1, expectedDepartures: 0, vacancyOverride: 4.2, attritionOverride: 5.5 }
};

// One row per individual officer so each hire is timed and shaped on its own.
const INITIAL_HIRE_ROWS = Object.entries(INITIAL_MARKET_INPUTS)
  .filter(([, value]) => value.grossHires > 0)
  .flatMap(([typeId, value]) =>
    Array.from({ length: value.grossHires }, (_, index) => ({
      id: `seed-${typeId}-${index + 1}`,
      typeId,
      name: `${typeId.charAt(0).toUpperCase()}${typeId.slice(1)} hire #${index + 1}`,
      fte: 1,
      startMonth: 1,
      assumptions: {},
      perYear: {}
    }))
  );

const INITIAL_EXPENSE_LEVERS = {
  travel: {
    label: 'Travel & Entertainment',
    value: 1.8,
    centralBudget: 2,
    centralLimit: 2.4,
    unit: 'currency',
    gl: '70120'
  },
  businessDevelopment: {
    label: 'Business Development',
    value: 2.6,
    centralBudget: 2.7,
    centralLimit: 3.1,
    unit: 'currency',
    gl: '70140'
  },
  community: {
    label: 'Community Investment / Donations',
    value: 0.9,
    centralBudget: 0.9,
    centralLimit: 1.2,
    unit: 'currency',
    gl: '70480'
  },
  legalRecoupment: {
    label: 'Deal Legal Expense Recoupment',
    value: 58,
    centralBudget: 55,
    centralLimit: 65,
    grossExpense: 3.6,
    unit: 'percent',
    gl: '60960'
  }
};

const INITIAL_WORKFORCE_LEVERS = {
  existingProducerFte: {
    label: 'Existing producer FTE',
    value: 142,
    baseline: 142,
    min: 110,
    max: 175,
    step: 1,
    unit: 'count'
  },
  supportFte: {
    label: 'Support / product partner FTE',
    value: 47,
    baseline: 47,
    min: 32,
    max: 62,
    step: 1,
    unit: 'count'
  },
  vacancyFactorPct: {
    label: 'Existing workforce vacancy factor',
    value: 5.5,
    baseline: 5.5,
    min: 0,
    max: 12,
    step: 0.25,
    unit: 'percent'
  },
  meritIncreasePct: {
    label: 'Merit / wage increase',
    value: 3.6,
    baseline: 3.5,
    min: 0,
    max: 7,
    step: 0.1,
    unit: 'percent'
  },
  incentiveAccrualPct: {
    label: 'Incentive accrual',
    value: 18.5,
    baseline: 18,
    min: 8,
    max: 30,
    step: 0.5,
    unit: 'percent'
  },
  productivityLiftPct: {
    label: 'Existing banker productivity lift',
    value: 1.7,
    baseline: 1.5,
    min: -4,
    max: 6,
    step: 0.1,
    unit: 'percent'
  },
  loanRetentionPct: {
    label: 'Existing loan book retention',
    value: 94.5,
    baseline: 94,
    min: 85,
    max: 99,
    step: 0.25,
    unit: 'percent'
  },
  depositRetentionPct: {
    label: 'Existing deposit book retention',
    value: 92.4,
    baseline: 92,
    min: 82,
    max: 99,
    step: 0.25,
    unit: 'percent'
  }
};

const MACRO_SETS = {
  Base: {
    label: 'Base',
    sofr: 4.85,
    gdp: 2.1,
    inflation: 2.6,
    unemployment: 4.1,
    depositBeta: 38,
    creditLoss: BASE_CREDIT_LOSS_RATE,
    loanDemand: 1,
    prepayment: 8.5,
    depositRunoff: 3.8,
    expenseInflation: 3.1,
    wageInflation: 3.8
  },
  Upside: {
    label: 'Upside',
    sofr: 4.45,
    gdp: 2.8,
    inflation: 2.3,
    unemployment: 3.8,
    depositBeta: 34,
    creditLoss: 0.34,
    loanDemand: 1.08,
    prepayment: 9.8,
    depositRunoff: 2.9,
    expenseInflation: 2.6,
    wageInflation: 3.4
  },
  Downside: {
    label: 'Downside',
    sofr: 3.85,
    gdp: 0.6,
    inflation: 3.1,
    unemployment: 5.2,
    depositBeta: 48,
    creditLoss: 0.78,
    loanDemand: 0.86,
    prepayment: 6.2,
    depositRunoff: 6.4,
    expenseInflation: 4.2,
    wageInflation: 4.6
  },
  'Custom Shock': {
    label: 'Custom Shock',
    sofr: 4.85,
    gdp: 1.4,
    inflation: 3,
    unemployment: 4.7,
    depositBeta: 44,
    creditLoss: 0.62,
    loanDemand: 0.94,
    prepayment: 7.1,
    depositRunoff: 5.4,
    expenseInflation: 3.9,
    wageInflation: 4.4
  }
};

const SOURCE_BADGES = {
  actuals: { label: 'GL actuals', tone: 'moss' },
  central: { label: 'Central baseline', tone: 'neutral' },
  macro: { label: 'Macro shock', tone: 'amber' },
  market: { label: 'Market input', tone: 'gold' },
  fpa: { label: 'FP&A override', tone: 'gold' },
  live: { label: 'Live feed', tone: 'moss' },
  mock: { label: 'Mock API', tone: 'amber' }
};

const BASE_PORTFOLIO = {
  loans: 46200,
  deposits: 50500,
  loanYield: 6.48,
  depositCost: 2.47,
  loanRepricingBeta: 58,
  depositRateFloor: 0.15,
  wholesaleFundingSpreadBps: 80,
  depositFundingBenefit: 0,
  nii: 1748,
  nonInterestIncome: 512,
  nonInterestExpense: 1542,
  provision: 162,
  taxRate: 23.5,
  netIncome: 401
};

const MODEL_VERSION = 'regional-forecast-v0.2';
const UNUSED_COMMITMENT_FEE_RATE = 0.35;
const GL_FACT_VALUE_SCALE = 1000000;
const MONTH_NAME_ABBRS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FORECAST_DATA_SOURCE = 'RegionalForecastModule';

const DEPOSIT_LOAD_MIX = [
  { account: '219', line: 'Ending Deposits - Transaction', share: 0.35 },
  { account: '229', line: 'Ending Deposits - Savings', share: 0.45 },
  { account: '239', line: 'Ending Deposits - Time', share: 0.2 }
];

const EXPENSE_LOAD_MIX = [
  { account: '609', line: 'Personnel Compensation', share: 0.55 },
  { account: '619', line: 'Employee Benefits', share: 0.12 },
  { account: '659', line: 'Professional Services / Legal', share: 0.1 },
  { account: '709', line: 'Business Development', share: 0.04 },
  { account: '759', line: 'Travel and Entertainment', share: 0.03 },
  { account: '779', line: 'Community Investment / Donations', share: 0.02 },
  { account: '789', line: 'Other Operating Expense', share: 0.14 }
];

const toCurrencyDollars = (valueMillions) =>
  Math.round((Number(valueMillions) || 0) * GL_FACT_VALUE_SCALE * 100) / 100;

const getForecastPeriodParts = (monthRow) => {
  const monthNumber = Number(String(monthRow.month || '').replace('M', '')) || 1;
  const monthIndex = Math.max(0, Math.min(11, monthNumber - 1));
  const periodDate = new Date(Date.UTC(monthRow.fiscalYearNumber, monthNumber, 0)).toISOString().slice(0, 10);

  return {
    period: `${MONTH_NAME_ABBRS[monthIndex]}.${monthRow.fiscalYearNumber}`,
    periodDate,
    monthKey: periodDate
  };
};

// Policy decision (2026-06-12 audit): scenario-version names use the
// deterministic underscore convention already established in the cube
// (e.g. MARKET_SUBMITTED_COMMERCIAL_BANKING_ATLANTA_BASE) — built from
// scenario + division + market + macro mode. The earlier pipe-delimited
// format would have forked a second cube version on reload instead of
// replacing the existing one (review-console load keys on this string).
const sanitizeVersionToken = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const buildScenarioVersion = (scenario, market, macroMode) =>
  [
    sanitizeVersionToken(scenario),
    sanitizeVersionToken(market.region),
    sanitizeVersionToken(market.market),
    sanitizeVersionToken(macroMode)
  ].join('_');

// Planning write-back sign standard (2026-06-11 audit decision): planning
// versions in GL_Fact store POSITIVE MAGNITUDES for every line — the same
// convention as the SeedScript versions (Actual / priors / budgets) that
// the comparison layer consumes, whose arithmetic subtracts expense-type
// rollups (netIncome = nii + fees - expense - provision - tax). The signed
// enterprise convention (liabilities/expenses negative) is applied only at the
// enterprise-cube export boundary, never inside the planning data path.
// Previously deposits/IE/NIE/provision/tax were negated here, which would have
// double-flipped the day a loaded version was reused as a prior/budget version.
const buildForecastLoadRows = (monthlyRows, provenanceByAccount = {}) =>
  monthlyRows.flatMap((monthRow) => {
    const periodParts = getForecastPeriodParts(monthRow);
    const common = {
      ...periodParts,
      status: 'Validated'
    };

    const rows = [
      {
        account: '139',
        line: 'Ending Loans',
        module: 'Balance sheet engine',
        financeMeasure: 'EOP',
        value: monthRow.endingLoans
      },
      ...DEPOSIT_LOAD_MIX.map((depositLine) => ({
        account: depositLine.account,
        line: depositLine.line,
        module: 'Balance sheet engine',
        financeMeasure: 'EOP',
        value: monthRow.endingDeposits * depositLine.share
      })),
      {
        account: '419',
        line: 'Interest Income - Loans',
        module: 'Rate / portfolio engine',
        financeMeasure: 'MTD',
        value: monthRow.interestIncome
      },
      {
        account: '459',
        line: 'Interest Expense - Deposits',
        module: 'Rate / funding engine',
        financeMeasure: 'MTD',
        value: monthRow.interestExpense
      },
      {
        // Policy decision (2026-06-12 audit): the deposit funding benefit is an
        // explicit GL line so loaded versions tie to package net income exactly.
        // By model definition (applyYearAdjustments) it is nii − (II − IE).
        // 4290: first free ID — the whole 400-499 block is taken and 429 is
        // already "Interest Income - Securities" in the enterprise chart.
        account: '4290',
        line: 'Interest Income - Funding Benefit (FTP)',
        module: 'Rate / funding engine',
        financeMeasure: 'MTD',
        value: monthRow.nii - (monthRow.interestIncome - monthRow.interestExpense)
      },
      {
        account: '529',
        line: 'Fee Income - Advisory / Treasury',
        module: 'Fee engine',
        financeMeasure: 'MTD',
        value: monthRow.nonInterestIncome
      },
      ...EXPENSE_LOAD_MIX.map((expenseLine) => ({
        account: expenseLine.account,
        line: expenseLine.line,
        module: 'Expense engine',
        financeMeasure: 'MTD',
        value: monthRow.nonInterestExpense * expenseLine.share
      })),
      {
        account: '850',
        line: 'Provision for Credit Losses',
        module: 'Credit / macro stress',
        financeMeasure: 'MTD',
        value: monthRow.creditProvision
      },
      {
        account: '909',
        line: 'Income Tax Expense',
        module: 'Tax engine',
        financeMeasure: 'MTD',
        value: monthRow.taxExpense
      }
    ];

    return rows.map((row) => ({
      ...common,
      ...row,
      forecastProvenance: resolveForecastProvenance(row.account, provenanceByAccount),
      valueDollars: toCurrencyDollars(row.value)
    }));
  });

const MONTHLY_OVERRIDE_FIELDS = [
  'endingLoans',
  'endingDeposits',
  'interestIncome',
  'interestExpense',
  'fundingBenefit',
  'nonInterestIncome',
  'nonInterestExpense',
  'creditProvision',
  'taxExpense'
];

const metricOverrideKeyFor = (row) => row.period || `${row.fiscalYearNumber}-${row.month}`;

const applyMonthlyMetricOverrides = (monthlyRows, overrides = {}) =>
  monthlyRows.map((row, index) => {
    const key = metricOverrideKeyFor(row);
    const override = overrides[key] || overrides[row.period] || overrides[String(index)];
    if (!override) return row;

    const next = { ...row };
    MONTHLY_OVERRIDE_FIELDS.forEach((field) => {
      const value = Number(override[field]);
      if (Number.isFinite(value)) next[field] = value;
    });
    const fundingBenefit = Number.isFinite(Number(override.fundingBenefit))
      ? Number(override.fundingBenefit)
      : (next.nii || 0) - ((next.interestIncome || 0) - (next.interestExpense || 0));
    next.nii = (next.interestIncome || 0) - (next.interestExpense || 0) + fundingBenefit;
    next.netIncome =
      (next.nii || 0) +
      (next.nonInterestIncome || 0) -
      (next.nonInterestExpense || 0) -
      (next.creditProvision || 0) -
      (next.taxExpense || 0);
    next.excelOverride = true;
    return next;
  });

const glOverrideKeyFor = (row) => `${row.account}|${row.period}|${row.financeMeasure}`;

const applyGlRowOverrides = (rows, overrides = {}) =>
  rows.map((row) => {
    const key = glOverrideKeyFor(row);
    const override = overrides[key];
    const overrideValue = Number(override?.value);
    if (!override || !Number.isFinite(overrideValue)) return row;
    return {
      ...row,
      value: overrideValue,
      valueDollars: toCurrencyDollars(overrideValue),
      status: row.status === 'Blocked' ? row.status : 'Validated',
      excelOverride: true,
      overrideReason: override.reason || ''
    };
  });

const downloadWorkbookBlob = (response, fallbackName) => {
  const disposition = response.headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || fallbackName;
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const deriveBasePortfolioFromGl = (summaryMillions) => {
  if (!summaryMillions) return BASE_PORTFOLIO;

  const loans = Number(summaryMillions.loans);
  const deposits = Number(summaryMillions.deposits);
  // Zero is a VALID balance: deposit-only units (e.g. Branch Banking) carry no
  // loan book. Substituting the $46.2B reference-bank figure for a zero made
  // those units forecast at whole-bank scale. Fall back only when the feed
  // value is genuinely unusable (non-finite / negative).
  const safeLoans = Number.isFinite(loans) && loans >= 0 ? loans : BASE_PORTFOLIO.loans;
  const safeDeposits = Number.isFinite(deposits) && deposits >= 0 ? deposits : BASE_PORTFOLIO.deposits;
  if (safeLoans + safeDeposits <= 0) return BASE_PORTFOLIO; // no balance data at all

  const trailingInterestIncome = Number(summaryMillions.interestIncome);
  const trailingInterestExpense = Number(summaryMillions.interestExpense);
  const trailingNonInterestIncome = Number(summaryMillions.nonInterestIncome);
  const trailingNonInterestExpense = Number(summaryMillions.nonInterestExpense);
  const hasTrailingInterestIncome = Number.isFinite(trailingInterestIncome) && trailingInterestIncome >= 0;
  const hasTrailingInterestExpense = Number.isFinite(trailingInterestExpense) && trailingInterestExpense >= 0;
  const hasTrailingNir = Number.isFinite(trailingNonInterestIncome) && trailingNonInterestIncome >= 0;
  const hasTrailingNie = Number.isFinite(trailingNonInterestExpense) && trailingNonInterestExpense >= 0;

  const loanYield = hasTrailingInterestIncome && safeLoans > 0
    ? clamp((trailingInterestIncome / safeLoans) * 100, 0.1, 12)
    : BASE_PORTFOLIO.loanYield;
  const depositCost = hasTrailingInterestExpense && safeDeposits > 0
    ? clamp((trailingInterestExpense / safeDeposits) * 100, 0, 8)
    : BASE_PORTFOLIO.depositCost;

  const actualNii = (hasTrailingInterestIncome ? trailingInterestIncome : 0) -
    (hasTrailingInterestExpense ? trailingInterestExpense : 0);
  const modeledRateNii = safeLoans * (loanYield / 100) - safeDeposits * (depositCost / 100);
  const depositFundingBenefit =
    (hasTrailingInterestIncome || hasTrailingInterestExpense)
      ? actualNii - modeledRateNii
      : BASE_PORTFOLIO.depositFundingBenefit;

  // Scale missing P&L levels to the unit's balance footprint so a $3B market is
  // not carrying the expense base of a $46B reference bank. When GL actuals have
  // a trailing P&L line, use it as the calibration anchor instead of the generic
  // reference-bank level.
  const scale = clamp(
    (safeLoans + safeDeposits) / (BASE_PORTFOLIO.loans + BASE_PORTFOLIO.deposits),
    0.005,
    3
  );

  return {
    ...BASE_PORTFOLIO,
    loans: safeLoans,
    deposits: safeDeposits,
    loanYield,
    depositCost,
    depositFundingBenefit,
    nii: BASE_PORTFOLIO.nii * scale,
    nonInterestIncome: hasTrailingNir ? trailingNonInterestIncome : BASE_PORTFOLIO.nonInterestIncome * scale,
    nonInterestExpense: hasTrailingNie ? trailingNonInterestExpense : BASE_PORTFOLIO.nonInterestExpense * scale,
    provision: BASE_PORTFOLIO.provision * scale,
    netIncome: BASE_PORTFOLIO.netIncome * scale
  };
};

const PEER_PRO_FORMA_SET = [
  {
    peer: 'Regions Financial',
    source: 'Mock earnings call pro forma',
    roa: 1.14,
    rotce: 15.1,
    nim: 3.56,
    efficiency: 56.7,
    cet1: 10.5,
    loanGrowth: 2.6
  },
  {
    peer: 'Fifth Third',
    source: 'Mock earnings call pro forma',
    roa: 1.2,
    rotce: 16.4,
    nim: 3.42,
    efficiency: 54.8,
    cet1: 10.7,
    loanGrowth: 3.1
  },
  {
    peer: 'Citizens',
    source: 'Mock earnings call pro forma',
    roa: 1.05,
    rotce: 13.8,
    nim: 3.18,
    efficiency: 59.4,
    cet1: 10.2,
    loanGrowth: 2.1
  },
  {
    peer: 'M&T Bank',
    source: 'Mock earnings call pro forma',
    roa: 1.22,
    rotce: 15.9,
    nim: 3.62,
    efficiency: 55.5,
    cet1: 11.1,
    loanGrowth: 2.4
  },
  {
    peer: 'Synovus',
    source: 'Mock earnings call pro forma',
    roa: 1.12,
    rotce: 14.8,
    nim: 3.37,
    efficiency: 58.1,
    cet1: 10.4,
    loanGrowth: 3.4
  },
  {
    peer: 'Pinnacle',
    source: 'Mock earnings call pro forma',
    roa: 1.28,
    rotce: 17.2,
    nim: 3.49,
    efficiency: 53.9,
    cet1: 10.1,
    loanGrowth: 6.2
  }
];

const PEER_METRICS = [
  { key: 'roa', label: 'ROA', favorable: 'higher' },
  { key: 'rotce', label: 'ROTCE', favorable: 'higher' },
  { key: 'nim', label: 'NIM', favorable: 'higher' },
  { key: 'efficiency', label: 'Efficiency ratio', favorable: 'lower' },
  { key: 'cet1', label: 'CET1', favorable: 'higher' },
  { key: 'loanGrowth', label: 'Loan growth', favorable: 'balanced' }
];

const BASE_REVIEW_METRICS = [
  { key: 'endingLoans', label: 'Ending loans', type: 'balance' },
  { key: 'endingDeposits', label: 'Ending deposits', type: 'balance' },
  { key: 'nii', label: 'NII', type: 'flow' },
  { key: 'nonInterestIncome', label: 'Noninterest income', type: 'flow' },
  { key: 'nonInterestExpense', label: 'Noninterest expense', type: 'flow' },
  { key: 'creditProvision', label: 'Provision', type: 'flow' },
  { key: 'netIncome', label: 'Net income', type: 'flow' },
  { key: 'nim', label: 'NIM', type: 'rate' },
  { key: 'efficiencyRatio', label: 'Efficiency ratio', type: 'rate' }
];

const MACRO_DIVERGENCE_METRICS = [
  { key: 'sofr', label: 'SOFR', digits: 2 },
  { key: 'gdp', label: 'GLP / GDP', digits: 1 },
  { key: 'inflation', label: 'Inflation', digits: 1 },
  { key: 'unemployment', label: 'Unemployment', digits: 1 },
  { key: 'depositBeta', label: 'Deposit beta', digits: 0 },
  { key: 'creditLoss', label: 'Credit loss', digits: 2 },
  { key: 'depositRunoff', label: 'Deposit runoff', digits: 1 },
  { key: 'expenseInflation', label: 'Expense inflation', digits: 1 }
];

const tooltipStyle = {
  background: 'var(--ace-panel-strong)',
  border: '1px solid var(--ace-line-strong)',
  borderRadius: '12px',
  color: 'hsl(var(--foreground))'
};

const chartGridColor = 'hsl(var(--chart-grid))';
const chartAxisColor = 'hsl(var(--chart-axis))';

const formatCurrency = (value, digits = 0) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(value);

const formatMillions = (value, digits = 1) => `${formatCurrency(value, digits)}MM`;

const formatPercent = (value, digits = 1) => `${Number(value).toFixed(digits)}%`;

const formatSignedPercent = (value, digits = 1) =>
  `${Number(value) >= 0 ? '+' : ''}${Number(value).toFixed(digits)}%`;

const formatSignedPoints = (value, digits = 1) =>
  `${Number(value) >= 0 ? '+' : ''}${Number(value).toFixed(digits)} pts`;

const varianceTone = (value) => Number(value) > 0 ? 'positive' : Number(value) < 0 ? 'negative' : 'neutral';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clonePlainValue = (value) => (
  value === undefined ? undefined : JSON.parse(JSON.stringify(value))
);

const cloneYearAdjustments = (adjustments) => Object.fromEntries(
  Object.entries(adjustments || {}).map(([key, values]) => [key, Array.isArray(values) ? [...values] : values])
);

const isAcceptedAiSource = (source) => /\bAI\b/i.test(String(source || '')) && !/undo|revert/i.test(String(source || ''));

const formatLeverValue = (lever, value = lever.value) =>
  lever.unit === 'percent' ? formatPercent(value, 0) : formatMillions(value, 1);

const formatWorkforceValue = (lever, value = lever.value) => {
  if (lever.unit === 'percent') return formatPercent(value, 1);
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 1 });
};

const formatLineageValue = (field, value) => {
  const normalizedField = field.toLowerCase();
  if (normalizedField.includes('shock') && normalizedField.includes('rate')) return `${value} bps`;
  if (normalizedField.includes('stress') && normalizedField.includes('credit')) return `${value} bps`;
  if (
    normalizedField.includes('pct') ||
    normalizedField.includes('override') ||
    normalizedField.includes('recoupment') ||
    normalizedField.includes('runoff') ||
    normalizedField.includes('limiter') ||
    normalizedField.includes('vacancy') ||
    normalizedField.includes('merit') ||
    normalizedField.includes('incentive') ||
    normalizedField.includes('productivity') ||
    normalizedField.includes('retention') ||
    normalizedField.includes('expense pressure') ||
    normalizedField.includes('wage pressure')
  ) {
    return formatPercent(value, 1);
  }
  return value;
};

const getExpenseDelta = (lever) => {
  if (lever.unit === 'percent') return 0;
  return lever.value - lever.centralBudget;
};

const getLegalRecoupmentCredit = (lever) => lever.grossExpense * (lever.value / 100);

const getLegalRecoupmentDelta = (lever) =>
  lever.grossExpense * ((lever.value - lever.centralBudget) / 100);

const statusFor = (value, green, yellow, direction = 'higher') => {
  if (direction === 'lower') {
    if (value <= green) return 'green';
    if (value <= yellow) return 'yellow';
    return 'red';
  }

  if (value >= green) return 'green';
  if (value >= yellow) return 'yellow';
  return 'red';
};

const toBulletPercent = (value, min, max) => clamp(((value - min) / (max - min)) * 100, 0, 100);

const bandWidth = (band, min, max) => toBulletPercent(band.end, min, max) - toBulletPercent(band.start, min, max);

const higherIsBetterBands = (min, yellow, target, max) => [
  { label: 'Breach', start: min, end: yellow, tone: 'red' },
  { label: 'Watch', start: yellow, end: target, tone: 'yellow' },
  { label: 'Healthy', start: target, end: max, tone: 'green' }
];

const lowerIsBetterBands = (min, target, yellow, max) => [
  { label: 'Healthy', start: min, end: target, tone: 'green' },
  { label: 'Watch', start: target, end: yellow, tone: 'yellow' },
  { label: 'Breach', start: yellow, end: max, tone: 'red' }
];

const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  const midpoint = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[midpoint - 1] + sorted[midpoint]) / 2 : sorted[midpoint];
};

const getAnnualField = (annualRow, key) => {
  if (key === 'endingLoans') return annualRow.endingLoans;
  if (key === 'endingDeposits') return annualRow.endingDeposits;
  if (key === 'nim') return annualRow.nim;
  if (key === 'efficiencyRatio') return annualRow.efficiencyRatio;
  return annualRow[key];
};

const formatMetricValue = (value, type, digits = 1) => {
  if (type === 'rate') return formatPercent(value, digits);
  return formatMillions(value, type === 'balance' ? 0 : 1);
};

const statusIcon = {
  green: CheckCircle2,
  yellow: AlertTriangle,
  red: XCircle
};

const statusLabel = {
  green: 'Green',
  yellow: 'Yellow',
  red: 'Red'
};

const SourceBadge = ({ kind, children }) => {
  const badge = SOURCE_BADGES[kind] || { label: kind, tone: 'neutral' };

  return (
    <span className={`forecast-source-badge ${badge.tone}`}>
      <span aria-hidden="true" />
      {children || badge.label}
    </span>
  );
};

const buildWaterfallRows = ({ startName, startValue, startFill, deltas, endName, endValue, endFill }) => {
  let runningValue = startValue;
  const rows = [
    {
      name: startName,
      amount: startValue,
      cumulative: startValue,
      start: 0,
      end: startValue,
      kind: 'total',
      fill: startFill
    }
  ];

  deltas.forEach((delta) => {
    const start = runningValue;
    const end = runningValue + delta.value;

    rows.push({
      name: delta.name,
      amount: delta.value,
      cumulative: end,
      start,
      end,
      kind: delta.value >= 0 ? 'increase' : 'decrease',
      fill: delta.value >= 0 ? delta.fill : 'hsl(var(--destructive))'
    });

    runningValue = end;
  });

  rows.push({
    name: endName,
    amount: endValue,
    cumulative: endValue,
    start: 0,
    end: endValue,
    kind: 'total',
    fill: endFill
  });

  return rows;
};

const getWaterfallDomain = (rows) => {
  // Zoom to the data envelope (no forced zero) with a buffer so group
  // attribution floats render at full height; totals anchor to the floor.
  const values = rows.flatMap((row) => [row.start, row.end]);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const pad = Math.max((rawMax - rawMin) * 0.1, Math.abs(rawMax) * 0.02, 0.5);

  return {
    min: rawMin - pad,
    max: rawMax + pad * 0.8,
    span: Math.max(rawMax - rawMin + pad * 1.8, 1)
  };
};

const WaterfallChart = ({ rows, domain, ariaLabel, className = '' }) => (
  <div
    className={`forecast-waterfall-chart ${className}`}
    style={{
      '--waterfall-zero': `${((0 - domain.min) / domain.span) * 100}%`,
      '--waterfall-count': rows.length
    }}
    role="img"
    aria-label={ariaLabel}
  >
    <div className="forecast-waterfall-axis" aria-hidden="true">
      <span>{formatMillions(domain.max, 0)}</span>
      <span>{formatMillions(domain.min, 0)}</span>
    </div>
    <div className="forecast-waterfall-plot">
      {rows.map((row) => {
        const lowerValue = row.kind === 'total' ? domain.min : Math.min(row.start, row.end);
        const upperValue = Math.max(row.start, row.end);
        const bottom = ((lowerValue - domain.min) / domain.span) * 100;
        const height = Math.max(((upperValue - lowerValue) / domain.span) * 100, row.kind === 'total' ? 1.5 : 0.8);

        return (
          <div className="forecast-waterfall-column" key={row.name}>
            <span
              className={`forecast-waterfall-bar ${row.kind}`}
              style={{
                bottom: `${bottom}%`,
                height: `${height}%`,
                background: row.fill,
                opacity: row.dim ? 0.22 : 1
              }}
              title={`${row.name}: ${row.kind === 'total' ? 'value' : 'change'} ${formatMillions(row.amount)}; cumulative ${formatMillions(row.cumulative)}`}
            />
            <span className="forecast-waterfall-value" style={{ bottom: `${Math.min(bottom + height + 2, 93)}%` }}>
              {row.kind === 'total' ? formatMillions(row.amount, 0) : `${row.amount >= 0 ? '+' : ''}${formatMillions(row.amount, 1)}`}
            </span>
            <span className="forecast-waterfall-label">{row.name}</span>
          </div>
        );
      })}
    </div>
  </div>
);

const getManualWeight = (month) => {
  if (month <= 3) return 76;
  if (month <= 12) return Math.round(76 - (month - 3) * 4.7);
  if (month <= 24) return Math.round(34 - (month - 12) * 1.5);
  return 12;
};

const extractSofrRate = (payload) => {
  const queue = [payload];

  while (queue.length > 0) {
    const current = queue.shift();

    if (Array.isArray(current)) {
      queue.push(...current);
      continue;
    }

    if (!current || typeof current !== 'object') continue;

    const label = String(
      current.type ||
        current.rateType ||
        current.refRateType ||
        current.name ||
        current.description ||
        current.effectiveDate ||
        ''
    ).toLowerCase();

    const rawRate = current.percentRate ?? current.rate ?? current.value ?? current.avgRate;
    const parsedRate = Number(rawRate);

    if (label.includes('sofr') && Number.isFinite(parsedRate)) {
      return parsedRate;
    }

    Object.values(current).forEach((value) => {
      if (value && typeof value === 'object') queue.push(value);
    });
  }

  return null;
};

const calculatePortfolioBaselineModel = (macro, loanGrowthLimiterPct = 8.5, basePortfolio = BASE_PORTFOLIO) => {
  const rateDelta = macro.sofr - MACRO_SETS.Base.sofr;
  const loanYield = clamp(
    basePortfolio.loanYield + rateDelta * (basePortfolio.loanRepricingBeta / 100),
    3.75,
    9.5
  );
  const depositCost = Math.max(
    basePortfolio.depositRateFloor,
    basePortfolio.depositCost + rateDelta * (macro.depositBeta / 100)
  );
  const wholesaleFundingCost = macro.sofr + basePortfolio.wholesaleFundingSpreadBps / 100;
  const statisticalLoanGrowth = clamp(
    3.8 +
      macro.gdp * 0.62 -
      macro.creditLoss * 0.48 -
      Math.max(0, macro.unemployment - 4) * 0.42 -
      Math.max(0, macro.prepayment - 8.5) * 0.08,
    -2,
    9
  );
  const adjustedLoanGrowth = clamp(statisticalLoanGrowth, -3, loanGrowthLimiterPct);
  const statisticalDepositGrowth = clamp(
    3.1 + macro.gdp * 0.38 - macro.depositRunoff * 0.45 - Math.max(0, macro.unemployment - 4) * 0.18,
    -5,
    8
  );
  const adjustedDepositGrowth = statisticalDepositGrowth;
  const endingLoans = basePortfolio.loans * (1 + adjustedLoanGrowth / 100);
  const endingDeposits = basePortfolio.deposits * (1 + adjustedDepositGrowth / 100);
  const averageLoans = (basePortfolio.loans + endingLoans) / 2;
  const averageDeposits = (basePortfolio.deposits + endingDeposits) / 2;
  const interestIncome = averageLoans * (loanYield / 100);
  const interestExpense = averageDeposits * (depositCost / 100);
  const depositFundingBenefit = Number(basePortfolio.depositFundingBenefit) || 0;
  const nii = interestIncome - interestExpense + depositFundingBenefit;
  const nonInterestIncome = basePortfolio.nonInterestIncome;
  const expenseInflation =
    basePortfolio.nonInterestExpense * ((macro.expenseInflation + macro.wageInflation * 0.35) / 100);
  const nonInterestExpense = basePortfolio.nonInterestExpense + expenseInflation;
  const creditProvision =
    basePortfolio.provision +
    Math.max(0, averageLoans - basePortfolio.loans) * (macro.creditLoss / 100) +
    basePortfolio.loans * Math.max(0, macro.creditLoss - MACRO_SETS.Base.creditLoss) / 100;
  const preTaxIncome = nii + nonInterestIncome - nonInterestExpense - creditProvision;
  const taxExpense = Math.max(0, preTaxIncome * (basePortfolio.taxRate / 100));
  const netIncome = preTaxIncome - taxExpense;
  const efficiencyRatio = nonInterestExpense / (nii + nonInterestIncome);
  const nim = averageLoans > 0 ? nii / averageLoans : 0;
  const rwaGrowth = adjustedLoanGrowth * 0.82 + macro.creditLoss * 0.35;

  return {
    grossHires: 0,
    departures: 0,
    netHires: 0,
    loans12: 0,
    cappedLoans12: 0,
    deposits12: 0,
    fee12: 0,
    expense12: 0,
    unusedCommitmentFees: 0,
    newHireLoanInterestIncome: 0,
    depositFundingBenefit,
    legalRecoupmentCredit: 0,
    legalRecoupmentDelta: 0,
    controllableExpenseDelta: 0,
    statisticalLoanGrowth,
    statisticalDepositGrowth,
    adjustedLoanGrowth,
    adjustedDepositGrowth,
    endingLoans,
    endingDeposits,
    averageLoans,
    averageDeposits,
    loanYield,
    depositCost,
    wholesaleFundingCost,
    interestIncome,
    interestExpense,
    nii,
    nonInterestIncome,
    nonInterestExpense,
    creditProvision,
    preTaxIncome,
    taxExpense,
    netIncome,
    efficiencyRatio,
    nim,
    rwaGrowth
  };
};

const toMillions = (value) => Number(value || 0) / GL_FACT_VALUE_SCALE;

const actualMonthlyRowFromComparison = (row, previousRow) => {
  const [rawYear, rawMonth] = String(row.period || '').split('-');
  const fiscalYearNumber = Number(rawYear);
  const monthOfYear = Math.max(1, Math.min(12, Number(rawMonth) || 1));
  const monthIndex = monthOfYear - 1;
  const endingLoans = toMillions(row.loans);
  const endingDeposits = toMillions(row.deposits);
  const previousLoans = previousRow ? toMillions(previousRow.loans) : endingLoans;
  const previousDeposits = previousRow ? toMillions(previousRow.deposits) : endingDeposits;
  const averageLoans = (previousLoans + endingLoans) / 2;
  const averageDeposits = (previousDeposits + endingDeposits) / 2;
  const interestIncome = toMillions(row.interestIncome);
  const interestExpense = toMillions(row.interestExpense);
  const nii = toMillions(row.nii);
  const nonInterestIncome = toMillions(row.fees);
  const nonInterestExpense = toMillions(row.expense);
  const creditProvision = toMillions(row.provision);
  const taxExpense = toMillions(row.tax);
  const netIncome = toMillions(row.netIncome);

  return {
    label: 'Actuals',
    sourceType: 'actual',
    sourceLabel: 'Actuals',
    fiscalYear: `FY${fiscalYearNumber}`,
    fiscalYearNumber,
    monthNumber: monthOfYear,
    month: MONTH_LABELS[monthIndex],
    period: `FY${String(fiscalYearNumber).slice(2)} ${MONTH_LABELS[monthIndex]}`,
    endingLoans,
    endingDeposits,
    averageLoans,
    averageDeposits,
    loanYield: averageLoans > 0 ? (interestIncome * 12) / averageLoans * 100 : 0,
    depositCost: averageDeposits > 0 ? (interestExpense * 12) / averageDeposits * 100 : 0,
    interestIncome,
    interestExpense,
    nii,
    nonInterestIncome,
    nonInterestExpense,
    creditProvision,
    preTaxIncome: nii + nonInterestIncome - nonInterestExpense - creditProvision,
    taxExpense,
    netIncome,
    nim: averageLoans > 0 ? (nii * 12) / averageLoans * 100 : 0,
    efficiencyRatio: nii + nonInterestIncome > 0 ? (nonInterestExpense / (nii + nonInterestIncome)) * 100 : 0,
    annualLoanGrowth: 0,
    annualDepositGrowth: 0
  };
};

const buildActualMonthlyRows = (actualRows = []) => {
  const sortedRows = [...actualRows]
    .filter((row) => row?.period)
    .sort((a, b) => String(a.period).localeCompare(String(b.period)));

  return sortedRows.map((row, index) => actualMonthlyRowFromComparison(row, sortedRows[index - 1]));
};

const relabelForecastMonth = (row, fiscalYearNumber, monthOfYear, sourceType, sourceLabel) => ({
  ...row,
  label: sourceLabel,
  sourceType,
  sourceLabel,
  fiscalYear: `FY${fiscalYearNumber}`,
  fiscalYearNumber,
  monthNumber: monthOfYear,
  month: MONTH_LABELS[monthOfYear - 1],
  period: `FY${String(fiscalYearNumber).slice(2)} ${MONTH_LABELS[monthOfYear - 1]}`
});

const buildOp01MonthlySeries = (actualRows, forecastRows) => {
  const actualMonthlyRows = buildActualMonthlyRows(actualRows);
  const taggedForecastRows = forecastRows.map((row) => ({
    ...row,
    sourceType: row.sourceType || 'forecast',
    sourceLabel: row.sourceLabel || 'Forecast'
  }));
  if (!actualMonthlyRows.length) return taggedForecastRows;

  const latestActual = actualMonthlyRows[actualMonthlyRows.length - 1];
  const currentYear = latestActual.fiscalYearNumber;
  const currentMonth = latestActual.monthNumber;
  const actualYearCounts = actualMonthlyRows.reduce((counts, row) => {
    counts[row.fiscalYearNumber] = (counts[row.fiscalYearNumber] || 0) + 1;
    return counts;
  }, {});
  const completePriorActualYears = new Set(
    Object.entries(actualYearCounts)
      .filter(([year, count]) => Number(year) < currentYear && count >= 12)
      .map(([year]) => Number(year))
  );
  const priorFullActualRows = actualMonthlyRows.filter((row) => completePriorActualYears.has(row.fiscalYearNumber));
  const currentYearActualRows = actualMonthlyRows.filter((row) => row.fiscalYearNumber === currentYear);
  const forecastByMonth = new Map(
    taggedForecastRows.map((row) => [((row.monthNumber - 1) % 12) + 1, row])
  );
  const remainingCurrentYearRows = Array.from({ length: Math.max(12 - currentMonth, 0) }, (_, offset) => {
    const monthOfYear = currentMonth + offset + 1;
    const template = forecastByMonth.get(monthOfYear) || taggedForecastRows[monthOfYear - 1] || taggedForecastRows[0];
    return relabelForecastMonth(
      template,
      currentYear,
      monthOfYear,
      'currentYearForecast',
      'Forecast remainder'
    );
  });
  const futureForecastRows = taggedForecastRows.filter((row) => row.fiscalYearNumber > currentYear);

  return [
    ...priorFullActualRows,
    ...currentYearActualRows,
    ...remainingCurrentYearRows,
    ...futureForecastRows
  ];
};

const aggregateAnnualRows = (monthlyRows) => {
  const years = [...new Set(monthlyRows.map((row) => row.fiscalYearNumber))].sort((a, b) => a - b);
  return years.map((year) => {
    const rows = monthlyRows.filter((row) => row.fiscalYearNumber === year);
    const last = rows[rows.length - 1] || {};
    const totals = rows.reduce(
      (sum, row) => ({
        nii: sum.nii + row.nii,
        nonInterestIncome: sum.nonInterestIncome + row.nonInterestIncome,
        nonInterestExpense: sum.nonInterestExpense + row.nonInterestExpense,
        creditProvision: sum.creditProvision + row.creditProvision,
        taxExpense: sum.taxExpense + row.taxExpense,
        netIncome: sum.netIncome + row.netIncome,
        averageLoans: sum.averageLoans + row.averageLoans,
        averageDeposits: sum.averageDeposits + row.averageDeposits
      }),
      {
        nii: 0,
        nonInterestIncome: 0,
        nonInterestExpense: 0,
        creditProvision: 0,
        taxExpense: 0,
        netIncome: 0,
        averageLoans: 0,
        averageDeposits: 0
      }
    );
    const sourceTypes = new Set(rows.map((row) => row.sourceType || 'forecast'));
    const actualMonths = rows.filter((row) => row.sourceType === 'actual').length;
    const forecastMonths = rows.length - actualMonths;
    const sourceType = sourceTypes.size === 1 ? [...sourceTypes][0] : 'mixed';
    const avgLoans = totals.averageLoans / Math.max(rows.length, 1);
    const yearLabel = sourceType === 'actual'
      ? `FY${year}A`
      : sourceType === 'mixed'
        ? `FY${year} A+F`
        : `FY${year}F`;

    return {
      year: yearLabel,
      fiscalYearNumber: year,
      sourceType,
      actualMonths,
      forecastMonths,
      subLabel: sourceType === 'mixed'
        ? `${actualMonths} mo actual + ${forecastMonths} mo forecast`
        : sourceType === 'actual'
          ? 'full-year actual'
          : 'forecast',
      endingLoans: last.endingLoans || 0,
      endingDeposits: last.endingDeposits || 0,
      averageLoans: avgLoans,
      averageDeposits: totals.averageDeposits / Math.max(rows.length, 1),
      nii: totals.nii,
      nonInterestIncome: totals.nonInterestIncome,
      nonInterestExpense: totals.nonInterestExpense,
      creditProvision: totals.creditProvision,
      taxExpense: totals.taxExpense,
      netIncome: totals.netIncome,
      nim: avgLoans > 0 ? (totals.nii / avgLoans) * 100 : 0,
      efficiencyRatio:
        totals.nii + totals.nonInterestIncome > 0
          ? (totals.nonInterestExpense / (totals.nii + totals.nonInterestIncome)) * 100
          : 0,
      loanGrowth: last.annualLoanGrowth || 0,
      depositGrowth: last.annualDepositGrowth || 0
    };
  });
};

const buildMacroAnnualPath = (baseMacro, scenarioMacro) =>
  FORECAST_YEARS.map((year, index) => {
    const fade = 1 - index * 0.08;
    const cycleLift = index * 0.06;

    return {
      year: `FY${year}`,
      baseSofr: baseMacro.sofr - index * 0.08,
      scenarioSofr: scenarioMacro.sofr * fade + baseMacro.sofr * (1 - fade) - index * 0.08,
      baseGdp: baseMacro.gdp + cycleLift,
      scenarioGdp: scenarioMacro.gdp * fade + baseMacro.gdp * (1 - fade) + cycleLift,
      baseInflation: Math.max(1.8, baseMacro.inflation - index * 0.07),
      scenarioInflation: Math.max(1.8, scenarioMacro.inflation * fade + baseMacro.inflation * (1 - fade) - index * 0.07),
      baseUnemployment: baseMacro.unemployment + index * 0.04,
      scenarioUnemployment: scenarioMacro.unemployment * fade + baseMacro.unemployment * (1 - fade) + index * 0.04,
      baseDepositBeta: baseMacro.depositBeta - index * 0.8,
      scenarioDepositBeta: scenarioMacro.depositBeta * fade + baseMacro.depositBeta * (1 - fade) - index * 0.8,
      baseCreditLoss: baseMacro.creditLoss + index * 0.015,
      scenarioCreditLoss: scenarioMacro.creditLoss * fade + baseMacro.creditLoss * (1 - fade) + index * 0.015,
      baseDepositRunoff: baseMacro.depositRunoff + index * 0.05,
      scenarioDepositRunoff: scenarioMacro.depositRunoff * fade + baseMacro.depositRunoff * (1 - fade) + index * 0.05,
      baseExpenseInflation: Math.max(2.2, baseMacro.expenseInflation - index * 0.05),
      scenarioExpenseInflation: Math.max(
        2.2,
        scenarioMacro.expenseInflation * fade + baseMacro.expenseInflation * (1 - fade) - index * 0.05
      )
    };
  });

// Item G — FP&A adjustment layer helpers. The FP&A overlay lives entirely in
// forecast_submission_event 'adjust' rows tagged scenario 'FP&A Adjusted' (never
// in the market's submitted inputs). The most recent such event's leversAfter is
// the current overlay; folding is "latest wins" (undo records a reverting event).
const FPA_SCENARIO = 'FP&A Adjusted';

const normalizeLeverVector = (obj) => Object.fromEntries(
  Object.keys(DEFAULT_YEAR_ADJUSTMENTS).map((key) => [
    key,
    Array.isArray(obj?.[key])
      ? ADJUSTMENT_YEARS.map((_, i) => Number(obj[key][i]) || 0)
      : [...DEFAULT_YEAR_ADJUSTMENTS[key]]
  ])
);

const latestFpaOverlay = (events) => {
  const fpa = (events || []).filter(
    (event) => event.event === 'adjust' && event.payload && event.payload.scenario === FPA_SCENARIO
  );
  // get_submission returns events already sorted by created_at desc.
  return fpa.length ? fpa[0].payload : null;
};

const buildForecastImpactLines = (beforeMonthly, afterMonthly, years = ADJUSTMENT_YEARS) => {
  const aggregate = (monthly) => {
    const out = {};
    FORECAST_IMPACT_LINE_DEFS.forEach((def) => { out[def.field] = {}; });
    years.forEach((year) => {
      const rows = monthly.filter((row) => row.fiscalYearNumber === year);
      if (!rows.length) return;
      const last = rows[rows.length - 1];
      FORECAST_IMPACT_LINE_DEFS.forEach((def) => {
        out[def.field][year] = def.measure === 'EOP'
          ? Number(last[def.field]) || 0
          : rows.reduce((sum, row) => sum + (Number(row[def.field]) || 0), 0);
      });
    });
    return out;
  };
  const before = aggregate(beforeMonthly);
  const after = aggregate(afterMonthly);
  return FORECAST_IMPACT_LINE_DEFS.map((def) => ({
    field: def.field,
    label: def.label,
    measure: def.measure,
    byYear: Object.fromEntries(years.map((year) => [year, {
      old: (before[def.field] || {})[year] || 0,
      new: (after[def.field] || {})[year] || 0
    }]))
  }));
};

const buildRegionalForecastModel = ({
  activeMacro,
  basePortfolio,
  expenseInputs,
  hireRows,
  marketInputs,
  shocks,
  workforceInputs
}) => {
  const rateDelta = activeMacro.sofr - MACRO_SETS.Base.sofr;
  const loanYield = clamp(
    basePortfolio.loanYield + rateDelta * (basePortfolio.loanRepricingBeta / 100),
    3.75,
    9.5
  );
  const depositCost = Math.max(
    basePortfolio.depositRateFloor,
    basePortfolio.depositCost + rateDelta * (activeMacro.depositBeta / 100)
  );
  const wholesaleFundingCost = activeMacro.sofr + basePortfolio.wholesaleFundingSpreadBps / 100;

  // Aggregate individual hire cohorts (rows) up to officer-type economics.
  const hireAgg = OFFICER_TYPES.reduce((acc, type) => {
    acc[type.id] = { fte: 0, weighted: 0, loanSum: 0, depositSum: 0, feeSum: 0, expenseSum: 0, rampSum: 0, vacSum: 0, attrSum: 0 };
    return acc;
  }, {});
  hireRows.forEach((row) => {
    const agg = hireAgg[row.typeId];
    const type = OFFICER_TYPES.find((t) => t.id === row.typeId);
    if (!agg || !type) return;
    const fte = toFiniteNumber(row.fte, 0);
    if (fte <= 0) return;
    const typeInput = marketInputs[row.typeId] || {};
    const timing = Math.max(0, 12 - ((toFiniteNumber(row.startMonth, 1) || 1) - 1)) / 12;
    agg.fte += fte;
    agg.weighted += fte * timing;
    agg.vacSum += fte * toFiniteNumber(row.assumptions?.vacancyOverride, typeInput.vacancyOverride ?? type.vacancy);
    agg.attrSum += fte * toFiniteNumber(row.assumptions?.attritionOverride, typeInput.attritionOverride ?? type.attrition);
    agg.loanSum += fte * toFiniteNumber(row.assumptions?.annualLoan, type.annualLoan);
    agg.depositSum += fte * toFiniteNumber(row.assumptions?.annualDeposit, type.annualDeposit);
    agg.feeSum += fte * toFiniteNumber(row.assumptions?.annualFee, type.annualFee);
    agg.expenseSum += fte * toFiniteNumber(row.assumptions?.annualExpense, type.annualExpense);
    agg.rampSum += fte * toFiniteNumber(row.assumptions?.rampMonths, type.rampMonths);
  });

  const officerRows = OFFICER_TYPES.map((officerType) => {
    const agg = hireAgg[officerType.id];
    const officer = {
      ...officerType,
      annualLoan: agg.fte > 0 ? agg.loanSum / agg.fte : officerType.annualLoan,
      annualDeposit: agg.fte > 0 ? agg.depositSum / agg.fte : officerType.annualDeposit,
      annualFee: agg.fte > 0 ? agg.feeSum / agg.fte : officerType.annualFee,
      annualExpense: agg.fte > 0 ? agg.expenseSum / agg.fte : officerType.annualExpense,
      rampMonths: agg.fte > 0 ? agg.rampSum / agg.fte : officerType.rampMonths
    };
    const timingFactor = agg.fte > 0 ? agg.weighted / agg.fte : 1;
    const input = marketInputs[officer.id];
    const grossHiresFromRows = agg.fte;
    const netHires = grossHiresFromRows - input.expectedDepartures;
    const vacancy = agg.fte > 0 ? agg.vacSum / agg.fte : (input.vacancyOverride ?? officer.vacancy);
    const attrition = agg.fte > 0 ? agg.attrSum / agg.fte : (input.attritionOverride ?? officer.attrition);
    const vacancyAdjustedFte = Math.max(0, netHires) * (1 - vacancy / 100) * timingFactor;
    const rampFactor12 = clamp(12 / officer.rampMonths, 0.35, 1);
    const rampFactor24 = clamp(24 / officer.rampMonths, 0.55, 1);
    const attritionFactor = 1 - attrition / 200;
    const creditHaircut = clamp(1 - activeMacro.creditLoss / 100, 0.88, 1);
    const demand = activeMacro.loanDemand * clamp(1 + activeMacro.gdp / 140, 0.9, 1.08);
    const grossLoanProduction12 =
      vacancyAdjustedFte * officer.annualLoan * rampFactor12 * attritionFactor * demand * creditHaircut;
    const grossLoanProduction24 =
      vacancyAdjustedFte * officer.annualLoan * rampFactor24 * attritionFactor * demand * creditHaircut;
    const lineCommitments12 = grossLoanProduction12 * (officer.lineMix / 100);
    const fundedLines12 = lineCommitments12 * (officer.lineUtilization / 100);
    const termLoans12 = grossLoanProduction12 - lineCommitments12;
    const fundedLoanBalance12 = termLoans12 + fundedLines12;
    const deposits12 =
      vacancyAdjustedFte *
      officer.annualDeposit *
      rampFactor12 *
      attritionFactor *
      clamp(1 - activeMacro.depositRunoff / 100, 0.86, 1.02);
    const averageLoanBalance12 = fundedLoanBalance12 * 0.55;
    const averageDepositBalance12 = deposits12 * 0.55;
    const loanInterestIncome =
      termLoans12 * 0.55 * ((activeMacro.sofr + officer.loanSpreadBps / 100) / 100) +
      fundedLines12 * 0.55 * ((activeMacro.sofr + officer.lineSpreadBps / 100) / 100);
    const depositFundingBenefit =
      averageDepositBalance12 * Math.max(0, wholesaleFundingCost - depositCost) / 100;
    const unusedCommitmentFees =
      Math.max(0, lineCommitments12 - fundedLines12) * (UNUSED_COMMITMENT_FEE_RATE / 100);
    const provision12 = fundedLoanBalance12 * (activeMacro.creditLoss / 100) * 0.55;

    return {
      ...officer,
      ...input,
      grossHires: grossHiresFromRows,
      netHires,
      vacancy,
      attrition,
      vacancyAdjustedFte,
      grossLoanProduction12,
      grossLoanProduction24,
      termLoans12,
      lineCommitments12,
      fundedLines12,
      loans12: fundedLoanBalance12,
      loans24: grossLoanProduction24,
      deposits12,
      averageLoanBalance12,
      averageDepositBalance12,
      loanInterestIncome,
      depositFundingBenefit,
      unusedCommitmentFees,
      provision12,
      fee12: vacancyAdjustedFte * officer.annualFee * rampFactor12,
      expense12:
        vacancyAdjustedFte *
        officer.annualExpense *
        (1 + (activeMacro.expenseInflation + activeMacro.wageInflation) / 200)
    };
  });

  const grossHires = officerRows.reduce((sum, row) => sum + row.grossHires, 0);
  const departures = officerRows.reduce((sum, row) => sum + row.expectedDepartures, 0);
  const netHires = officerRows.reduce((sum, row) => sum + row.netHires, 0);
  const loans12 = officerRows.reduce((sum, row) => sum + row.loans12, 0);
  const deposits12 = officerRows.reduce((sum, row) => sum + row.deposits12, 0);
  const termLoans12 = officerRows.reduce((sum, row) => sum + row.termLoans12, 0);
  const lineCommitments12 = officerRows.reduce((sum, row) => sum + row.lineCommitments12, 0);
  const fundedLines12 = officerRows.reduce((sum, row) => sum + row.fundedLines12, 0);
  const newHireLoanInterestIncome = officerRows.reduce((sum, row) => sum + row.loanInterestIncome, 0);
  const newHireDepositFundingBenefit = officerRows.reduce((sum, row) => sum + row.depositFundingBenefit, 0);
  const unusedCommitmentFees = officerRows.reduce((sum, row) => sum + row.unusedCommitmentFees, 0);
  const newHireProvision = officerRows.reduce((sum, row) => sum + row.provision12, 0);
  const fee12 = officerRows.reduce((sum, row) => sum + row.fee12, 0);
  const expense12 = officerRows.reduce((sum, row) => sum + row.expense12, 0);
  const controllableExpenseDelta = Object.values(expenseInputs).reduce(
    (sum, lever) => sum + getExpenseDelta(lever),
    0
  );
  const legalRecoupmentCredit = getLegalRecoupmentCredit(expenseInputs.legalRecoupment);
  const legalRecoupmentDelta = getLegalRecoupmentDelta(expenseInputs.legalRecoupment);
  const existingProducerFte = workforceInputs.existingProducerFte.value;
  const supportFte = workforceInputs.supportFte.value;
  const vacancyFactorPct = workforceInputs.vacancyFactorPct.value;
  // Year-1 engine reads baselines for these five levers; the per-year
  // workforce layer in applyYearAdjustments owns their effect by fiscal year.
  const meritIncreasePct = workforceInputs.meritIncreasePct.baseline;
  const incentiveAccrualPct = workforceInputs.incentiveAccrualPct.baseline;
  const productivityLiftPct = workforceInputs.productivityLiftPct.baseline;
  const loanRetentionPct = workforceInputs.loanRetentionPct.baseline;
  const depositRetentionPct = workforceInputs.depositRetentionPct.baseline;
  const existingWorkforceCompBase = existingProducerFte * 0.34 + supportFte * 0.18;
  const existingWorkforceLoanGrowth =
    (productivityLiftPct - workforceInputs.productivityLiftPct.baseline) * 0.18 +
    (loanRetentionPct - workforceInputs.loanRetentionPct.baseline) * 0.06 -
    (vacancyFactorPct - workforceInputs.vacancyFactorPct.baseline) * 0.05;
  const existingWorkforceDepositGrowth =
    (productivityLiftPct - workforceInputs.productivityLiftPct.baseline) * 0.12 +
    (depositRetentionPct - workforceInputs.depositRetentionPct.baseline) * 0.07 -
    (vacancyFactorPct - workforceInputs.vacancyFactorPct.baseline) * 0.04;
  const workforceExpenseDelta =
    existingWorkforceCompBase * ((meritIncreasePct - workforceInputs.meritIncreasePct.baseline) / 100) +
    existingWorkforceCompBase * 0.22 * ((incentiveAccrualPct - workforceInputs.incentiveAccrualPct.baseline) / 100) -
    existingWorkforceCompBase * 0.52 * ((vacancyFactorPct - workforceInputs.vacancyFactorPct.baseline) / 100) +
    (supportFte - workforceInputs.supportFte.baseline) * 0.18;

  const statisticalLoanGrowth = clamp(
    3.8 +
      activeMacro.gdp * 0.62 -
      activeMacro.creditLoss * 0.48 -
      Math.max(0, activeMacro.unemployment - 4) * 0.42 -
      Math.max(0, activeMacro.prepayment - 8.5) * 0.08,
    -2,
    9
  );
  const uncappedMarketLoanGrowth = (loans12 / basePortfolio.loans) * 100;
  const adjustedLoanGrowth = clamp(
    statisticalLoanGrowth + uncappedMarketLoanGrowth + existingWorkforceLoanGrowth,
    -3,
    shocks.loanGrowthLimiterPct,
  );
  const newProductionScale =
    uncappedMarketLoanGrowth > 0
      ? clamp((adjustedLoanGrowth - statisticalLoanGrowth) / uncappedMarketLoanGrowth, 0, 1)
      : 1;
  const cappedLoans12 = loans12 * newProductionScale;
  const cappedTermLoans12 = termLoans12 * newProductionScale;
  const cappedFundedLines12 = fundedLines12 * newProductionScale;
  const cappedLineCommitments12 = lineCommitments12 * newProductionScale;

  const statisticalDepositGrowth = clamp(
    3.1 +
      activeMacro.gdp * 0.38 -
      activeMacro.depositRunoff * 0.45 -
      Math.max(0, activeMacro.unemployment - 4) * 0.18,
    -5,
    8
  );
  const adjustedDepositGrowth = clamp(
    statisticalDepositGrowth + (deposits12 / basePortfolio.deposits) * 100 + existingWorkforceDepositGrowth,
    -7,
    9
  );

  const endingLoans = basePortfolio.loans * (1 + adjustedLoanGrowth / 100);
  const endingDeposits = basePortfolio.deposits * (1 + adjustedDepositGrowth / 100);
  const averageLoans = (basePortfolio.loans + endingLoans) / 2;
  const averageDeposits = (basePortfolio.deposits + endingDeposits) / 2;
  const interestIncome = averageLoans * (loanYield / 100) + newHireLoanInterestIncome * newProductionScale;
  const interestExpense = averageDeposits * (depositCost / 100);
  const depositFundingBenefit = newHireDepositFundingBenefit * newProductionScale;
  const nii = interestIncome - interestExpense + depositFundingBenefit;
  const nonInterestIncome = basePortfolio.nonInterestIncome + fee12 + unusedCommitmentFees * newProductionScale;
  const expenseInflation =
    basePortfolio.nonInterestExpense * ((activeMacro.expenseInflation + activeMacro.wageInflation * 0.35) / 100);
  const nonInterestExpense =
    basePortfolio.nonInterestExpense +
    expenseInflation +
    expense12 +
    workforceExpenseDelta +
    controllableExpenseDelta -
    legalRecoupmentDelta;
  const creditProvision =
    basePortfolio.provision +
    Math.max(0, averageLoans - basePortfolio.loans) * (activeMacro.creditLoss / 100) +
    basePortfolio.loans * Math.max(0, activeMacro.creditLoss - MACRO_SETS.Base.creditLoss) / 100 +
    newHireProvision * newProductionScale;
  const preTaxIncome = nii + nonInterestIncome - nonInterestExpense - creditProvision;
  const taxExpense = Math.max(0, preTaxIncome * (basePortfolio.taxRate / 100));
  const netIncome = preTaxIncome - taxExpense;
  const efficiencyRatio = nonInterestExpense / (nii + nonInterestIncome);
  const nim = averageLoans > 0 ? nii / averageLoans : 0;
  const rwaGrowth = adjustedLoanGrowth * 0.82 + activeMacro.creditLoss * 0.35;

  return {
    officerRows,
    grossHires,
    departures,
    netHires,
    loans12,
    cappedLoans12,
    termLoans12: cappedTermLoans12,
    fundedLines12: cappedFundedLines12,
    lineCommitments12: cappedLineCommitments12,
    deposits12,
    fee12,
    expense12,
    unusedCommitmentFees: unusedCommitmentFees * newProductionScale,
    newHireLoanInterestIncome: newHireLoanInterestIncome * newProductionScale,
    depositFundingBenefit,
    legalRecoupmentCredit,
    legalRecoupmentDelta,
    controllableExpenseDelta,
    existingWorkforceLoanGrowth,
    existingWorkforceDepositGrowth,
    workforceExpenseDelta,
    existingWorkforceCompBase,
    statisticalLoanGrowth,
    statisticalDepositGrowth,
    adjustedLoanGrowth,
    adjustedDepositGrowth,
    endingLoans,
    endingDeposits,
    averageLoans,
    averageDeposits,
    loanYield,
    depositCost,
    wholesaleFundingCost,
    interestIncome,
    interestExpense,
    nii,
    nonInterestIncome,
    nonInterestExpense,
    creditProvision,
    preTaxIncome,
    taxExpense,
    netIncome,
    efficiencyRatio,
    nim,
    rwaGrowth
  };
};

const RegionalForecastingModule = () => {
  const { submissionId: routeSubmissionId } = useParams();
  const isAggregate = routeSubmissionId === 'aggregate';
  const submissionId = isAggregate ? null : routeSubmissionId;
  const aggregateUnitIds = useMemo(
    () =>
      isAggregate
        ? (new URLSearchParams(window.location.search).get('units') || '')
            .split(',')
            .filter(Boolean)
        : [],
    [isAggregate]
  );
  const [aggregateUnits, setAggregateUnits] = useState([]);
  const [activeProfile] = useState(() => getActiveForecastUser());
  const isLiaison = activeProfile.role === 'liaison';
  const [submission, setSubmission] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [saveState, setSaveState] = useState('idle');
  const [saveError, setSaveError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [narratives, setNarratives] = useState({});
  // B7: FP&A-captured overrides for blocking quality-gate warnings.
  const [qualityOverrides, setQualityOverrides] = useState({}); // gateCode -> { owner, note }
  const [gateBlockers, setGateBlockers] = useState([]);         // server 422 gates rendered inline
  const isFpa = activeProfile.role === 'manager' || activeProfile.role === 'central';
  const [workspaceState, setWorkspaceState] = useState({
    baselineDecision: null,
    baselineReviewedAt: null,
    baselineIssue: '',
    contextAcknowledgedAt: null,
    centralSettingsUpdatedAt: null,
    // Stage visits are session-local progress signals; persisted business inputs remain authoritative after refresh.
    visitedStages: []
  });
  const [workflowError, setWorkflowError] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // M4: the top-down driver-view anchor for this unit (published Base scenario).
  const [driverViewAnchor, setDriverViewAnchor] = useState(null);
  // M5 propagation: latest published macro scenarios (for stale-version chip),
  // the review-changes dialog, and explicit re-anchor state.
  const [macroScenarioList, setMacroScenarioList] = useState([]);
  const [macroStaleDialogOpen, setMacroStaleDialogOpen] = useState(false);
  const [reanchorBusy, setReanchorBusy] = useState(false);
  const [reanchorError, setReanchorError] = useState(null);
  const [reanchorResult, setReanchorResult] = useState(null);
  const [selectedMarketId, setSelectedMarketId] = useState(MARKETS[0].id);
  const [scenario, setScenario] = useState('Baseline Forecast');
  const [macroMode, setMacroMode] = useState('Base');
  const [resultsView, setResultsView] = useState('Variance');
  const [resultsFocusYear, setResultsFocusYear] = useState(null);
  // Profitability basis: inputters default to the internal FTP view,
  // managers/executives default to GAAP.
  const [resultsBasis, setResultsBasis] = useState(() =>
    getActiveForecastUser().role === 'liaison' ? 'FTP' : 'GAAP'
  );
  const [activeUser, setActiveUser] = useState(() => getActiveForecastUser().label);
  const activeActor = apiActor(activeUser);
  const [marketInputs, setMarketInputs] = useState(INITIAL_MARKET_INPUTS);
  const [workforceInputs, setWorkforceInputs] = useState(INITIAL_WORKFORCE_LEVERS);
  const [leverAttentionCount, setLeverAttentionCount] = useState(0);
  const updateLeverAttentionCount = useCallback((count) => setLeverAttentionCount(count), []);
  const [expenseInputs, setExpenseInputs] = useState(INITIAL_EXPENSE_LEVERS);
  const [hireRows, setHireRows] = useState(INITIAL_HIRE_ROWS);
  const [yearAdjustments, setYearAdjustments] = useState(DEFAULT_YEAR_ADJUSTMENTS);
  const [quarterlyGrowth, setQuarterlyGrowth] = useState(DEFAULT_QUARTERLY_GROWTH);
  const [pendingLeverPreview, setPendingLeverPreview] = useState(null);
  const [autoApplyLeverPreview, setAutoApplyLeverPreview] = useState(false);
  const [workforceYearInputs, setWorkforceYearInputs] = useState(DEFAULT_WORKFORCE_YEARS);
  const [hrPlan, setHrPlan] = useState(DEFAULT_HR_PLAN);
  const [regionalSearchParams, setRegionalSearchParams] = useSearchParams();
  // Item G — FP&A Quick Fix: a manager opening a package with ?adjust=fpa edits
  // the FP&A adjustment layer (scenario 'FP&A Adjusted'), never the market's
  // submitted values. The live model + ForecastPromptBox are reused unchanged.
  const fpaAdjustMode = !isAggregate && Boolean(submissionId)
    && activeProfile.role === 'manager'
    && regionalSearchParams.get('adjust') === 'fpa';
  const marketLeverBaselineRef = useRef(null);   // market submitted levers (for the FP&A delta)
  const fpaOverlaySeededRef = useRef(false);
  const [fpaSaveState, setFpaSaveState] = useState('idle'); // idle | saving | saved | error
  const workforceTab = regionalSearchParams.get('workforceTab') === 'new-hires' ? 'new-hires' : 'existing';
  const setWorkforceTab = (tab) => {
    const next = new URLSearchParams(regionalSearchParams);
    next.set('workforceTab', tab);
    setRegionalSearchParams(next);
  };
  const [hrRoster, setHrRoster] = useState(null);   // {headcount, roster: [...]} per entity
  const [feeDrivers, setFeeDrivers] = useState(DEFAULT_FEE_DRIVERS);
  const [monthlyMetricOverrides, setMonthlyMetricOverrides] = useState({});
  const [glRowOverrides, setGlRowOverrides] = useState({});
  const [excelBusy, setExcelBusy] = useState(null);  // 'export' | 'preview' | 'apply'
  const [excelImportPreview, setExcelImportPreview] = useState(null);
  const [excelNotice, setExcelNotice] = useState(null);
  const [pendingAppliedImport, setPendingAppliedImport] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryBusy, setAiSummaryBusy] = useState(false);
  const [lineage, setLineage] = useState({});
  const [forecastProvenanceByAccount, setForecastProvenanceByAccount] = useState({});
  const persistedForecastProvenanceRef = useRef({});
  const aiProvenanceLeverCountsRef = useRef({});
  const [loadState, setLoadState] = useState('validated');
  const [baselineSource, setBaselineSource] = useState({
    status: 'Seeded model',
    tone: 'neutral',
    message: 'Awaiting Supabase GL_Fact baseline pull',
    summaryMillions: null,
    latestPeriodDate: null
  });
  const [baselineHydrated, setBaselineHydrated] = useState(false);
  const [baselineLoading, setBaselineLoading] = useState(false);
  const aggregateBaselineRequestIdRef = useRef(0);
  const entityBaselineRequestIdRef = useRef(0);
  const [loadResult, setLoadResult] = useState(null);
  const [isWritingForecast, setIsWritingForecast] = useState(false);
  const [rateFeed, setRateFeed] = useState({
    status: 'Seeded',
    sofr: null,
    lastUpdated: 'Awaiting refresh',
    source: 'NY Fed Markets API - fallback to central macro set'
  });
  const [peerRefresh, setPeerRefresh] = useState({
    status: 'Mocked peer set',
    lastUpdated: 'Not refreshed',
    source: 'S&P Global Market Intelligence API stub'
  });
  const [shocks, setShocks] = useState({
    rateShockBps: 0,
    gdpShock: 0,
    creditStressBps: 0,
    liquidityRunoffPct: 0,
    expenseShockPct: 0,
    wageShockPct: 0,
    depositBetaShockPct: 0,
    loanGrowthLimiterPct: 8.5
  });
  const excelImportRef = useRef(null);

  const selectedMarket = useMemo(() => {
    if (isAggregate) {
      return {
        id: 'aggregate',
        region: 'Aggregate',
        market: `${aggregateUnits.length || aggregateUnitIds.length} business units`,
        planningEntity: 'AGGREGATE',
        financeLead: activeProfile.name
      };
    }
    if (submission) {
      return {
        id: submission.planning_entity,
        region: submission.division,
        market: submission.market,
        planningEntity: submission.planning_entity,
        financeLead:
          submission.assigned_to && submission.assigned_to !== 'Unassigned'
            ? submission.assigned_to
            : activeProfile.name
      };
    }
    return MARKETS.find((market) => market.id === selectedMarketId) || MARKETS[0];
  }, [isAggregate, aggregateUnits.length, aggregateUnitIds.length, submission, selectedMarketId, activeProfile]);

  // Aggregate session: load member units + sum their planning-entity balances.
  useEffect(() => {
    if (!isAggregate || aggregateUnitIds.length === 0) return undefined;
    let cancelled = false;
    const requestId = aggregateBaselineRequestIdRef.current + 1;
    aggregateBaselineRequestIdRef.current = requestId;
    setBaselineHydrated(false);
    setBaselineLoading(true);
    Promise.all([
      apiService.fetchForecastPlanningEntities(),
      Promise.all(aggregateUnitIds.map((id) => apiService.fetchForecastSubmission(id)))
    ])
      .then(([entities, records]) => {
        if (cancelled || requestId !== aggregateBaselineRequestIdRef.current) return;
        const lookup = Object.fromEntries(
          entities.planningEntities.map((entity) => [entity.planningEntity, entity])
        );
        const unitsList = records.map((record) => ({
          id: record.id,
          planning_entity: record.planning_entity,
          division: record.division,
          market: record.market,
          loansBase: lookup[record.planning_entity]?.loansBase || 0,
          depositsBase: lookup[record.planning_entity]?.depositsBase || 0,
          feeBase: lookup[record.planning_entity]?.feeAnnualBase || 0
        }));
        setAggregateUnits(unitsList);
        setBaselineSource({
          status: `Aggregate of ${unitsList.length} units`,
          tone: 'moss',
          message: `${unitsList.map((unit) => `${unit.division} · ${unit.market}`).join(' + ')} — summed planning-entity balances`,
          latestPeriodDate: null,
          summaryMillions: {
            loans: unitsList.reduce((sum, unit) => sum + unit.loansBase, 0) / 1e6,
            deposits: unitsList.reduce((sum, unit) => sum + unit.depositsBase, 0) / 1e6
          }
        });
        setBaselineHydrated(true);
        setBaselineLoading(false);
      })
      .catch((error) => {
        if (!cancelled && requestId === aggregateBaselineRequestIdRef.current) {
          setWorkflowError(error?.response?.data?.error || error.message || 'Aggregate load failed');
          setBaselineHydrated(true);
          setBaselineLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAggregate, aggregateUnitIds]);

  const hydrateSubmissionInputs = (inputs = {}) => {
    if (inputs.marketInputs) {
      setMarketInputs((previous) =>
        Object.fromEntries(
          Object.keys(INITIAL_MARKET_INPUTS).map((key) => [
            key,
            { ...INITIAL_MARKET_INPUTS[key], ...(previous[key] || {}), ...(inputs.marketInputs[key] || {}) }
          ])
        )
      );
    }
    if (inputs.workforceInputs) {
      setWorkforceInputs((previous) =>
        Object.fromEntries(
          Object.keys(INITIAL_WORKFORCE_LEVERS).map((key) => [
            key,
            { ...INITIAL_WORKFORCE_LEVERS[key], ...(previous[key] || {}), ...(inputs.workforceInputs[key] || {}) }
          ])
        )
      );
    }
    if (inputs.expenseInputs) {
      setExpenseInputs((previous) =>
        Object.fromEntries(
          Object.keys(INITIAL_EXPENSE_LEVERS).map((key) => [
            key,
            { ...INITIAL_EXPENSE_LEVERS[key], ...(previous[key] || {}), ...(inputs.expenseInputs[key] || {}) }
          ])
        )
      );
    }
    if (inputs.shocks) setShocks((previous) => ({ ...previous, ...inputs.shocks }));
    if (inputs.hireRows && Array.isArray(inputs.hireRows)) setHireRows(inputs.hireRows);
    if (inputs.yearAdjustments) {
      setYearAdjustments((previous) =>
        Object.fromEntries(
          Object.keys(DEFAULT_YEAR_ADJUSTMENTS).map((key) => [
            key,
            Array.isArray(inputs.yearAdjustments[key])
              ? ADJUSTMENT_YEARS.map((_, i) => Number(inputs.yearAdjustments[key][i]) || 0)
              : previous[key]
          ])
        )
      );
    }
    setQuarterlyGrowth(normalizeQuarterlyGrowth(inputs.quarterlyGrowth));
    if (inputs.hrPlan) {
      setHrPlan({ ...DEFAULT_HR_PLAN, ...inputs.hrPlan, actions: inputs.hrPlan.actions || {} });
    }
    if (inputs.workforceYearInputs) {
      setWorkforceYearInputs((previous) =>
        Object.fromEntries(
          WORKFORCE_YEAR_DEFS.map((def) => [
            def.id,
            Array.isArray(inputs.workforceYearInputs[def.id])
              ? ADJUSTMENT_YEARS.map((_, i) => {
                  const value = Number(inputs.workforceYearInputs[def.id][i]);
                  return Number.isFinite(value) ? value : def.current;
                })
              : previous[def.id]
          ])
        )
      );
    }
    if (inputs.feeDrivers && Array.isArray(inputs.feeDrivers)) {
      setFeeDrivers(DEFAULT_FEE_DRIVERS.map((fallback) => {
        const saved = inputs.feeDrivers.find((d) => d.id === fallback.id);
        return saved
          ? {
              ...fallback,
              ratioBps: ADJUSTMENT_YEARS.map((_, i) => Number(saved.ratioBps?.[i]) || 0),
              overlays: Array.isArray(saved.overlays) ? saved.overlays : []
            }
          : fallback;
      }));
    }
    if (inputs.lineage) setLineage(inputs.lineage);
    aiProvenanceLeverCountsRef.current = {};
    if (Object.prototype.hasOwnProperty.call(inputs, 'forecastProvenanceByAccount')) {
      persistedForecastProvenanceRef.current = inputs.forecastProvenanceByAccount || {};
      setForecastProvenanceByAccount(inputs.forecastProvenanceByAccount || {});
    } else {
      persistedForecastProvenanceRef.current = {};
      setForecastProvenanceByAccount({});
    }
    if (inputs.scenario) setScenario(inputs.scenario);
    if (inputs.macroMode && MACRO_SETS[inputs.macroMode]) setMacroMode(inputs.macroMode);
    if (inputs.monthlyMetricOverrides) setMonthlyMetricOverrides(inputs.monthlyMetricOverrides || {});
    if (inputs.glRowOverrides) setGlRowOverrides(inputs.glRowOverrides || {});
    if (inputs.workspaceState) {
      setWorkspaceState((previous) => ({ ...previous, ...inputs.workspaceState }));
    }
  };

  useEffect(() => {
    if (!submissionId) {
      setHydrated(true);
      return undefined;
    }
    let cancelled = false;
    apiService
      // actingAs enforces org-map coverage server-side: an out-of-coverage open
      // package returns 403 and surfaces below via workflowError.
      .fetchForecastSubmission(submissionId, getActiveForecastUser().name)
      .then((record) => {
        if (cancelled) return;
        setSubmission(record);
        setSubmissionStatus(record.status);
        setLastSavedAt(record.updatedAt || record.updated_at || null);
        setSaveError(null);
        const inputs = record.inputs || {};
        // Merge over defaults so partial or older drafts can never crash the model loop.
        hydrateSubmissionInputs(inputs);
        if (record.ai_summary) setAiSummary(record.ai_summary);
        if (Array.isArray(record.narratives)) {
          setNarratives(
            Object.fromEntries(record.narratives.map((entry) => [entry.trigger, entry.text || '']))
          );
        }
        setHydrated(true);
      })
      .catch((error) => {
        if (cancelled) return;
        setWorkflowError(
          error?.response?.data?.error || error.message || 'Failed to load submission'
        );
        setHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  // Item G: once the package has hydrated in FP&A-adjust mode, capture the market
  // baseline levers (for the delta) and apply the existing FP&A overlay on top so
  // the manager continues from the current adjusted state.
  useEffect(() => {
    if (!fpaAdjustMode || !hydrated || !submission || fpaOverlaySeededRef.current) return;
    fpaOverlaySeededRef.current = true;
    marketLeverBaselineRef.current = {
      yearAdjustments: normalizeLeverVector(submission.inputs?.yearAdjustments),
      quarterlyGrowth: submission.inputs?.quarterlyGrowth || {}
    };
    const overlay = latestFpaOverlay(submission.events || []);
    if (overlay?.leversAfter) {
      setYearAdjustments(normalizeLeverVector(overlay.leversAfter));
      if (overlay.quarterlyAfter) setQuarterlyGrowth(normalizeQuarterlyGrowth(overlay.quarterlyAfter));
    }
  }, [fpaAdjustMode, hydrated, submission]);

  useEffect(() => {
    if (isAggregate) return undefined; // aggregate baseline is summed from planning entities
    let cancelled = false;
    const requestId = entityBaselineRequestIdRef.current + 1;
    entityBaselineRequestIdRef.current = requestId;
    setBaselineHydrated(false);
    setBaselineLoading(true);

    setBaselineSource({
      status: 'Loading GL actuals',
      tone: 'neutral',
      message: 'Reading canonical GL_Fact actuals from Supabase',
      summaryMillions: null,
      latestPeriodDate: null
    });

    // Submission-scoped pages baseline from the planning entity's OWN actuals
    // (Versions='Actual', scoped to the PLN_ planning entity via the
    // gl_fact_actual_rollup matview). ACTUAL_MGMT was retired in the
    // single-actuals cutover; entity scoping is handled by the entity param.
    // The legacy market-name lookup matched every division's enterprise
    // entities in the market and seeded the unit at whole-market scale —
    // the source of the Executive Review overstatement.
    const entityScoped = Boolean(submissionId)
      && selectedMarket.planningEntity
      && selectedMarket.planningEntity !== 'AGGREGATE';

    apiService.fetchRegionalForecastBaseline({
      market: selectedMarket.market,
      region: selectedMarket.region,
      ...(entityScoped
        ? { entity: selectedMarket.planningEntity, sourceVersion: 'Actual' }
        : { sourceVersion: 'Actual' })
      })
      .then((baseline) => {
        if (cancelled || requestId !== entityBaselineRequestIdRef.current) return;
        setBaselineSource({
          status: baseline.available ? 'Supabase GL_Fact actuals' : 'Seeded model',
          tone: baseline.available ? 'moss' : 'amber',
          message: baseline.available
            ? `${baseline.rowCount.toLocaleString()} rows through ${baseline.latestPeriodDate}`
            : baseline.message,
          summaryMillions: baseline.summaryMillions || null,
          latestPeriodDate: baseline.latestPeriodDate || null
        });
        setBaselineHydrated(true);
        setBaselineLoading(false);
      })
      .catch((error) => {
        if (cancelled || requestId !== entityBaselineRequestIdRef.current) return;
        setBaselineSource({
          status: 'Seeded model fallback',
          tone: 'amber',
          message: error?.response?.data?.error || error.message || 'Baseline API unavailable',
          summaryMillions: null,
          latestPeriodDate: null
        });
        setBaselineHydrated(true);
        setBaselineLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAggregate, submissionId, selectedMarket.planningEntity, selectedMarket.market, selectedMarket.region]);

  // M4: fetch the active driver-view anchor for this unit (pure top-down driver
  // view under the published Base scenario). Degrades to null when no anchor has
  // been built — the panel and gate stay dormant until Central FP&A re-anchors.
  useEffect(() => {
    const entity = selectedMarket.planningEntity;
    if (isAggregate || !entity || entity === 'AGGREGATE') {
      setDriverViewAnchor(null);
      return undefined;
    }
    let cancelled = false;
    apiService.fetchDriverViewAnchor({
      entity,
      cycleCode: submission?.cycle_code || 'FY2027_BUDGET',
      scenarioKey: 'Base'
    })
      .then((result) => {
        if (!cancelled) setDriverViewAnchor(result?.anchor || null);
      })
      .catch(() => {
        if (!cancelled) setDriverViewAnchor(null);
      });
    return () => { cancelled = true; };
  }, [isAggregate, selectedMarket.planningEntity, submission?.cycle_code]);

  // M5 propagation: pull the macro-scenario book so the workspace can detect a
  // newer published Base version than the plan is anchored to. Degrades to an
  // empty list (chip stays dormant) if the store is unavailable.
  const refreshMacroScenarioList = useCallback(() => {
    if (isAggregate) {
      setMacroScenarioList([]);
      return Promise.resolve([]);
    }
    return apiService.fetchMacroScenarios(submission?.cycle_code || 'FY2027_BUDGET')
      .then((payload) => {
        const rows = Array.isArray(payload)
          ? payload
          : (payload && Array.isArray(payload.scenarios) ? payload.scenarios : []);
        setMacroScenarioList(rows);
        return rows;
      })
      .catch(() => {
        setMacroScenarioList([]);
        return [];
      });
  }, [isAggregate, submission?.cycle_code]);

  useEffect(() => {
    let cancelled = false;
    refreshMacroScenarioList().then((rows) => {
      if (cancelled) return rows;
      return rows;
    });
    return () => { cancelled = true; };
  }, [refreshMacroScenarioList]);

  const basePortfolio = useMemo(
    () => deriveBasePortfolioFromGl(baselineSource.summaryMillions),
    [baselineSource.summaryMillions]
  );

  // Central FP&A-owned macro base: server-persisted, propagated to everyone.
  // Upside/Downside keep their seeded deltas relative to the central base.
  const [centralSettings, setCentralSettings] = useState(null);
  const [centralSaveState, setCentralSaveState] = useState('idle');
  const [centralSaveError, setCentralSaveError] = useState(null);
  const [centralSavedAt, setCentralSavedAt] = useState(null);
  const centralSaveRequestIdRef = useRef(0);
  const centralRetryRequestRef = useRef(null);
  useEffect(() => {
    apiService.fetchCentralSettings('FY2027_BUDGET')
      .then(setCentralSettings)
      .catch(() => setCentralSettings(null));
  }, []);

  const macroSets = useMemo(() => {
    const override = centralSettings?.macroBase;
    if (!override) return MACRO_SETS;
    const base = { ...MACRO_SETS.Base, ...override };
    const shifted = (setName) =>
      Object.fromEntries(
        Object.entries(MACRO_SETS[setName]).map(([key, value]) => [
          key,
          typeof value === 'number' && typeof base[key] === 'number'
            ? base[key] + (value - MACRO_SETS.Base[key])
            : value
        ])
      );
    return {
      Base: base,
      Upside: shifted('Upside'),
      Downside: shifted('Downside'),
      'Custom Shock': shifted('Custom Shock')
    };
  }, [centralSettings]);

  const buildActiveMacroForShocks = useCallback((nextShocks = shocks) => {
    const base = macroSets[macroMode];
    return {
      ...base,
      sofr: (rateFeed.sofr ?? base.sofr) + nextShocks.rateShockBps / 100,
      gdp: base.gdp + nextShocks.gdpShock,
      depositBeta: clamp(base.depositBeta + nextShocks.depositBetaShockPct, 5, 90),
      creditLoss: base.creditLoss + nextShocks.creditStressBps / 100,
      depositRunoff: base.depositRunoff + nextShocks.liquidityRunoffPct,
      expenseInflation: base.expenseInflation + nextShocks.expenseShockPct,
      wageInflation: base.wageInflation + nextShocks.wageShockPct
    };
  }, [macroMode, macroSets, rateFeed.sofr, shocks]);

  const activeMacro = useMemo(
    () => buildActiveMacroForShocks(shocks),
    [buildActiveMacroForShocks, shocks]
  );

  const isCentral = activeProfile.role === 'central';
  const updateCentralSetting = async (group, key, value, label, retryPriorValue) => {
    if (!centralSettings) return;
    const prior = retryPriorValue ?? centralSettings[group]?.[key];
    const nextGroup = { ...centralSettings[group], [key]: value };
    const nextSettings = { ...centralSettings, [group]: nextGroup };
    const request = { group, key, value, label, prior };
    const requestId = centralSaveRequestIdRef.current + 1;
    centralSaveRequestIdRef.current = requestId;
    centralRetryRequestRef.current = request;

    setCentralSettings(nextSettings);
    setCentralSaveState('saving');
    setCentralSaveError(null);

    try {
      const savedSettings = await apiService.saveCentralSettings({
        cycleCode: 'FY2027_BUDGET',
        actor: activeProfile.name,
        [group]: nextGroup
      });
      if (requestId !== centralSaveRequestIdRef.current) return;
      const savedAt = savedSettings?.updatedAt || new Date().toISOString();
      setCentralSettings(savedSettings || nextSettings);
      setCentralSaveState('saved');
      setCentralSaveError(null);
      setCentralSavedAt(savedAt);
      centralRetryRequestRef.current = null;
      recordLineage(`central:${group}:${key}`, `Central — ${label}`, prior, value, 'Central settings save');
    } catch (error) {
      if (requestId !== centralSaveRequestIdRef.current) return;
      setCentralSaveState('error');
      setCentralSaveError(error?.response?.data?.error || error.message || 'Central settings save failed');
    }
  };

  const retryCentralSettingSave = () => {
    const request = centralRetryRequestRef.current;
    if (!request) return;
    updateCentralSetting(request.group, request.key, request.value, request.label, request.prior);
  };

  const recordLineage = (key, field, priorValue, newValue, source = 'FP&A input') => {
    setLineage((previous) => ({
      ...previous,
      [key]: {
        key,
        field,
        priorValue,
        newValue,
        user: activeActor,
        timestamp: new Date().toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        }),
        source
      }
    }));
  };

  const updateMarketInput = (officerId, field, value) => {
    const numericValue = toFiniteNumber(value);
    const lineageKey = `hire:${officerId}:${field}`;
    recordLineage(lineageKey, FIELD_LABELS[field] || field, marketInputs[officerId][field], numericValue);
    setMarketInputs((previous) => ({
      ...previous,
      [officerId]: {
        ...previous[officerId],
        [field]: numericValue
      }
    }));
  };

  const updateExpenseInput = (leverId, value) => {
    const numericValue = toFiniteNumber(value);
    const lineageKey = `expense:${leverId}:value`;
    const nextExpenseInputs = clonePlainValue(expenseInputs);
    nextExpenseInputs[leverId] = {
      ...nextExpenseInputs[leverId],
      value: numericValue
    };
    stageManualLeverPreview({
      kind: 'expenseInput',
      title: expenseInputs[leverId].label,
      detail: `${expenseInputs[leverId].value} to ${numericValue}`,
      primaryFields: ['nonInterestExpense'],
      priorState: { expenseInputs: clonePlainValue(expenseInputs) },
      nextState: { expenseInputs: nextExpenseInputs },
      overrides: { expenseInputs: nextExpenseInputs },
      lineage: {
        key: lineageKey,
        field: expenseInputs[leverId].label,
        priorValue: expenseInputs[leverId].value,
        newValue: numericValue
      }
    });
  };

  const updateWorkforceInput = (leverId, value) => {
    const numericValue = toFiniteNumber(value);
    const lineageKey = `workforce:${leverId}:value`;
    recordLineage(lineageKey, FIELD_LABELS[leverId] || workforceInputs[leverId].label, workforceInputs[leverId].value, numericValue);
    setWorkforceInputs((previous) => ({
      ...previous,
      [leverId]: {
        ...previous[leverId],
        value: numericValue
      }
    }));
  };

  const updateShock = (field, value) => {
    const numericValue = toFiniteNumber(value);
    const lineageKey = `macro:${field}`;
    const nextShocks = {
      ...shocks,
      [field]: numericValue
    };
    const primaryByShock = {
      creditStressBps: 'creditProvision',
      expenseShockPct: 'nonInterestExpense',
      wageShockPct: 'nonInterestExpense',
      rateShockBps: 'nii',
      depositBetaShockPct: 'interestExpense',
      liquidityRunoffPct: 'endingDeposits',
      gdpShock: 'endingLoans',
      loanGrowthLimiterPct: 'endingLoans'
    };
    stageManualLeverPreview({
      kind: 'shock',
      title: FIELD_LABELS[field] || field,
      detail: `${shocks[field]} to ${numericValue}`,
      primaryFields: [primaryByShock[field]].filter(Boolean),
      priorState: { shocks: { ...shocks } },
      nextState: { shocks: nextShocks },
      overrides: { shocks: nextShocks },
      lineage: {
        key: lineageKey,
        field: FIELD_LABELS[field] || field,
        priorValue: shocks[field],
        newValue: numericValue
      }
    });
  };

  const resetWorkforceInputs = () => setWorkforceInputs(INITIAL_WORKFORCE_LEVERS);

  const resetExpenseInputs = () => setExpenseInputs(INITIAL_EXPENSE_LEVERS);

  const resetMacroLevers = () =>
    setShocks({
      rateShockBps: 0,
      gdpShock: 0,
      creditStressBps: 0,
      liquidityRunoffPct: 0,
      expenseShockPct: 0,
      wageShockPct: 0,
      depositBetaShockPct: 0,
      loanGrowthLimiterPct: 8.5
    });

  const refreshRates = async () => {
    setRateFeed((previous) => ({
      ...previous,
      status: 'Refreshing',
      lastUpdated: 'Calling NY Fed Markets API'
    }));

    try {
      const response = await fetch('https://markets.newyorkfed.org/api/rates/all/latest.json');
      if (!response.ok) throw new Error(`NY Fed API returned ${response.status}`);

      const payload = await response.json();
      const sofr = extractSofrRate(payload);

      setRateFeed({
        status: sofr ? 'Live' : 'Connected',
        sofr,
        lastUpdated: new Date().toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        }),
        source: 'https://markets.newyorkfed.org/api/rates/all/latest.json'
      });
    } catch (error) {
      setRateFeed({
        status: 'Seeded fallback',
        sofr: null,
        lastUpdated: 'Live pull unavailable in this browser session',
        source: 'NY Fed Markets API - fallback to central macro set'
      });
    }
  };

  const refreshPeerData = () => {
    setPeerRefresh({
      status: 'Stubbed refresh complete',
      lastUpdated: new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }),
      source: 'Visible S&P Global Market Intelligence API affordance; using mocked peer set in v1 prototype'
    });
  };

  const model = useMemo(
    () => buildRegionalForecastModel({
      activeMacro,
      basePortfolio,
      expenseInputs,
      hireRows,
      marketInputs,
      shocks,
      workforceInputs
    }),
    [activeMacro, basePortfolio, expenseInputs, hireRows, marketInputs, shocks, workforceInputs]
  );

  const baselineModel = useMemo(
    () => calculatePortfolioBaselineModel(MACRO_SETS.Base, 8.5, basePortfolio),
    [basePortfolio]
  );

  const scenarioBaseModel = useMemo(
    () => calculatePortfolioBaselineModel(activeMacro, shocks.loanGrowthLimiterPct, basePortfolio),
    [activeMacro, basePortfolio, shocks.loanGrowthLimiterPct]
  );

  const baseForecastRows = useMemo(
    () => [
      {
        metric: 'Ending loans',
        base: baselineModel.endingLoans,
        scenario: scenarioBaseModel.endingLoans,
        adjusted: model.endingLoans,
        note: 'Portfolio growth plus market production cap'
      },
      {
        metric: 'Ending deposits',
        base: baselineModel.endingDeposits,
        scenario: scenarioBaseModel.endingDeposits,
        adjusted: model.endingDeposits,
        note: 'Baseline deposit growth plus producer deposit gathering'
      },
      {
        metric: 'NII',
        base: baselineModel.nii,
        scenario: scenarioBaseModel.nii,
        adjusted: model.nii,
        note: 'Rate path, pricing beta, balance growth, and funding value'
      },
      {
        metric: 'Net income',
        base: baselineModel.netIncome,
        scenario: scenarioBaseModel.netIncome,
        adjusted: model.netIncome,
        note: 'After fees, expenses, provision, and tax'
      }
    ],
    [baselineModel, model, scenarioBaseModel]
  );

  const remainderYear = FORECAST_YEARS[0] - 1;
  const latestActualMonth = useMemo(() => deriveLatestActualMonth({
    isAggregate,
    baselineLoading,
    latestPeriodDate: baselineSource.latestPeriodDate,
    remainderYear
  }), [baselineLoading, baselineSource.latestPeriodDate, isAggregate, remainderYear]);
  const remainderMonthCounts = useMemo(
    () => remainingYearQuarterMonthCounts(latestActualMonth),
    [latestActualMonth]
  );
  const remainderMonthCount = remainderMonthCounts.reduce((sum, count) => sum + count, 0);
  const remainderQuarterValues = useMemo(() => Object.fromEntries(
    ['loanGrowthPts', 'depositGrowthPts'].map((leverId) => {
      const config = quarterlyGrowth[leverId]?.roy;
      return [leverId, resolveQuarterValues({
        annualTarget: config?.annualTarget ?? 0,
        config,
        monthCounts: remainderMonthCounts
      })];
    })
  ), [quarterlyGrowth, remainderMonthCounts]);
  const buildRemainderBridge = useCallback((projectionModel, macro, label, includeOverrides) => (
    buildMonthlyProjection(projectionModel, macro, label, basePortfolio, {
      startYear: remainderYear,
      startMonth: latestActualMonth + 1,
      monthCount: remainderMonthCount,
      growthAdjustmentAt: includeOverrides
        ? ({ leverId, zeroBasedMonth }) => (
            remainderQuarterValues[leverId]?.[Math.floor(zeroBasedMonth / 3)] || 0
          )
        : undefined
    })
  ), [basePortfolio, latestActualMonth, remainderMonthCount, remainderQuarterValues, remainderYear]);
  const baselineRemainderSeries = useMemo(
    () => buildRemainderBridge(baselineModel, MACRO_SETS.Base, 'Central base remainder', false),
    [baselineModel, buildRemainderBridge]
  );
  const scenarioRemainderSeries = useMemo(
    () => buildRemainderBridge(scenarioBaseModel, activeMacro, 'Scenario remainder', false),
    [activeMacro, buildRemainderBridge, scenarioBaseModel]
  );
  const adjustedRemainderSeries = useMemo(
    // RoY starts from the selected scenario plus explicit RoY quarter overlays;
    // FY2027+ annual business levers enter when the full `model` forecast begins.
    () => buildRemainderBridge(scenarioBaseModel, activeMacro, 'Modified remainder', true),
    [activeMacro, buildRemainderBridge, scenarioBaseModel]
  );
  const baselineForecastOpening = useMemo(
    () => deriveBridgeOpeningPortfolio(baselineRemainderSeries, basePortfolio),
    [baselineRemainderSeries, basePortfolio]
  );
  const scenarioForecastOpening = useMemo(
    () => deriveBridgeOpeningPortfolio(scenarioRemainderSeries, basePortfolio),
    [scenarioRemainderSeries, basePortfolio]
  );
  const adjustedForecastOpening = useMemo(
    () => deriveBridgeOpeningPortfolio(adjustedRemainderSeries, basePortfolio),
    [adjustedRemainderSeries, basePortfolio]
  );

  const baselineMonthlySeries = useMemo(
    () => buildMonthlyProjection(baselineModel, MACRO_SETS.Base, 'Central base', baselineForecastOpening),
    [baselineForecastOpening, baselineModel]
  );

  const scenarioMonthlySeries = useMemo(
    () => buildMonthlyProjection(scenarioBaseModel, activeMacro, 'Scenario base', scenarioForecastOpening),
    [activeMacro, scenarioBaseModel, scenarioForecastOpening]
  );

  const adjustedRawMonthlySeries = useMemo(
    () => buildMonthlyProjection(model, activeMacro, 'Modified plan', adjustedForecastOpening),
    [activeMacro, adjustedForecastOpening, model]
  );

  const newProductionMonthly = useMemo(
    () => buildNewProductionMonthly(hireRows, OFFICER_TYPES, marketInputs, activeMacro),
    [hireRows, marketInputs, activeMacro]
  );

  // Detailed roster mode: comp delta ($MM/yr, plan vs baseline-assumption
  // roster) flows into the model alongside the user's comp lever.
  const hrCompDeltaByYear = useMemo(() => {
    if (hrPlan.mode !== 'roster' || !hrRoster?.roster) return null;
    const baseline = rosterCompByYear(hrRoster.roster, hrPlan, true);
    const plan = rosterCompByYear(hrRoster.roster, hrPlan, false);
    return plan.map((value, index) => Number((value - baseline[index]).toFixed(3)));
  }, [hrPlan, hrRoster]);

  const effectiveYearAdjustments = useMemo(() => {
    if (!hrCompDeltaByYear) return yearAdjustments;
    return {
      ...yearAdjustments,
      compAdjMM: yearAdjustments.compAdjMM.map((value, index) => value + hrCompDeltaByYear[index])
    };
  }, [yearAdjustments, hrCompDeltaByYear]);

  const adjustedMonthlySeries = useMemo(
    () => {
      const adjusted = applyYearAdjustments(
        adjustedRawMonthlySeries,
        effectiveYearAdjustments,
        feeDrivers,
        newProductionMonthly,
        basePortfolio.taxRate,
        workforceYearInputs,
        model.existingWorkforceCompBase,
        quarterlyGrowth
      ).adjusted;
      return applyMonthlyMetricOverrides(adjusted, monthlyMetricOverrides);
    },
    [
      adjustedRawMonthlySeries,
      effectiveYearAdjustments,
      feeDrivers,
      newProductionMonthly,
      basePortfolio.taxRate,
      workforceYearInputs,
      model.existingWorkforceCompBase,
      quarterlyGrowth,
      monthlyMetricOverrides
    ]
  );

  const buildAdjustedMonthlySeriesForState = useCallback((overrides = {}) => {
    const nextShocks = overrides.shocks || shocks;
    const nextExpenseInputs = overrides.expenseInputs || expenseInputs;
    const nextYearAdjustments = overrides.yearAdjustments || yearAdjustments;
    const nextQuarterlyGrowth = overrides.quarterlyGrowth || quarterlyGrowth;
    const nextFeeDrivers = overrides.feeDrivers || feeDrivers;
    const nextActiveMacro = buildActiveMacroForShocks(nextShocks);
    const nextModel = buildRegionalForecastModel({
      activeMacro: nextActiveMacro,
      basePortfolio,
      expenseInputs: nextExpenseInputs,
      hireRows,
      marketInputs,
      shocks: nextShocks,
      workforceInputs
    });
    const nextScenarioBaseModel = calculatePortfolioBaselineModel(
      nextActiveMacro,
      nextShocks.loanGrowthLimiterPct,
      basePortfolio
    );
    const nextRemainderQuarterValues = Object.fromEntries(
      ['loanGrowthPts', 'depositGrowthPts'].map((leverId) => {
        const config = nextQuarterlyGrowth[leverId]?.roy;
        return [leverId, resolveQuarterValues({
          annualTarget: config?.annualTarget ?? 0,
          config,
          monthCounts: remainderMonthCounts
        })];
      })
    );
    const nextAdjustedRemainderSeries = buildMonthlyProjection(
      nextScenarioBaseModel,
      nextActiveMacro,
      'Modified remainder',
      basePortfolio,
      {
        startYear: remainderYear,
        startMonth: latestActualMonth + 1,
        monthCount: remainderMonthCount,
        growthAdjustmentAt: ({ leverId, zeroBasedMonth }) => (
          nextRemainderQuarterValues[leverId]?.[Math.floor(zeroBasedMonth / 3)] || 0
        )
      }
    );
    const nextAdjustedForecastOpening = deriveBridgeOpeningPortfolio(
      nextAdjustedRemainderSeries,
      basePortfolio
    );
    const nextAdjustedRawMonthlySeries = buildMonthlyProjection(
      nextModel,
      nextActiveMacro,
      'Modified plan',
      nextAdjustedForecastOpening
    );
    const nextNewProductionMonthly = buildNewProductionMonthly(
      hireRows,
      OFFICER_TYPES,
      marketInputs,
      nextActiveMacro
    );
    const nextEffectiveYearAdjustments = hrCompDeltaByYear
      ? {
          ...nextYearAdjustments,
          compAdjMM: nextYearAdjustments.compAdjMM.map((value, index) => value + hrCompDeltaByYear[index])
        }
      : nextYearAdjustments;
    const adjusted = applyYearAdjustments(
      nextAdjustedRawMonthlySeries,
      nextEffectiveYearAdjustments,
      nextFeeDrivers,
      nextNewProductionMonthly,
      basePortfolio.taxRate,
      workforceYearInputs,
      nextModel.existingWorkforceCompBase,
      nextQuarterlyGrowth
    ).adjusted;
    return applyMonthlyMetricOverrides(adjusted, monthlyMetricOverrides);
  }, [
    basePortfolio,
    buildActiveMacroForShocks,
    expenseInputs,
    feeDrivers,
    hireRows,
    hrCompDeltaByYear,
    latestActualMonth,
    marketInputs,
    monthlyMetricOverrides,
    quarterlyGrowth,
    remainderMonthCount,
    remainderMonthCounts,
    remainderYear,
    shocks,
    workforceInputs,
    workforceYearInputs,
    yearAdjustments
  ]);

  // Proposed transfers in/out of this unit, read from every package's hrPlan.
  const [hrTransfers, setHrTransfers] = useState(null);
  useEffect(() => {
    const division = submissionId ? submission?.division : selectedMarket.region;
    const market = submissionId ? submission?.market : selectedMarket.market;
    if (!division || !market || isAggregate) return undefined;
    let cancelled = false;
    apiService.fetchHrTransfers({ division, market })
      .then((data) => { if (!cancelled) setHrTransfers(data); })
      .catch(() => { if (!cancelled) setHrTransfers(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId, submission?.division, submission?.market, selectedMarket.market, isAggregate]);

  const outgoingTransferCount = useMemo(
    () => Object.values(hrPlan.actions || {}).filter((a) => a.action === 'transfer').length,
    [hrPlan.actions]
  );

  // Lazy roster load when detailed mode is on for the active entity.
  useEffect(() => {
    if (hrPlan.mode !== 'roster') return undefined;
    const entity = submissionId ? submission?.planning_entity : selectedMarket.planningEntity;
    if (!entity || entity === 'AGGREGATE') return undefined;
    if (hrRoster?.planningEntity === entity) return undefined;
    let cancelled = false;
    apiService.fetchHrRoster(entity)
      .then((data) => { if (!cancelled) setHrRoster(data); })
      .catch(() => { if (!cancelled) setHrRoster({ planningEntity: entity, headcount: 0, roster: [] }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hrPlan.mode, submissionId, submission?.planning_entity, selectedMarket.planningEntity]);

  const updateHrPlan = (nextPlan, label) => {
    setHrPlan(nextPlan);
    recordLineage(`hr:${label}`, `Headcount plan — ${label}`, '·', 'updated');
  };

  const scenarioVersion = useMemo(
    () => buildScenarioVersion(scenario, selectedMarket, macroMode),
    [macroMode, scenario, selectedMarket]
  );

  const forecastLoadRows = useMemo(
    () => applyGlRowOverrides(buildForecastLoadRows(adjustedMonthlySeries, forecastProvenanceByAccount), glRowOverrides),
    [adjustedMonthlySeries, forecastProvenanceByAccount, glRowOverrides]
  );

  useEffect(() => {
    setLoadState('validated');
    setLoadResult(null);
  }, [forecastLoadRows, scenarioVersion]);

  const baselineAnnualSeries = useMemo(
    () => buildAnnualFromMonthly(baselineMonthlySeries),
    [baselineMonthlySeries]
  );

  // Actual history for OP_01: prior full years + current-year actual YTD.
  const [entityMonthlyHistory, setEntityMonthlyHistory] = useState(null);
  useEffect(() => {
    const entity = submissionId ? submission?.planning_entity : selectedMarket.planningEntity;
    if (!entity || entity === 'AGGREGATE') {
      setEntityMonthlyHistory(null);
      return undefined;
    }
    let cancelled = false;
    apiService.fetchForecastComparison({ cycleCode: 'FY2027_BUDGET', level: 'entity', entity, grain: 'monthly' })
      .then((monthlyData) => {
        if (cancelled) return;
        setEntityMonthlyHistory(monthlyData.groups?.[0] || null);
      })
      .catch(() => {
        if (!cancelled) {
          setEntityMonthlyHistory(null);
        }
      });
    return () => { cancelled = true; };
  }, [submissionId, submission?.planning_entity, selectedMarket.planningEntity]);

  const op01MonthlySeries = useMemo(
    () => buildOp01MonthlySeries(entityMonthlyHistory?.series?.actuals || [], baselineMonthlySeries),
    [entityMonthlyHistory, baselineMonthlySeries]
  );

  const op01AnnualSeries = useMemo(
    () => aggregateAnnualRows(op01MonthlySeries),
    [op01MonthlySeries]
  );

  const op01CurrentYearRow = useMemo(
    () =>
      op01AnnualSeries.find((row) => row.sourceType === 'mixed') ||
      op01AnnualSeries.find((row) => row.sourceType === 'forecast') ||
      op01AnnualSeries[0],
    [op01AnnualSeries]
  );

  const growthMatrixColumns = useMemo(
    () =>
      op01AnnualSeries.map((row) => ({
        label: row.year,
        sub: row.subLabel,
        actual: row.sourceType === 'actual' || row.sourceType === 'mixed',
        values: {
          loans: row.endingLoans,
          deposits: row.endingDeposits,
          nii: row.nii,
          fees: row.nonInterestIncome,
          expense: row.nonInterestExpense,
          provision: row.creditProvision,
          netIncome: row.netIncome
        }
      })),
    [op01AnnualSeries]
  );

  const scenarioAnnualSeries = useMemo(
    () => buildAnnualFromMonthly(scenarioMonthlySeries),
    [scenarioMonthlySeries]
  );

  const adjustedAnnualSeries = useMemo(
    () => buildAnnualFromMonthly(adjustedMonthlySeries),
    [adjustedMonthlySeries]
  );

  // M4: the plan's driver-view snapshot (byYear metric map) and the per-line
  // deltas vs the active anchor. The gate + panel + narrative triggers all read
  // from these so display and validation never fork calculation paths.
  const driverViewPlanSnapshot = useMemo(
    () => buildDriverViewPlanSnapshot(adjustedAnnualSeries),
    [adjustedAnnualSeries]
  );
  const driverViewDeltas = useMemo(
    () => (driverViewAnchor?.anchorSnapshot
      ? buildDriverViewDeltas({
          anchorSnapshot: driverViewAnchor.anchorSnapshot,
          planByYear: driverViewPlanSnapshot
        })
      : []),
    [driverViewAnchor, driverViewPlanSnapshot]
  );
  // Display-only supporting lines (never gate). Shown in the panel disclosure.
  const driverViewDisclosureDeltas = useMemo(
    () => (driverViewAnchor?.anchorSnapshot
      ? buildDriverViewDeltas({
          anchorSnapshot: driverViewAnchor.anchorSnapshot,
          planByYear: driverViewPlanSnapshot,
          metrics: ['nii', 'fees', 'expense', 'provision']
        })
      : []),
    [driverViewAnchor, driverViewPlanSnapshot]
  );

  // M5 propagation: is the plan anchored to an older Base version than the
  // latest published one? Pure detection (unit-tested in forecastImpact) — the
  // saved pointer is the anchor's scenarioKey/scenarioVersion.
  const macroStaleness = useMemo(
    () => detectMacroScenarioStaleness({
      savedScenarioKey: driverViewAnchor?.scenarioKey || 'Base',
      savedScenarioVersion: driverViewAnchor?.scenarioVersion ?? null,
      scenarios: macroScenarioList
    }),
    [driverViewAnchor, macroScenarioList]
  );

  // Changed-variable diff (old -> new) between the anchored version's variables
  // and the latest published version — same presentation as the console MX_03.
  const macroChangeDiffRows = useMemo(() => {
    if (!macroStaleness.stale || !macroStaleness.published) return [];
    const savedVersion = macroScenarioList.find((row) =>
      String(row.key || row.scenario_key || '').toLowerCase() === macroStaleness.scenarioKey.toLowerCase()
      && Number(row.version) === macroStaleness.savedVersion);
    return buildMacroVariableDiff({
      savedVariables: savedVersion?.variables || {},
      publishedVariables: macroStaleness.published?.variables || {}
    });
  }, [macroStaleness, macroScenarioList]);

  // Re-anchor is offered by workflow state: editable states allow it (levers
  // preserved); submitted/approved are blocked (reviewer must reopen first);
  // loaded shows the chip only. Aggregate has no unit anchor.
  const reanchorEditableStatuses = ['not_started', 'in_progress', 'returned'];
  const macroReanchorMode = useMemo(() => {
    if (isAggregate || !selectedMarket.planningEntity || selectedMarket.planningEntity === 'AGGREGATE') {
      return 'unavailable';
    }
    const status = submissionStatus || 'not_started';
    if (status === 'loaded') return 'chip-only';
    if (status === 'submitted' || status === 'approved') return 'blocked';
    if (reanchorEditableStatuses.includes(status)) return 'enabled';
    return 'chip-only';
  }, [isAggregate, selectedMarket.planningEntity, submissionStatus]);

  const runWorkspaceReanchor = useCallback(async () => {
    if (macroReanchorMode !== 'enabled' || !macroStaleness.published) return;
    setReanchorBusy(true);
    setReanchorError(null);
    setReanchorResult(null);
    // Capture the pre-re-anchor plan-vs-driver-view deltas so we can show the
    // before/after shift once the new anchor loads (levers are unchanged; only
    // the top-down reference moves).
    const beforeDeltas = (driverViewDeltas || []).map((delta) => ({
      metric: delta.metric, metricLabel: delta.metricLabel, year: delta.year,
      deviationPct: delta.deviationPct, exceeded: delta.exceeded, anchor: delta.anchor
    }));
    try {
      const result = await apiService.rebuildDriverViewAnchors({
        cycleCode: submission?.cycle_code || 'FY2027_BUDGET',
        actor: activeProfile.name,
        scenarioKey: macroStaleness.scenarioKey || 'Base',
        scenarioVersion: macroStaleness.publishedVersion,
        scope: 'entity',
        entity: selectedMarket.planningEntity,
        note: `Workspace re-anchor to ${macroStaleness.scenarioKey} v${macroStaleness.publishedVersion}`
      });
      const nextAnchor = (result?.anchors || []).find((anchor) =>
        anchor.planningEntity === selectedMarket.planningEntity) || result?.anchors?.[0] || null;
      if (nextAnchor) setDriverViewAnchor(nextAnchor);
      // After deltas are recomputed against the new anchor snapshot via the
      // shared driver-view path (buildDriverViewDeltas), pairing them by
      // metric+year with beforeDeltas gives the reviewer the shift.
      const afterAnchorSnapshot = nextAnchor?.anchorSnapshot || null;
      const afterDeltas = afterAnchorSnapshot
        ? buildDriverViewDeltas({ anchorSnapshot: afterAnchorSnapshot, planByYear: driverViewPlanSnapshot })
        : [];
      setReanchorResult({
        ...result,
        beforeAfter: afterDeltas.map((after) => {
          const before = beforeDeltas.find((b) => b.metric === after.metric && b.year === after.year) || null;
          return {
            key: `${after.metric}-${after.year}`,
            metricLabel: after.metricLabel,
            year: after.year,
            beforePct: before ? before.deviationPct : null,
            afterPct: after.deviationPct,
            exceeded: after.exceeded
          };
        })
      });
      await refreshMacroScenarioList();
    } catch (error) {
      const data = error?.response?.data;
      setReanchorError(data?.requiredPermission
        ? `${data?.error || 'Re-anchor denied'} (requires ${data.requiredPermission})`
        : (data?.error || error.message || 'Re-anchor failed'));
    } finally {
      setReanchorBusy(false);
    }
  }, [
    macroReanchorMode, macroStaleness, submission?.cycle_code, activeProfile.name,
    selectedMarket.planningEntity, refreshMacroScenarioList,
    driverViewDeltas, driverViewPlanSnapshot
  ]);

  // Centrally planned expense lines: FP&A-owned, driver-based, pushed down to
  // every business unit. Read-only here — the drivers explain the math so a
  // market leader knows what moves them (and that they don't input them).
  const centralPlanRows = useMemo(() => {
    if (!baselineAnnualSeries.length) return [];
    const c = {
      fdicBp: 6.0, ffePctNie: 4.5, ffeEscalatorPct: 1.0, intangiblesPctNie: 1.8,
      intangiblesDeclinePct: 10.0, oreoCarryPct: 5.0, netRentPctNie: 6.0,
      ...(centralSettings?.centralItems || {})
    };
    const items = [
      {
        id: 'fdic', label: 'FDIC insurance', coefKey: 'fdicBp', coefLabel: 'bp of avg deposits',
        driver: `Average deposits × ${c.fdicBp.toFixed(1)}bp assessment`,
        value: (row) => row.averageDeposits * (c.fdicBp / 10000)
      },
      {
        id: 'ffeDep', label: 'FF&E depreciation', coefKey: 'ffePctNie', coefLabel: '% of NIE base',
        driver: `Fixed-asset base ${c.ffePctNie.toFixed(1)}% of NIE, +${c.ffeEscalatorPct.toFixed(1)}% additions`,
        value: (row, index) => row.nonInterestExpense * (c.ffePctNie / 100) * ((1 + c.ffeEscalatorPct / 100) ** index)
      },
      {
        id: 'intangibles', label: 'Amortization of intangibles', coefKey: 'intangiblesPctNie', coefLabel: '% of NIE base',
        driver: `Acquisition schedule ${c.intangiblesPctNie.toFixed(1)}% of NIE, declining ${c.intangiblesDeclinePct.toFixed(0)}%/yr`,
        value: (row, index) => row.nonInterestExpense * (c.intangiblesPctNie / 100) * ((1 - c.intangiblesDeclinePct / 100) ** index)
      },
      {
        id: 'oreo', label: 'OREO expense', coefKey: 'oreoCarryPct', coefLabel: '% carry on losses',
        driver: `Scenario credit loss × loan exposure × ${c.oreoCarryPct.toFixed(1)}% carry`,
        value: (row) => row.averageLoans * (activeMacro.creditLoss / 100) * (c.oreoCarryPct / 100)
      },
      {
        id: 'netRent', label: 'Net rent & related', coefKey: 'netRentPctNie', coefLabel: '% of NIE base',
        driver: `Lease schedule ${c.netRentPctNie.toFixed(1)}% of NIE with contractual escalators`,
        value: (row, index) => row.nonInterestExpense * (c.netRentPctNie / 100) * ((1 + (activeMacro.expenseInflation || 3.1) / 100) ** index)
      }
    ];
    return items.map((item) => ({
      ...item,
      coefValue: c[item.coefKey],
      byYear: baselineAnnualSeries.map((row, index) => item.value(row, index))
    }));
  }, [baselineAnnualSeries, activeMacro, centralSettings]);

  const baseMonthlyNetIncomeMatrix = useMemo(
    () =>
      FORECAST_YEARS.map((year) => {
        const row = { fiscalYear: `FY${year}` };
        baselineMonthlySeries
          .filter((month) => month.fiscalYearNumber === year)
          .forEach((month) => {
            row[month.month] = month.netIncome;
          });
        return row;
      }),
    [baselineMonthlySeries]
  );

  const baseMonthlyNiiMatrix = useMemo(
    () =>
      FORECAST_YEARS.map((year) => {
        const row = { fiscalYear: `FY${year}` };
        baselineMonthlySeries
          .filter((month) => month.fiscalYearNumber === year)
          .forEach((month) => {
            row[month.month] = month.nii;
          });
        return row;
      }),
    [baselineMonthlySeries]
  );

  const macroAnnualPath = useMemo(
    () => buildMacroAnnualPath(MACRO_SETS.Base, activeMacro),
    [activeMacro]
  );

  const macroDivergenceRows = useMemo(
    () =>
      MACRO_DIVERGENCE_METRICS.map((metric) => {
        const metricKey = `${metric.key[0].toUpperCase()}${metric.key.slice(1)}`;
        const annualValues = macroAnnualPath.map((row) => ({
          year: row.year,
          base: row[`base${metricKey}`],
          scenario: row[`scenario${metricKey}`],
          variance: row[`scenario${metricKey}`] - row[`base${metricKey}`]
        }));

        return {
          ...metric,
          annualValues,
          totalVariance: annualValues.reduce((sum, value) => sum + value.variance, 0) / annualValues.length
        };
      }),
    [macroAnnualPath]
  );

  const annualResults = useMemo(
    () =>
      baselineAnnualSeries.map((baseline, index) => {
        const scenarioBase = scenarioAnnualSeries[index];
        const adjusted = adjustedAnnualSeries[index];
        const variancePct = baseline.netIncome !== 0 ? ((adjusted.netIncome - baseline.netIncome) / Math.abs(baseline.netIncome)) * 100 : 0;
        const rateVolumeVariance = adjusted.nii - baseline.nii;
        const feeVariance = adjusted.nonInterestIncome - baseline.nonInterestIncome;
        const expenseVariance = -(adjusted.nonInterestExpense - baseline.nonInterestExpense);
        const creditVariance = -(adjusted.creditProvision - baseline.creditProvision);
        const taxVariance = -(adjusted.taxExpense - baseline.taxExpense);
        const largestDriver = [
          { label: 'NII', value: rateVolumeVariance },
          { label: 'Fees', value: feeVariance },
          { label: 'Expense', value: expenseVariance },
          { label: 'Credit', value: creditVariance },
          { label: 'Tax', value: taxVariance }
        ].sort((a, b) => Math.abs(b.value) - Math.abs(a.value))[0];

        return {
          year: baseline.year,
          baseline: baseline.netIncome,
          scenarioBase: scenarioBase.netIncome,
          adjusted: adjusted.netIncome,
          delta: adjusted.netIncome - baseline.netIncome,
          variancePct,
          rateVolumeVariance,
          feeVariance,
          expenseVariance,
          creditVariance,
          taxVariance,
          largestDriver: largestDriver.label,
          niiDelta: adjusted.nii - baseline.nii,
          efficiency: adjusted.efficiencyRatio,
          nim: adjusted.nim,
          loanGrowth: adjusted.loanGrowth,
          depositGrowth: adjusted.depositGrowth
        };
      }),
    [adjustedAnnualSeries, baselineAnnualSeries, scenarioAnnualSeries]
  );

  const executiveBridgeRows = useMemo(() => {
    const yearOne = annualResults[0];

    return buildWaterfallRows({
      startName: 'Baseline plan',
      startValue: yearOne.baseline,
      startFill: 'hsl(var(--chart-5))',
      deltas: [
        { name: 'NII / volume', value: yearOne.rateVolumeVariance, fill: 'hsl(var(--chart-2))' },
        { name: 'Fees', value: yearOne.feeVariance, fill: 'hsl(var(--chart-1))' },
        { name: 'Expense control', value: yearOne.expenseVariance, fill: 'hsl(var(--chart-4))' },
        { name: 'Credit', value: yearOne.creditVariance, fill: 'hsl(var(--destructive))' },
        { name: 'Tax', value: yearOne.taxVariance, fill: 'hsl(var(--chart-5))' }
      ],
      endName: 'Modified plan',
      endValue: yearOne.adjusted,
      endFill: 'hsl(var(--chart-3))'
    });
  }, [annualResults]);

  const executiveBridgeDomain = useMemo(() => getWaterfallDomain(executiveBridgeRows), [executiveBridgeRows]);

  const annualVarianceWaterfalls = useMemo(
    () =>
      annualResults.map((row) => ({
        year: row.year,
        baseline: row.baseline,
        adjusted: row.adjusted,
        delta: row.delta,
        largestDriver: row.largestDriver,
        rows: buildWaterfallRows({
          startName: 'Baseline',
          startValue: row.baseline,
          startFill: 'hsl(var(--chart-5))',
          deltas: [
            { name: 'NII', value: row.rateVolumeVariance, fill: 'hsl(var(--chart-2))' },
            { name: 'Fees', value: row.feeVariance, fill: 'hsl(var(--chart-1))' },
            { name: 'Expense', value: row.expenseVariance, fill: 'hsl(var(--chart-4))' },
            { name: 'Credit', value: row.creditVariance, fill: 'hsl(var(--destructive))' },
            { name: 'Tax', value: row.taxVariance, fill: 'hsl(var(--chart-5))' }
          ],
          endName: 'Modified',
          endValue: row.adjusted,
          endFill: 'hsl(var(--chart-3))'
        })
      })),
    [annualResults]
  );

  const annualVarianceWaterfallDomain = useMemo(
    () => getWaterfallDomain(annualVarianceWaterfalls.flatMap((year) => year.rows)),
    [annualVarianceWaterfalls]
  );

  // Single continuous waterfall spanning every forecast year:
  // [FY Base][driver floats][FY Plan] ... repeated per year on one axis.
  const multiYearWaterfallRows = useMemo(
    () =>
      annualVarianceWaterfalls.flatMap((yearBridge) => {
        const shortYear = String(yearBridge.year).replace('FY', "'");
        const dim = resultsFocusYear !== null && yearBridge.year !== resultsFocusYear;
        return yearBridge.rows.map((row, index) => ({
          ...row,
          dim,
          name:
            index === 0
              ? `${yearBridge.year} Base`
              : index === yearBridge.rows.length - 1
              ? `${yearBridge.year} Plan`
              : `${row.name} ${shortYear}`
        }));
      }),
    [annualVarianceWaterfalls, resultsFocusYear]
  );

  const focusedResult = useMemo(
    () => (resultsFocusYear !== null
      ? annualResults.find((row) => row.year === resultsFocusYear) || null
      : null),
    [annualResults, resultsFocusYear]
  );

  const varianceLineRows = useMemo(
    () =>
      BASE_REVIEW_METRICS.map((metric) => {
        const baselineFy1 = getAnnualField(baselineAnnualSeries[0], metric.key);
        const adjustedFy1 = getAnnualField(adjustedAnnualSeries[0], metric.key);
        const baselineFy2 = getAnnualField(baselineAnnualSeries[1], metric.key);
        const adjustedFy2 = getAnnualField(adjustedAnnualSeries[1], metric.key);
        const baselineCumulative =
          metric.type === 'flow'
            ? baselineAnnualSeries.reduce((sum, row) => sum + getAnnualField(row, metric.key), 0)
            : getAnnualField(baselineAnnualSeries[baselineAnnualSeries.length - 1], metric.key);
        const adjustedCumulative =
          metric.type === 'flow'
            ? adjustedAnnualSeries.reduce((sum, row) => sum + getAnnualField(row, metric.key), 0)
            : getAnnualField(adjustedAnnualSeries[adjustedAnnualSeries.length - 1], metric.key);

        return {
          ...metric,
          baselineFy1,
          adjustedFy1,
          varianceFy1: adjustedFy1 - baselineFy1,
          baselineFy2,
          adjustedFy2,
          varianceFy2: adjustedFy2 - baselineFy2,
          baselineCumulative,
          adjustedCumulative,
          varianceCumulative: adjustedCumulative - baselineCumulative
        };
      }),
    [adjustedAnnualSeries, baselineAnnualSeries]
  );

  const companyReturnMetrics = useMemo(() => {
    const averageAssets = model.averageLoans / 0.72;
    const tangibleCommonEquity = averageAssets * 0.084;
    const cet1 = 11.7 - model.rwaGrowth * 0.08 - activeMacro.creditLoss * 0.18;

    return {
      roa: (model.netIncome / averageAssets) * 100,
      rotce: (model.netIncome / tangibleCommonEquity) * 100,
      nim: model.nim * 100,
      efficiency: model.efficiencyRatio * 100,
      cet1,
      loanGrowth: model.adjustedLoanGrowth
    };
  }, [activeMacro.creditLoss, model]);

  const peerComparisonRows = useMemo(
    () =>
      PEER_METRICS.map((metric) => {
        const values = PEER_PRO_FORMA_SET.map((peer) => peer[metric.key]);
        const min = Math.min(...values);
        const max = Math.max(...values);
        const midpoint = median(values);
        const value = companyReturnMetrics[metric.key];
        const spread = Math.max(max - min, 0.01);
        const position = clamp(((value - min) / spread) * 100, 0, 100);
        const variance = value - midpoint;
        const favorable =
          metric.favorable === 'lower'
            ? value <= midpoint
            : metric.favorable === 'higher'
              ? value >= midpoint
              : Math.abs(variance) <= 1.5;

        return {
          ...metric,
          value,
          min,
          max,
          median: midpoint,
          position,
          peerPositions: PEER_PRO_FORMA_SET.map((peer) => ({
            peer: peer.peer,
            value: peer[metric.key],
            position: clamp(((peer[metric.key] - min) / spread) * 100, 0, 100)
          })),
          variance,
          favorable
        };
      }),
    [companyReturnMetrics]
  );

  const forecastSeries = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => {
        const month = index + 1;
        const manualWeight = getManualWeight(month);
        const loans =
          basePortfolio.loans * (1 + (model.adjustedLoanGrowth / 100) * (month / 12)) +
          model.loans12 * (month / 12);
        const deposits =
          basePortfolio.deposits * (1 + (model.adjustedDepositGrowth / 100) * (month / 12)) +
          model.deposits12 * (month / 12);
        const nii =
          (model.nii / 12) *
          (1 + (month - 1) * 0.006 + (activeMacro.sofr - MACRO_SETS.Base.sofr) * 0.008);

        return {
          month: `M${month}`,
          loans,
          deposits,
          nii,
          manualWeight,
          baselineWeight: 100 - manualWeight
        };
      }),
    [activeMacro.sofr, basePortfolio, model]
  );

  const constraintRows = useMemo(() => {
    const lcr = 126 - activeMacro.depositRunoff * 1.8 - Math.max(0, model.adjustedLoanGrowth - 5) * 0.9;
    const cet1 = 11.7 - model.rwaGrowth * 0.08 - activeMacro.creditLoss * 0.18;
    const leverage = 9.1 - model.adjustedLoanGrowth * 0.04;
    const totalRiskBased = 14.2 - model.rwaGrowth * 0.07 - activeMacro.creditLoss * 0.12;
    const ldr = 89.5 + model.adjustedLoanGrowth * 0.55 - model.adjustedDepositGrowth * 0.32;
    const wholesaleFunding = 7.2 + activeMacro.depositRunoff * 0.72 + Math.max(0, ldr - 92) * 0.35;
    const creConcentration = 242 + model.adjustedLoanGrowth * 2.5 + model.netHires * 0.9;
    const runoffStress = activeMacro.depositRunoff + shocks.liquidityRunoffPct * 0.85;
    const buildYearPoints = ({ min, max, green, yellow, direction = 'higher', digits = 1, calculate }) =>
      annualResults.map((row, index) => {
        const value = calculate(row, index);
        return {
          year: row.year,
          value,
          display: formatPercent(value, digits),
          status: statusFor(value, green, yellow, direction),
          position: toBulletPercent(value, min, max)
        };
      });

    return [
      {
        metric: 'LCR',
        value: lcr,
        display: formatPercent(lcr),
        status: statusFor(lcr, 118, 108),
        direction: 'higher',
        min: 90,
        max: 135,
        target: 118,
        targetLabel: 'Target >= 118%',
        bands: higherIsBetterBands(90, 108, 118, 135),
        yearPoints: buildYearPoints({
          min: 90,
          max: 135,
          green: 118,
          yellow: 108,
          calculate: (row, index) =>
            126 - (activeMacro.depositRunoff + index * 0.18) * 1.8 - Math.max(0, row.loanGrowth - 5) * 0.9 - index * 0.35
        }),
        threshold: 'Red <= 100%, yellow buffer to 118%',
        driver: 'Deposit runoff and loan growth funding need'
      },
      {
        metric: 'CET1',
        value: cet1,
        display: formatPercent(cet1),
        status: statusFor(cet1, 10.5, 8.5),
        direction: 'higher',
        min: 6,
        max: 14,
        target: 10.5,
        targetLabel: 'Target >= 10.5%',
        bands: higherIsBetterBands(6, 8.5, 10.5, 14),
        yearPoints: buildYearPoints({
          min: 6,
          max: 14,
          green: 10.5,
          yellow: 8.5,
          calculate: (row, index) =>
            11.7 - (row.loanGrowth * 0.82 + activeMacro.creditLoss * 0.35) * 0.08 - activeMacro.creditLoss * 0.18 - index * 0.04
        }),
        threshold: 'Red near regulatory minimum, yellow management buffer',
        driver: 'RWA growth from market production'
      },
      {
        metric: 'Tier 1 Leverage',
        value: leverage,
        display: formatPercent(leverage),
        status: statusFor(leverage, 8.2, 6.2),
        direction: 'higher',
        min: 3,
        max: 11,
        target: 8.2,
        targetLabel: 'Target >= 8.2%',
        bands: higherIsBetterBands(3, 6.2, 8.2, 11),
        yearPoints: buildYearPoints({
          min: 3,
          max: 11,
          green: 8.2,
          yellow: 6.2,
          calculate: (row, index) => 9.1 - row.loanGrowth * 0.04 - index * 0.04
        }),
        threshold: 'Red near 4% rule, yellow management buffer',
        driver: 'Balance sheet expansion'
      },
      {
        metric: 'Total RBC',
        value: totalRiskBased,
        display: formatPercent(totalRiskBased),
        status: statusFor(totalRiskBased, 12.5, 10.5),
        direction: 'higher',
        min: 8,
        max: 16,
        target: 12.5,
        targetLabel: 'Target >= 12.5%',
        bands: higherIsBetterBands(8, 10.5, 12.5, 16),
        yearPoints: buildYearPoints({
          min: 8,
          max: 16,
          green: 12.5,
          yellow: 10.5,
          calculate: (row, index) =>
            14.2 - (row.loanGrowth * 0.82 + activeMacro.creditLoss * 0.35) * 0.07 - activeMacro.creditLoss * 0.12 - index * 0.04
        }),
        threshold: 'Red near 8% rule, yellow management buffer',
        driver: 'Credit stress and loan growth'
      },
      {
        metric: 'Loan-to-Deposit',
        value: ldr,
        display: formatPercent(ldr),
        status: statusFor(ldr, 92, 98, 'lower'),
        direction: 'lower',
        min: 70,
        max: 110,
        target: 92,
        targetLabel: 'Target <= 92%',
        bands: lowerIsBetterBands(70, 92, 98, 110),
        yearPoints: buildYearPoints({
          min: 70,
          max: 110,
          green: 92,
          yellow: 98,
          direction: 'lower',
          calculate: (row, index) => 89.5 + row.loanGrowth * 0.55 - row.depositGrowth * 0.32 + index * 0.18
        }),
        threshold: 'Yellow above 92%, red above 98%',
        driver: 'Loans growing faster than core deposits'
      },
      {
        metric: 'Wholesale Funding',
        value: wholesaleFunding,
        display: formatPercent(wholesaleFunding),
        status: statusFor(wholesaleFunding, 12, 16, 'lower'),
        direction: 'lower',
        min: 0,
        max: 20,
        target: 12,
        targetLabel: 'Target <= 12%',
        bands: lowerIsBetterBands(0, 12, 16, 20),
        yearPoints: buildYearPoints({
          min: 0,
          max: 20,
          green: 12,
          yellow: 16,
          direction: 'lower',
          calculate: (row, index) => {
            const annualLdr = 89.5 + row.loanGrowth * 0.55 - row.depositGrowth * 0.32 + index * 0.18;
            return 7.2 + (activeMacro.depositRunoff + index * 0.18) * 0.72 + Math.max(0, annualLdr - 92) * 0.35;
          }
        }),
        threshold: 'Yellow above 12%, red above 16%',
        driver: 'Funding gap under liquidity stress'
      },
      {
        metric: 'CRE Concentration',
        value: creConcentration,
        display: formatPercent(creConcentration, 0),
        status: statusFor(creConcentration, 275, 300, 'lower'),
        direction: 'lower',
        min: 150,
        max: 325,
        target: 275,
        targetLabel: 'Target <= 275%',
        bands: lowerIsBetterBands(150, 275, 300, 325),
        yearPoints: buildYearPoints({
          min: 150,
          max: 325,
          green: 275,
          yellow: 300,
          direction: 'lower',
          digits: 0,
          calculate: (row, index) => 242 + row.loanGrowth * 2.5 + model.netHires * 0.9 + index * 3
        }),
        threshold: 'Yellow above 275%, red above 300%',
        driver: 'Commercial hire production mix'
      },
      {
        metric: 'Deposit Runoff Stress',
        value: runoffStress,
        display: formatPercent(runoffStress),
        status: statusFor(runoffStress, 7, 10, 'lower'),
        direction: 'lower',
        min: 0,
        max: 12,
        target: 7,
        targetLabel: 'Target <= 7%',
        bands: lowerIsBetterBands(0, 7, 10, 12),
        yearPoints: buildYearPoints({
          min: 0,
          max: 12,
          green: 7,
          yellow: 10,
          direction: 'lower',
          calculate: (_, index) => activeMacro.depositRunoff + shocks.liquidityRunoffPct * 0.85 + index * 0.18
        }),
        threshold: 'Yellow above 7%, red above 10%',
        driver: 'Macro liquidity toggle and market deposit decay'
      },
      {
        metric: 'Loan Growth vs Appetite Cap',
        value: model.adjustedLoanGrowth,
        display: formatPercent(model.adjustedLoanGrowth),
        status: statusFor(
          model.adjustedLoanGrowth,
          shocks.loanGrowthLimiterPct - 1.5,
          shocks.loanGrowthLimiterPct,
          'lower'
        ),
        direction: 'lower',
        min: 0,
        max: Math.max(12, shocks.loanGrowthLimiterPct + 2),
        target: shocks.loanGrowthLimiterPct - 1.5,
        targetLabel: `Appetite cap ${formatPercent(shocks.loanGrowthLimiterPct, 1)}`,
        bands: lowerIsBetterBands(
          0,
          shocks.loanGrowthLimiterPct - 1.5,
          shocks.loanGrowthLimiterPct,
          Math.max(12, shocks.loanGrowthLimiterPct + 2)
        ),
        yearPoints: buildYearPoints({
          min: 0,
          max: Math.max(12, shocks.loanGrowthLimiterPct + 2),
          green: shocks.loanGrowthLimiterPct - 1.5,
          yellow: shocks.loanGrowthLimiterPct,
          direction: 'lower',
          calculate: (row) => row.loanGrowth
        }),
        threshold: `Watch within 1.5pts of cap, breach above ${formatPercent(shocks.loanGrowthLimiterPct, 1)}`,
        driver: 'Centrally set growth appetite — monitored here, not edited in OP_03'
      }
    ];
  }, [activeMacro, annualResults, model, shocks.liquidityRunoffPct, shocks.loanGrowthLimiterPct]);

  const constraintSummary = useMemo(() => {
    const counts = constraintRows.reduce(
      (summary, row) => ({
        ...summary,
        [row.status]: summary[row.status] + 1
      }),
      { green: 0, yellow: 0, red: 0 }
    );
    const firstIssue = constraintRows.find((row) => row.status === 'red') || constraintRows.find((row) => row.status === 'yellow');

    return {
      ...counts,
      firstIssue
    };
  }, [constraintRows]);

  const bridgeRows = useMemo(() => {
    const balanceRateDelta = model.nii - baselineModel.nii;
    const hiringEconomicsDelta =
      model.newHireLoanInterestIncome +
      model.depositFundingBenefit +
      model.fee12 +
      model.unusedCommitmentFees -
      model.expense12 -
      model.creditProvision +
      baselineModel.creditProvision;
    const expenseCreditTaxDelta =
      model.netIncome - baselineModel.netIncome - balanceRateDelta - hiringEconomicsDelta;

    return buildWaterfallRows({
      startName: 'Actual run-rate',
      startValue: baselineModel.netIncome,
      startFill: 'hsl(var(--chart-5))',
      deltas: [
        { name: 'Balance + rate', value: balanceRateDelta, fill: 'hsl(var(--chart-2))' },
        { name: 'Hiring economics', value: hiringEconomicsDelta, fill: 'hsl(var(--chart-1))' },
        { name: 'Expense / credit / tax', value: expenseCreditTaxDelta, fill: 'hsl(var(--chart-4))' }
      ],
      endName: 'Final forecast',
      endValue: model.netIncome,
      endFill: 'hsl(var(--chart-3))'
    });
  }, [baselineModel, model]);

  const bridgeDomain = useMemo(() => getWaterfallDomain(bridgeRows), [bridgeRows]);

  const calculationRows = useMemo(
    () => [
      {
        driver: 'Ending loans',
        formula: 'Beginning loans x portfolio growth + capped market production',
        output: formatMillions(model.endingLoans, 0),
        source: 'Portfolio engine + hiring'
      },
      {
        driver: 'Ending deposits',
        formula: 'Beginning deposits x deposit growth + market deposit production',
        output: formatMillions(model.endingDeposits, 0),
        source: 'Portfolio engine + hiring'
      },
      {
        driver: 'Loan yield',
        formula: 'Base loan yield + SOFR change x loan repricing beta',
        output: formatPercent(model.loanYield, 2),
        source: 'Rates + pricing beta'
      },
      {
        driver: 'Deposit cost',
        formula: 'Base deposit cost + SOFR change x deposit beta, floored',
        output: formatPercent(model.depositCost, 2),
        source: 'Rates + deposit beta'
      },
      {
        driver: 'Net interest income',
        formula: 'Interest income - deposit interest expense + core deposit funding value',
        output: formatMillions(model.nii),
        source: 'Portfolio + new production'
      },
      {
        driver: 'Noninterest income',
        formula: 'Baseline fees + hire fees + unused commitment fees',
        output: formatMillions(model.nonInterestIncome),
        source: 'Fee engine'
      },
      {
        driver: 'Noninterest expense',
        formula: 'Baseline NIE + inflation + hire expense + spend delta - legal recoupment delta',
        output: formatMillions(model.nonInterestExpense),
        source: 'Expense levers'
      },
      {
        driver: 'Provision',
        formula: 'Baseline provision + loan-growth provision + credit shock + new production provision',
        output: formatMillions(model.creditProvision),
        source: 'Credit macro'
      },
      {
        driver: 'Taxed net income',
        formula: '(NII + fees - NIE - provision) x after-tax rate',
        output: formatMillions(model.netIncome),
        source: `Tax rate ${formatPercent(basePortfolio.taxRate, 1)}`
      }
    ],
    [basePortfolio.taxRate, model]
  );

  // Business-unit review validates row integrity; enterprise Watch/Breach policy remains in the executive view.
  const stagedRows = useMemo(
    () => [
      {
        gl: '419',
        line: 'Interest Income - Loans',
        module: 'Baseline portfolio',
        period: '60 monthly MTD rows',
        value: model.interestIncome,
        status: 'Validated'
      },
      {
        gl: '459',
        line: 'Interest Expense - Deposits',
        module: 'Macro rate engine',
        period: '60 monthly MTD rows',
        value: model.interestExpense * -1,
        status: 'Validated'
      },
      {
        gl: '529',
        line: 'Fee Income - Advisory / Treasury',
        module: 'New-hire economics',
        period: '60 monthly MTD rows',
        value: model.nonInterestIncome,
        status: 'Validated'
      },
      {
        gl: '599',
        line: 'Unused Commitment Fee Income',
        module: 'New-hire economics',
        period: '60 monthly MTD rows',
        value: model.unusedCommitmentFees,
        status: 'Validated'
      },
      {
        gl: '659',
        line: 'Legal Expense Recoupment',
        module: 'Market expense lever',
        period: '60 monthly MTD rows',
        value: model.legalRecoupmentCredit,
        status: 'Validated'
      },
      {
        gl: '759',
        line: 'Travel & Entertainment',
        module: 'Market expense lever',
        period: '60 monthly MTD rows',
        value: expenseInputs.travel.value * -1,
        status: expenseInputs.travel.value > expenseInputs.travel.centralLimit ? 'Warning' : 'Validated'
      },
      {
        gl: '709',
        line: 'Business Development',
        module: 'Market expense lever',
        period: '60 monthly MTD rows',
        value: expenseInputs.businessDevelopment.value * -1,
        status:
          expenseInputs.businessDevelopment.value > expenseInputs.businessDevelopment.centralLimit
            ? 'Warning'
            : 'Validated'
      },
      {
        gl: '779',
        line: 'Community Investment / Donations',
        module: 'Market expense lever',
        period: '60 monthly MTD rows',
        value: expenseInputs.community.value * -1,
        status: expenseInputs.community.value > expenseInputs.community.centralLimit ? 'Warning' : 'Validated'
      },
      {
        gl: '609',
        line: 'New Hire Compensation & Benefits',
        module: 'Hiring economics',
        period: '60 monthly MTD rows',
        value: model.expense12 * -1,
        status: 'Validated'
      },
      {
        gl: '850',
        line: 'Provision for Credit Losses',
        module: 'Credit / macro stress',
        period: '60 monthly MTD rows',
        value: model.creditProvision * -1,
        status: 'Validated'
      },
      {
        gl: '909',
        line: 'Income Tax Expense',
        module: 'Tax engine',
        period: '60 monthly MTD rows',
        value: model.taxExpense * -1,
        status: 'Validated'
      }
    ].map((row) => ({
      ...row,
      forecastProvenance: resolveForecastProvenance(row.gl, forecastProvenanceByAccount)
    })),
    [expenseInputs, forecastProvenanceByAccount, model]
  );

  const validationCounts = stagedRows.reduce(
    (counts, row) => ({
      ...counts,
      [row.status]: (counts[row.status] || 0) + 1
    }),
    {}
  );

  const recentLineage = Object.values(lineage).slice(-6).reverse();
  const lineageHistoryEntries = Object.values(lineage).slice().reverse().map((entry, index) => ({
    id: `${entry.key || entry.field || 'change'}-${entry.timestamp || index}`,
    label: entry.field || 'Forecast assumption changed',
    event: entry.field || 'Forecast assumption changed',
    actor: entry.user,
    comment: `${formatLineageValue(entry.field, entry.priorValue)} to ${formatLineageValue(entry.field, entry.newValue)}`,
    created_at: entry.timestamp
  }));
  const hasBlockingRows = stagedRows.some((row) => row.status === 'Blocked');
  const loadApproved = loadState === 'approved' || loadState === 'loaded';
  const loadCompleted = loadState === 'loaded';
  const activeWorkforceLeverCount = Object.values(workforceInputs).filter((lever) => lever.value !== lever.baseline).length;
  const activeExpenseLeverCount = Object.values(expenseInputs).filter((lever) => lever.value !== lever.centralBudget).length;
  const activeShockCount = Object.entries(shocks).filter(([field, value]) => value !== (field === 'loanGrowthLimiterPct' ? 8.5 : 0)).length;
  const activeYearAdjustmentCount = Object.values(yearAdjustments).filter(
    (values) => values.some((value) => Number(value) !== 0)
  ).length;
  const activeWorkforceYearCount = WORKFORCE_YEAR_DEFS.filter((def) =>
    (workforceYearInputs[def.id] || []).some((value) => Number(value) !== Number(def.current))
  ).length;
  const activeFeeDriverCount = feeDrivers.filter((driver) =>
    driver.ratioBps.some((value) => Number(value) !== 0) || driver.overlays.length > 0
  ).length;
  const activeQuarterlyGrowthCount = hasQuarterlyGrowthChanges(quarterlyGrowth, latestActualMonth) ? 1 : 0;
  const excelOverrideCount = Object.keys(monthlyMetricOverrides || {}).length + Object.keys(glRowOverrides || {}).length;
  const totalOverrideCount = activeWorkforceLeverCount + activeExpenseLeverCount + activeShockCount
    + activeYearAdjustmentCount + activeWorkforceYearCount + activeFeeDriverCount
    + activeQuarterlyGrowthCount + excelOverrideCount;

  const modelRef = useRef(null);
  modelRef.current = {
    annualResults,
    constraintSummary,
    forecastLoadRows,
    stagedRows,
    validationCounts,
    hasBlockingRows,
    totalOverrideCount,
    scenarioVersion,
    driverViewPlanSnapshot,
    driverViewAnchor
  };

  const editableStatuses = ['not_started', 'in_progress', 'returned'];
  const canEditSubmission =
    isLiaison && (!submissionStatus || editableStatuses.includes(submissionStatus));
  // Item G: in FP&A-adjust mode the manager may edit the live model (levers + AI
  // prompt box), but the market autosave stays gated on canEditSubmission only —
  // so the market's submitted inputs are never overwritten. FP&A edits persist as
  // 'adjust' events to the FP&A layer instead.
  const canEditEffective = canEditSubmission || fpaAdjustMode;

  const buildSavePayload = () => {
    const model = modelRef.current || {};
    return {
      actor: activeActor,
      inputs: {
        marketInputs,
        workforceInputs,
        expenseInputs,
        shocks,
        lineage,
        scenario,
        macroMode,
        hireRows,
        yearAdjustments,
        quarterlyGrowth,
        workforceYearInputs,
        feeDrivers,
        hrPlan,
        monthlyMetricOverrides,
        glRowOverrides,
        forecastProvenanceByAccount,
        workspaceState,
        monthlyPlan: adjustedMonthlySeries,
        loadRows: model.forecastLoadRows,
        stagedRows: model.stagedRows,
        // M4: pin the driver-view anchor the plan was measured against.
        driverViewAnchorId: model.driverViewAnchor?.id || null,
        macroScenarioKey: model.driverViewAnchor?.scenarioKey || 'Base',
        macroScenarioVersion: model.driverViewAnchor?.scenarioVersion ?? null
      },
      narratives: Object.entries(narratives).map(([trigger, text]) => ({ trigger, text })),
      modelSnapshot: {
        capturedAt: new Date().toISOString(),
        scenarioVersion: model.scenarioVersion,
        annualResults: model.annualResults,
        // M4: driver-view snapshot (byYear metric map) + anchor pin so the
        // server B7 gate recomputes deviations against the exact anchor version.
        driverView: { byYear: model.driverViewPlanSnapshot || {} },
        driverViewAnchorId: model.driverViewAnchor?.id || null,
        macroScenarioKey: model.driverViewAnchor?.scenarioKey || 'Base',
        macroScenarioVersion: model.driverViewAnchor?.scenarioVersion ?? null,
        // Working balance/fee path ($MM) so the executive review can overlay
        // the forecast actually being worked on, not just prior plan.
        balanceSheetPath: adjustedAnnualSeries.map((row) => ({
          year: row.fiscalYearNumber,
          loans: row.endingLoans,
          deposits: row.endingDeposits,
          fees: row.nonInterestIncome
        })),
        constraintSummary: {
          green: model.constraintSummary?.green || 0,
          yellow: model.constraintSummary?.yellow || 0,
          red: model.constraintSummary?.red || 0
        },
        overrides: model.totalOverrideCount,
        validationCounts: model.validationCounts,
        blockedRows: (model.stagedRows || []).filter((row) => row.status === 'Blocked').length,
        monthlyMetricOverrideCount: Object.keys(monthlyMetricOverrides || {}).length,
        glRowOverrideCount: Object.keys(glRowOverrides || {}).length
      }
    };
  };

  const buildSavePayloadRef = useRef(buildSavePayload);
  buildSavePayloadRef.current = buildSavePayload;

  const saveDraftNow = useCallback(async () => {
    if (!submissionId) return null;
    setSaveState('saving');
    try {
      const response = await apiService.saveForecastSubmission(submissionId, buildSavePayloadRef.current());
      setSubmissionStatus((status) => (status === 'not_started' ? 'in_progress' : status));
      setLastSavedAt(response?.savedAt || new Date().toISOString());
      setSaveError(null);
      setSaveState('saved');
      return response;
    } catch (error) {
      setSaveError(error?.response?.data?.error || error.message || 'Draft save failed.');
      setSaveState('error');
      throw error;
    }
  }, [submissionId]);

  const retrySave = useCallback(() => {
    if (!submissionId || saveState === 'saving') return;
    saveDraftNow().catch(() => {});
  }, [saveDraftNow, saveState, submissionId]);

  useEffect(() => {
    if (!submissionId || !hydrated || !canEditSubmission) return undefined;
    setSaveState('pending');
    const timer = setTimeout(() => {
      saveDraftNow().catch(() => {});
    }, 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    marketInputs,
    workforceInputs,
    expenseInputs,
    shocks,
    lineage,
    scenario,
    macroMode,
    hireRows,
    yearAdjustments,
    quarterlyGrowth,
    workforceYearInputs,
    feeDrivers,
    monthlyMetricOverrides,
    glRowOverrides,
    forecastProvenanceByAccount,
    narratives,
    workspaceState,
    hydrated,
    submissionId,
    canEditSubmission,
    saveDraftNow
  ]);

  const applyManualLeverPreviewChange = (change, direction = 'apply') => {
    const state = direction === 'undo' ? change.priorState : change.nextState;
    if (state.yearAdjustments) setYearAdjustments(cloneYearAdjustments(state.yearAdjustments));
    if (state.quarterlyGrowth) setQuarterlyGrowth(clonePlainValue(state.quarterlyGrowth));
    if (state.shocks) setShocks({ ...state.shocks });
    if (state.expenseInputs) setExpenseInputs(clonePlainValue(state.expenseInputs));
    if (state.feeDrivers) setFeeDrivers(clonePlainValue(state.feeDrivers));

    if (change.lineage) {
      const { key, field, priorValue, newValue, source = 'FP&A input' } = change.lineage;
      recordLineage(
        key,
        field,
        direction === 'undo' ? newValue : priorValue,
        direction === 'undo' ? priorValue : newValue,
        direction === 'undo' ? `${source} · undo` : source
      );
    }
  };

  const stageManualLeverPreview = (change) => {
    const proposedMonthly = buildAdjustedMonthlySeriesForState(change.overrides);
    const diffLines = buildForecastImpactDiff({
      lines: buildForecastImpactLines(adjustedMonthlySeries, proposedMonthly),
      years: ADJUSTMENT_YEARS
    });
    const preview = {
      id: `${change.kind}-${Date.now()}`,
      title: change.title,
      detail: change.detail,
      mode: autoApplyLeverPreview ? 'applied' : 'pending',
      diffLines,
      primaryFields: change.primaryFields || [],
      change
    };

    if (autoApplyLeverPreview) {
      applyManualLeverPreviewChange(change);
    }
    setPendingLeverPreview(preview);
  };

  const commitPendingLeverPreview = () => {
    if (!pendingLeverPreview || pendingLeverPreview.mode === 'applied') return;
    applyManualLeverPreviewChange(pendingLeverPreview.change);
    setPendingLeverPreview((previous) => previous ? { ...previous, mode: 'applied' } : previous);
  };

  const undoPendingLeverPreview = () => {
    if (!pendingLeverPreview) return;
    if (pendingLeverPreview.mode === 'applied') {
      applyManualLeverPreviewChange(pendingLeverPreview.change, 'undo');
    }
    setPendingLeverPreview(null);
  };

  const classifyPendingPreviewLine = (line) =>
    pendingLeverPreview?.primaryFields?.includes(line.field) ? 'primary' : 'flow';

  const updateWorkforceYear = (def, yearIndex, value) => {
    const prior = workforceYearInputs[def.id][yearIndex];
    setWorkforceYearInputs((previous) => ({
      ...previous,
      [def.id]: previous[def.id].map((v, i) => (i === yearIndex ? value : v))
    }));
    recordLineage(
      `wfyear-${def.id}-${yearIndex}`,
      `${def.label} FY${ADJUSTMENT_YEARS[yearIndex]}`,
      prior,
      value
    );
  };

  const updateHireRows = (rows, label, rowId) => {
    setHireRows(rows);
    recordLineage(`hire-${rowId || 'rows'}-${label}`, `Hiring · ${label}`, hireRows.length, rows.length);
  };

  const updateAiProvenanceForLevers = useCallback((leverIds, delta = 1) => {
    const nextCounts = { ...aiProvenanceLeverCountsRef.current };
    (Array.isArray(leverIds) ? leverIds : [leverIds]).filter(Boolean).forEach((leverId) => {
      const nextCount = (nextCounts[leverId] || 0) + delta;
      if (nextCount > 0) nextCounts[leverId] = nextCount;
      else delete nextCounts[leverId];
    });
    aiProvenanceLeverCountsRef.current = nextCounts;
    setForecastProvenanceByAccount(
      buildAiForecastProvenanceByAccount(Object.keys(nextCounts), persistedForecastProvenanceRef.current)
    );
  }, []);

  const updateYearLever = (def, yearIndex, value, source = 'FP&A input') => {
    const numericValue = toFiniteNumber(value);
    const prior = yearAdjustments[def.id][yearIndex];
    const nextYearAdjustments = cloneYearAdjustments(yearAdjustments);
    nextYearAdjustments[def.id] = nextYearAdjustments[def.id].map((v, i) => (i === yearIndex ? numericValue : v));
    const lineage = {
      key: `yearadj-${def.id}-${yearIndex}`,
      field: `${def.label} FY${ADJUSTMENT_YEARS[yearIndex]} (${def.unit})`,
      priorValue: prior,
      newValue: numericValue,
      source
    };

    if (source !== 'FP&A input') {
      setYearAdjustments((previous) => ({
        ...previous,
        [def.id]: previous[def.id].map((v, i) => (i === yearIndex ? numericValue : v))
      }));
      recordLineage(lineage.key, lineage.field, lineage.priorValue, lineage.newValue, source);
      if (isAcceptedAiSource(source)) updateAiProvenanceForLevers(def.id);
      return;
    }

    stageManualLeverPreview({
      kind: 'yearAdjustments',
      title: `${def.label} FY${ADJUSTMENT_YEARS[yearIndex]}`,
      detail: `${prior} to ${numericValue} ${def.unit}`,
      primaryFields: [FORECAST_LEVER_PRIMARY_FIELDS[def.id]].filter(Boolean),
      priorState: { yearAdjustments: cloneYearAdjustments(yearAdjustments) },
      nextState: { yearAdjustments: nextYearAdjustments },
      overrides: { yearAdjustments: nextYearAdjustments },
      lineage
    });
  };

  const updateQuarterlyGrowth = (leverId, period, config, label) => {
    const prior = quarterlyGrowth[leverId]?.[period] || null;
    const nextQuarterlyGrowth = clonePlainValue(quarterlyGrowth);
    nextQuarterlyGrowth[leverId] = {
      ...(nextQuarterlyGrowth[leverId] || {}),
      [period]: config
    };
    const lineage = {
      key: `quarterly-${leverId}-${period}`,
      field: label,
      priorValue: describeQuarterlyGrowthConfig(prior),
      newValue: describeQuarterlyGrowthConfig(config)
    };

    if (/AI Prompt Box/i.test(label || '')) {
      setQuarterlyGrowth((previous) => ({
        ...previous,
        [leverId]: {
          ...(previous[leverId] || {}),
          [period]: config
        }
      }));
      recordLineage(lineage.key, lineage.field, lineage.priorValue, lineage.newValue);
      return;
    }

    stageManualLeverPreview({
      kind: 'quarterlyGrowth',
      title: label,
      detail: `${lineage.priorValue} to ${lineage.newValue}`,
      primaryFields: [FORECAST_LEVER_PRIMARY_FIELDS[leverId]].filter(Boolean),
      priorState: { quarterlyGrowth: clonePlainValue(quarterlyGrowth) },
      nextState: { quarterlyGrowth: nextQuarterlyGrowth },
      overrides: { quarterlyGrowth: nextQuarterlyGrowth },
      lineage
    });
  };

  // Parent-level lever entry: one annual amount applied to every year.
  const setYearLeverAll = (def, value) => {
    const numericValue = toFiniteNumber(value);
    const prior = yearAdjustments[def.id];
    const nextYearAdjustments = cloneYearAdjustments(yearAdjustments);
    nextYearAdjustments[def.id] = nextYearAdjustments[def.id].map(() => numericValue);
    stageManualLeverPreview({
      kind: 'yearAdjustmentsAll',
      title: `${def.label} all years`,
      detail: `${prior.join('/')} to ${numericValue} ${def.unit}`,
      primaryFields: [FORECAST_LEVER_PRIMARY_FIELDS[def.id]].filter(Boolean),
      priorState: { yearAdjustments: cloneYearAdjustments(yearAdjustments) },
      nextState: { yearAdjustments: nextYearAdjustments },
      overrides: { yearAdjustments: nextYearAdjustments },
      lineage: {
        key: `yearadj-${def.id}-all`,
        field: `${def.label} all years (${def.unit})`,
        priorValue: prior.join('/'),
        newValue: numericValue
      }
    });
  };

  const updateFeeDriver = (def, nextState, what) => {
    const nextFeeDrivers = feeDrivers.map((d) => (d.id === def.id ? nextState : d));
    stageManualLeverPreview({
      kind: 'feeDriver',
      title: `${def.label} · ${what}`,
      detail: 'Driver-based fee settings',
      primaryFields: ['nonInterestIncome'],
      priorState: { feeDrivers: clonePlainValue(feeDrivers) },
      nextState: { feeDrivers: clonePlainValue(nextFeeDrivers) },
      overrides: { feeDrivers: nextFeeDrivers },
      lineage: {
        key: `feedrv-${def.id}-${what}`,
        field: `${def.label} · ${what}`,
        priorValue: '·',
        newValue: '·'
      }
    });
  };

  // ---- Item D: AI Prompt Box — shadow simulation + accept-before-persist apply ----
  const [lastAiChangeset, setLastAiChangeset] = useState(null);

  // Current annual context ($MM) handed to the agent purely for magnitude grounding.
  const promptCurrentAnnual = useMemo(() => {
    const series = (adjustedAnnualSeries || []).filter((row) =>
      ADJUSTMENT_YEARS.includes(row.fiscalYearNumber));
    const byYear = (pick) => Object.fromEntries(
      series.map((row) => [row.fiscalYearNumber, Number((pick(row) || 0).toFixed(1))])
    );
    return {
      Loans: byYear((row) => row.endingLoans),
      'Transaction Deposits': byYear((row) => row.endingDeposits),
      'Other Non-Interest Income': byYear((row) => row.nonInterestIncome),
      'Other Operating Expense': byYear((row) => row.nonInterestExpense),
      'Provision for Credit Losses': byYear((row) => row.creditProvision)
    };
  }, [adjustedAnnualSeries]);

  // Run the SAME single model object with a proposed lever override and diff vs
  // the live adjusted series. Deterministic; the AI never produces these dollars.
  // Balance lines are point-in-time (EOP = last month); I/S lines accumulate (sum).
  const simulatePromptChangeset = useCallback((leverMap) => {
    const proposed = { ...yearAdjustments };
    Object.entries(leverMap || {}).forEach(([leverId, values]) => {
      if (Array.isArray(values)) proposed[leverId] = values.map((v) => Number(v) || 0);
    });
    const proposedMonthly = buildAdjustedMonthlySeriesForState({ yearAdjustments: proposed });
    return buildForecastImpactLines(adjustedMonthlySeries, proposedMonthly);
  }, [adjustedMonthlySeries, buildAdjustedMonthlySeriesForState, yearAdjustments]);

  // Real per-point $MM sensitivity (by FY) for the balance-growth levers, computed
  // from the SAME model (+1 pt vs current) — the magnitude key the agent uses to
  // anchor a dollar target to lever points. Recomputed when the model moves.
  const promptLeverSensitivity = useMemo(() => {
    const out = {};
    [['loanGrowthPts', 'endingLoans'], ['depositGrowthPts', 'endingDeposits']].forEach(
      ([leverId, field]) => {
        const base = yearAdjustments[leverId] || ADJUSTMENT_YEARS.map(() => 0);
        const lines = simulatePromptChangeset({ [leverId]: base.map((value) => value + 1) });
        const line = lines.find((entry) => entry.field === field);
        if (!line) return;
        out[leverId] = Object.fromEntries(ADJUSTMENT_YEARS.map((year) => {
          const cell = line.byYear?.[year] || {};
          return [year, Number((((Number(cell.new) || 0) - (Number(cell.old) || 0))).toFixed(3))];
        }));
      }
    );
    return out;
  }, [simulatePromptChangeset, yearAdjustments]);

  // Deterministic magnitude refine: scale a proposed pts vector (via the real model)
  // until the simulated balance delta at the reference year hits the dollar target.
  const refinePromptLever = useCallback((leverId, vector, targetDeltaMM, refYear) => {
    const field = leverId === 'loanGrowthPts' ? 'endingLoans'
      : leverId === 'depositGrowthPts' ? 'endingDeposits' : null;
    if (!field || !Number.isFinite(targetDeltaMM) || !refYear) return vector;
    const def = YEAR_LEVER_DEFS.find((d) => d.id === leverId) || { min: -5, max: 5 };
    const clampVec = (vec) => vec.map((x) => Math.max(def.min, Math.min(def.max, x)));
    const deltaAt = (vec) => {
      const line = simulatePromptChangeset({ [leverId]: vec }).find((entry) => entry.field === field);
      const cell = line?.byYear?.[refYear] || {};
      return (Number(cell.new) || 0) - (Number(cell.old) || 0);
    };
    let vec = clampVec(vector.slice());
    const tol = Math.max(0.5, 0.01 * Math.abs(targetDeltaMM));
    for (let i = 0; i < 5; i += 1) {
      const delta = deltaAt(vec);
      if (Math.abs(delta - targetDeltaMM) <= tol || Math.abs(delta) < 1e-6) break;
      const scaled = clampVec(vec.map((x) => x * (targetDeltaMM / delta)));
      if (scaled.every((x, idx) => x === vec[idx])) break; // clamped — can't get closer
      vec = scaled;
    }
    return vec.map((x) => Number(x.toFixed(3)));
  }, [simulatePromptChangeset]);

  // Item G: compute net income by FY under a full proposed lever vector via the
  // SAME single model object (no parallel calc path) — used to stamp annualAfter
  // on an FP&A adjustment so the review can show its earnings impact.
  const annualNetIncomeUnder = (leverVector) => {
    const lines = simulatePromptChangeset(leverVector) || [];
    const ni = lines.find((line) => line.field === 'netIncome');
    return Object.fromEntries(
      ADJUSTMENT_YEARS.map((year) => [year, Number(ni?.byYear?.[year]?.new) || 0])
    );
  };

  // Accept: apply each lever change through the existing setters (records lineage
  // + triggers autosave for liaisons; in FP&A-adjust mode autosave stays off and
  // the change persists to the FP&A layer instead), then write the attribution.
  const applyPromptChangeset = async (changeset) => {
    const source = fpaAdjustMode ? 'FP&A Quick Fix' : 'AI Prompt Box';
    const leversAfter = { ...yearAdjustments };
    (changeset.leverChanges || []).forEach((change) => {
      const def = YEAR_LEVER_DEFS.find((d) => d.id === change.leverId);
      if (!def) return;
      const vector = ADJUSTMENT_YEARS.map((year) =>
        Math.max(def.min, Math.min(def.max, Number(change.newByYear?.[String(year)]) || 0)));
      leversAfter[change.leverId] = vector;
      vector.forEach((value, index) => updateYearLever(def, index, value, source));
    });
    // Item E: apply the chosen intra-year shape to balance-growth levers through
    // the existing quarterly-growth presets (annual target preserved). Snapshot
    // the prior configs so undo reverts the shape exactly. Non-default presets only.
    const preset = PROFILE_TO_QUARTER_PRESET[changeset.seasonality?.profile];
    const priorQuarterly = {};
    if (preset && preset !== 'even') {
      (changeset.leverChanges || []).forEach((change) => {
        if (!['loanGrowthPts', 'depositGrowthPts'].includes(change.leverId)) return;
        priorQuarterly[change.leverId] = {};
        ADJUSTMENT_YEARS.forEach((year) => {
          priorQuarterly[change.leverId][String(year)] = quarterlyGrowth[change.leverId]?.[String(year)] || null;
          updateQuarterlyGrowth(
            change.leverId, String(year),
            { preset, preserveAnnual: true, overrides: [null, null, null, null] },
            `${change.label} FY${year} intra-year shape (AI Prompt Box)`
          );
        });
      });
    }
    setLastAiChangeset({ ...changeset, priorQuarterly });
    if (submissionId) {
      try {
        const basePayload = {
          source,
          prompt: changeset.prompt,
          interpretation: changeset.interpretation,
          summary: changeset.summary,
          leverChanges: changeset.leverChanges,
          secondOrder: changeset.secondOrder,
          citedContextNotes: changeset.citedContextNotes,
          planningEntity: selectedMarket.planningEntity,
          cycleCode: submission?.cycle_code || 'FY2027_BUDGET',
          scenarioVersion: modelRef.current?.scenarioVersion
        };
        // Item G: in FP&A-adjust mode stamp the overlay (full lever vector after the
        // change + earnings impact) under scenario 'FP&A Adjusted' so reload + the
        // review ledger can reconstruct and revert it, market submission untouched.
        const payload = fpaAdjustMode
          ? {
              ...basePayload,
              scenario: FPA_SCENARIO,
              kind: 'ai',
              actingAs: activeProfile.name,
              leversAfter,
              quarterlyAfter: quarterlyGrowth,
              annualAfter: annualNetIncomeUnder(leversAfter)
            }
          : basePayload;
        await apiService.recordForecastAdjustment(submissionId, {
          actor: activeActor,
          comment: (changeset.summary || changeset.interpretation || `${source} change`).slice(0, 200),
          payload
        });
      } catch (error) {
        // Attribution is best-effort; levers are already applied in-session.
      }
    }
  };

  // Undo: restore each lever's prior value exactly + a reversing attribution event.
  const undoPromptChangeset = async () => {
    if (!lastAiChangeset) return;
    (lastAiChangeset.leverChanges || []).forEach((change) => {
      const def = YEAR_LEVER_DEFS.find((d) => d.id === change.leverId);
      if (!def) return;
      ADJUSTMENT_YEARS.forEach((year, index) => {
        const value = Number(change.oldByYear?.[String(year)]) || 0;
        updateYearLever(def, index, value, 'AI Prompt Box · undo');
      });
    });
    // Item E: restore any intra-year quarterly shape this change set (exact revert).
    Object.entries(lastAiChangeset.priorQuarterly || {}).forEach(([leverId, byYear]) => {
      Object.entries(byYear).forEach(([year, config]) => {
        updateQuarterlyGrowth(
          leverId, year,
          config || { preset: 'even', preserveAnnual: true, overrides: [null, null, null, null] },
          `Reverted intra-year shape FY${year} (AI Prompt Box · undo)`
        );
      });
    });
    updateAiProvenanceForLevers(
      (lastAiChangeset.leverChanges || []).flatMap((change) =>
        ADJUSTMENT_YEARS.map(() => change.leverId)
      ),
      -1
    );
    if (submissionId) {
      try {
        await apiService.recordForecastAdjustment(submissionId, {
          actor: activeActor,
          comment: 'Reverted the AI Prompt Box change',
          payload: { source: 'AI Prompt Box · undo', revertOf: lastAiChangeset.leverChanges }
        });
      } catch (error) { /* best-effort */ }
    }
    setLastAiChangeset(null);
  };

  // ---- Item G: explicit FP&A Quick Fix (manual lever override) ----------------
  // The OP_03Y lever steppers ARE the explicit cell/line override; in FP&A-adjust
  // mode they edit the live model but autosave is off. "Save FP&A adjustment"
  // captures the net delta vs the market submission (second-order from the SAME
  // model) and persists it as one 'adjust' event under scenario 'FP&A Adjusted'.
  const fpaNetEffect = useMemo(() => {
    if (!fpaAdjustMode || !marketLeverBaselineRef.current) return null;
    const market = marketLeverBaselineRef.current.yearAdjustments;
    // simulate(market): old = current overlay, new = market → overlay−market = old−new.
    const lines = simulatePromptChangeset(market) || [];
    const changed = YEAR_LEVER_DEFS.some((def) =>
      ADJUSTMENT_YEARS.some((_, i) => (yearAdjustments[def.id]?.[i] || 0) !== (market[def.id]?.[i] || 0)));
    const effect = lines
      .map((line) => {
        const delta = ADJUSTMENT_YEARS.reduce((sum, year) => {
          const cell = line.byYear?.[year] || {};
          return sum + ((Number(cell.old) || 0) - (Number(cell.new) || 0));
        }, 0);
        const lastYear = ADJUSTMENT_YEARS[ADJUSTMENT_YEARS.length - 1];
        const lastCell = line.byYear?.[lastYear] || {};
        const eopDelta = (Number(lastCell.old) || 0) - (Number(lastCell.new) || 0);
        return { field: line.field, label: line.label, measure: line.measure,
          totalDeltaMM: line.measure === 'EOP' ? eopDelta : delta };
      })
      .filter((line) => Math.abs(line.totalDeltaMM) >= 0.05);
    return { changed, effect };
  }, [fpaAdjustMode, yearAdjustments, simulatePromptChangeset]);

  const saveFpaAdjustment = async () => {
    if (!submissionId || !fpaAdjustMode || !marketLeverBaselineRef.current) return;
    const market = marketLeverBaselineRef.current.yearAdjustments;
    const leverChanges = YEAR_LEVER_DEFS
      .filter((def) => ADJUSTMENT_YEARS.some((_, i) => (yearAdjustments[def.id]?.[i] || 0) !== (market[def.id]?.[i] || 0)))
      .map((def) => ({
        leverId: def.id, label: def.label, unit: def.unit,
        oldByYear: Object.fromEntries(ADJUSTMENT_YEARS.map((y, i) => [String(y), market[def.id]?.[i] || 0])),
        newByYear: Object.fromEntries(ADJUSTMENT_YEARS.map((y, i) => [String(y), yearAdjustments[def.id]?.[i] || 0]))
      }));
    if (leverChanges.length === 0) { setFpaSaveState('idle'); return; }
    setFpaSaveState('saving');
    setWorkflowError(null);
    try {
      await apiService.recordForecastAdjustment(submissionId, {
        actor: activeActor,
        comment: `FP&A explicit adjustment — ${leverChanges.map((c) => c.label).join(', ')}`.slice(0, 200),
        payload: {
          source: 'FP&A Quick Fix', scenario: FPA_SCENARIO, kind: 'explicit',
          actingAs: activeProfile.name,
          summary: `Manager override of ${leverChanges.length} lever(s): ${leverChanges.map((c) => c.label).join(', ')}`,
          leverChanges,
          secondOrder: (fpaNetEffect?.effect || []).map((line) => ({
            field: line.field, label: line.label, totalDeltaMM: Number(line.totalDeltaMM.toFixed(2))
          })),
          leversAfter: yearAdjustments,
          quarterlyAfter: quarterlyGrowth,
          annualAfter: annualNetIncomeUnder(yearAdjustments),
          planningEntity: submission?.planning_entity,
          cycleCode: submission?.cycle_code || 'FY2027_BUDGET',
          scenarioVersion: modelRef.current?.scenarioVersion
        }
      });
      setFpaSaveState('saved');
      setTimeout(() => setFpaSaveState('idle'), 2500);
    } catch (error) {
      setFpaSaveState('error');
      // B5: make a server permission denial (403) visible in the workspace notice
      // rather than swallowing it behind the generic "Save failed" chip.
      const data = error?.response?.data;
      if (error?.response?.status === 403) {
        setWorkflowError(
          `${data?.error || 'Quick Fix denied'}${data?.requiredPermission ? ` (requires ${data.requiredPermission})` : ''}`
        );
      } else {
        setWorkflowError(data?.error || error.message || 'FP&A adjustment save failed');
      }
    }
  };

  const discardFpaAdjustment = () => {
    const market = marketLeverBaselineRef.current;
    if (!market) return;
    setYearAdjustments(normalizeLeverVector(market.yearAdjustments));
    setQuarterlyGrowth(normalizeQuarterlyGrowth(market.quarterlyGrowth));
    setFpaSaveState('idle');
  };

  const generateAiSummary = async () => {
    if (!submissionId || aiSummaryBusy) return;
    setAiSummaryBusy(true);
    try {
      await saveDraftNow();
      const summary = await apiService.generateForecastChangeSummary(submissionId);
      setAiSummary(summary);
    } catch (error) {
      setAiSummary({ headline: 'Summary generation failed', drivers: [], risk: error?.response?.data?.error || error.message, generator: 'error' });
    } finally {
      setAiSummaryBusy(false);
    }
  };

  const exportForecastWorkbook = async () => {
    if (!submissionId || excelBusy) return;
    setExcelBusy('export');
    setExcelNotice(null);
    setWorkflowError(null);
    try {
      if (canEditSubmission) {
        await saveDraftNow();
      }
      const response = await apiService.exportForecastSubmissionWorkbook(submissionId, activeActor);
      downloadWorkbookBlob(response, `Forecast_${selectedMarket.planningEntity}.xlsx`);
      setExcelNotice({ tone: 'green', message: 'Excel workbook exported from the current saved draft.' });
    } catch (error) {
      setExcelNotice({ tone: 'red', message: error?.response?.data?.error || error.message || 'Excel export failed.' });
    } finally {
      setExcelBusy(null);
    }
  };

  const previewForecastWorkbookImport = async (file) => {
    if (!file || !submissionId || excelBusy) return;
    setExcelBusy('preview');
    setExcelNotice(null);
    setWorkflowError(null);
    try {
      await saveDraftNow();
      const preview = await apiService.previewForecastSubmissionWorkbookImport(submissionId, file, activeActor);
      setExcelImportPreview(preview);
      setExcelNotice({
        tone: preview.validation?.status === 'blocked' ? 'red' : 'green',
        message: preview.validation?.status === 'blocked'
          ? 'Workbook parsed with blocking validation issues.'
          : 'Workbook parsed. Review the preview before applying.'
      });
    } catch (error) {
      setExcelNotice({ tone: 'red', message: error?.response?.data?.error || error.message || 'Excel import failed.' });
    } finally {
      if (excelImportRef.current) excelImportRef.current.value = '';
      setExcelBusy(null);
    }
  };

  const applyForecastWorkbookImport = () => {
    if (!excelImportPreview || excelBusy || excelImportPreview.validation?.status === 'blocked') return;
    setExcelBusy('apply');
    hydrateSubmissionInputs(excelImportPreview.parsedInputs || {});
    setAiSummary(excelImportPreview.aiSummary || aiSummary);
    setPendingAppliedImport(excelImportPreview);
    setExcelImportPreview(null);
  };

  useEffect(() => {
    if (!pendingAppliedImport || !submissionId || !hydrated) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        await saveDraftNow();
        await apiService.markForecastSubmissionWorkbookImportApplied(
          submissionId,
          pendingAppliedImport.importId,
          activeActor
        );
        if (!cancelled) {
          setSaveState('saved');
          setExcelNotice({ tone: 'green', message: 'Excel import applied and saved to the forecast draft.' });
          setPendingAppliedImport(null);
        }
      } catch (error) {
        if (!cancelled) {
          setSaveState('error');
          setExcelNotice({ tone: 'red', message: error?.response?.data?.error || error.message || 'Apply failed.' });
          setPendingAppliedImport(null);
        }
      } finally {
        if (!cancelled) setExcelBusy(null);
      }
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pendingAppliedImport, submissionId, hydrated, activeActor, saveDraftNow]);

  const narrativeTriggers = useMemo(() => {
    const triggers = [];
    const fy1 = annualResults[0];
    if (fy1 && Math.abs(fy1.variancePct) > 3) {
      const fyLabel = String(fy1.year).startsWith('FY') ? fy1.year : `FY${fy1.year}`;
      triggers.push({
        id: 'fy1-net-income',
        label: `${fyLabel} net income ${fy1.variancePct > 0 ? '+' : ''}${fy1.variancePct.toFixed(1)}% vs baseline — explain the primary driver`
      });
    }
    // M4: per-line driver-view deviations beyond threshold require a
    // line-specific narrative (keyed 'driver-view-<metric>-<year>'). The server
    // B7 gate re-checks these against the anchor on submit.
    buildDriverViewNarrativeTriggers(driverViewDeltas).forEach((trigger) => {
      triggers.push(trigger);
    });
    return triggers;
  }, [annualResults, driverViewDeltas]);

  const commentaryRequired = totalOverrideCount > 0 || hireRows.length > 0 || narrativeTriggers.length > 0;
  const requiredNarrativeTriggers = useMemo(
    () => commentaryRequired
      ? [{ id: 'submission-summary', label: 'Summarize the actions taken and the expected business impact' }, ...narrativeTriggers]
      : narrativeTriggers,
    [commentaryRequired, narrativeTriggers]
  );

  const missingNarratives = requiredNarrativeTriggers.filter(
    (trigger) => !(narratives[trigger.id] || '').trim()
  );
  const quarterlyGrowthValidationIssues = ['loanGrowthPts', 'depositGrowthPts'].flatMap((leverId) => {
    const profile = summarizeQuarterlyGrowthProfile({
      configs: quarterlyGrowth[leverId],
      annualTargets: yearAdjustments[leverId],
      remainderYear,
      latestActualMonth
    });
    const leverLabel = leverId === 'loanGrowthPts' ? 'Loan growth' : 'Deposit growth';
    return Object.entries(profile.details)
      .filter(([, detail]) => detail.needsAttention)
      .map(([period, detail]) => ({
        id: `quarterly-growth-${leverId}-${period}`,
        label: `${leverLabel} quarterly profile does not preserve its annual target.`,
        detail: `${period === 'roy' ? `RoY ${remainderYear}` : `FY${period}`} effective ${Number.isFinite(detail.effectiveRate) ? detail.effectiveRate.toFixed(2) : '—'} pts vs ${detail.target.toFixed(2)} pts target. Use Profile on an override or turn off preservation.`,
        severity: 'error', stage: 'levers', targetId: 'regional-stage-levers'
      }));
  });
  // Baseline review is a soft, non-blocking acknowledge (the stage UI + feedback
  // box remain); the planning-context gate has been removed entirely. Neither
  // blocks submission anymore.
  const workspaceBlockingIssues = [
    ...quarterlyGrowthValidationIssues
  ];
  // B7: client-side preview of the submission-quality gates. The server re-runs
  // these on submit and is authoritative; this is inline UX only.
  const overridesPayload = Object.entries(qualityOverrides)
    .filter(([, value]) => String(value?.owner || '').trim() && String(value?.note || '').trim())
    .map(([gateCode, value]) => ({ gateCode, owner: value.owner.trim(), note: value.note.trim() }));
  const qualityNarrativePayload = requiredNarrativeTriggers.map((trigger) => ({
    trigger: trigger.id,
    text: narratives[trigger.id] || ''
  }));
  const qualityGateResult = evaluateQualityGates({
    narratives: qualityNarrativePayload,
    workspaceState: {
      baselineDecision: workspaceState.baselineDecision,
      baselineIssue: workspaceState.baselineIssue,
      contextWarnings: workspaceState.contextWarnings || []
    },
    overrides: overridesPayload,
    requiredNarratives: requiredNarrativeTriggers.map((trigger) => trigger.id)
  });
  const qualityGates = qualityGateResult.gates;
  const blockingQualityGates = qualityGates.filter((gate) => gate.blocking);
  const qualityGateIssues = qualityGates
    .filter((gate) => gate.blocking)
    .map((gate) => ({
      id: `gate-${gate.code}`,
      label: gate.message,
      detail: gate.code === 'baseline_flagged'
        ? 'Requires an FP&A override — or return to Baseline and confirm it to withdraw the flag.'
        : gate.severity === 'warning'
          ? 'Requires an FP&A override owner and note before submission.'
          : 'Resolve before submission.',
      severity: 'error',
      // Send the liaison to the place they can actually act: the baseline
      // decision panel for a flagged baseline, the review panel otherwise.
      stage: gate.code === 'baseline_flagged' ? 'baseline' : 'review',
      targetId: gate.code === 'baseline_flagged' ? 'baseline-review-decision' : 'forecast-submit'
    }));

  const reviewValidationIssues = [
    ...workspaceBlockingIssues,
    ...qualityGateIssues,
    ...stagedRows
      .filter((row) => row.status === 'Blocked')
      .map((row) => ({
        id: `blocked-${row.gl}`,
        label: `${row.line} is blocked`,
        detail: 'Review the affected assumption and resulting validation before submission.',
        severity: 'error',
        stage: 'results',
        targetId: 'regional-stage-results'
      })),
    ...missingNarratives.map((trigger) => ({
      id: `narrative-${trigger.id}`,
      label: `Commentary required: ${trigger.label}`,
      detail: 'Explain the decision, evidence, and expected financial effect.',
      severity: 'warning',
      stage: 'review',
      targetId: `forecast-narrative-${trigger.id}`
    }))
  ];

  const navigateToValidationIssue = (issue) => {
    setActiveWorkspaceStage(issue.stage);
    window.setTimeout(() => document.getElementById(issue.targetId)?.focus(), 0);
  };

  const returnedComment = useMemo(() => {
    if (submissionStatus !== 'returned') return null;
    const event = (submission?.events || []).find((entry) => entry.event === 'return');
    return event ? { comment: event.comment, actor: event.actor, at: event.created_at } : null;
  }, [submission, submissionStatus]);

  const submitToFpa = async () => {
    if (!submissionId || isTransitioning || hasBlockingRows || workspaceBlockingIssues.length > 0 || missingNarratives.length > 0 || blockingQualityGates.length > 0) return;
    setIsTransitioning(true);
    setWorkflowError(null);
    setGateBlockers([]);
    try {
      await saveDraftNow();
      try {
        const summary = await apiService.generateForecastChangeSummary(submissionId);
        setAiSummary(summary);
      } catch (summaryError) {
        /* summary is best-effort; submit proceeds without it */
      }
      const narrativePayload = requiredNarrativeTriggers.map((trigger) => ({
        trigger: trigger.id,
        label: trigger.label,
        text: narratives[trigger.id] || ''
      }));
      await apiService.transitionForecastSubmission(submissionId, {
        action: 'submit',
        actor: activeActor,
        payload: {
          narratives: narrativePayload,
          requiredNarratives: requiredNarrativeTriggers.map((trigger) => trigger.id),
          // B7: quality-gate context so the server evaluator re-check passes.
          workspaceState: {
            baselineDecision: workspaceState.baselineDecision,
            baselineIssue: workspaceState.baselineIssue,
            contextWarnings: workspaceState.contextWarnings || []
          },
          overrides: overridesPayload,
          // M4: hand the server the driver-view snapshot + anchor pin so the B7
          // gate recomputes deviations deterministically at submit time.
          modelSnapshot: {
            driverView: { byYear: driverViewPlanSnapshot },
            macroScenarioKey: driverViewAnchor?.scenarioKey || 'Base',
            macroScenarioVersion: driverViewAnchor?.scenarioVersion ?? null,
            driverViewAnchorId: driverViewAnchor?.id || null
          },
          validation: {
            blockedRows: stagedRows.filter((row) => row.status === 'Blocked').length,
            constraintRed: constraintSummary.red,
            constraintYellow: constraintSummary.yellow,
            validationCounts
          }
        }
      });
      setSubmissionStatus('submitted');
      setSubmission((previous) => (previous ? { ...previous, review_opened_at: null } : previous));
    } catch (error) {
      // B7: the server re-checks quality gates and returns 422 with gates[] when
      // they fail — surface those inline as blockers instead of a generic error.
      if (error?.response?.status === 422 && Array.isArray(error.response.data?.gates)) {
        setGateBlockers(error.response.data.gates);
        setWorkflowError(error.response.data.error || 'Submission blocked by quality gates.');
      } else {
        setWorkflowError(error?.response?.data?.error || error.message || 'Submit failed');
      }
    } finally {
      setIsTransitioning(false);
    }
  };

  const recallSubmission = async () => {
    if (!submissionId || isTransitioning) return;
    setIsTransitioning(true);
    setWorkflowError(null);
    try {
      await apiService.transitionForecastSubmission(submissionId, {
        action: 'recall',
        actor: activeActor
      });
      setSubmissionStatus('in_progress');
    } catch (error) {
      setWorkflowError(error?.response?.data?.error || error.message || 'Recall failed');
    } finally {
      setIsTransitioning(false);
    }
  };

  // Annual FTP decomposition of the adjusted plan (and baseline for variance).
  const ftpAnnual = useMemo(() => {
    const ftpRate = ftpRateFor(activeMacro.sofr);
    const byYear = FORECAST_YEARS.map((year) => {
      const months = adjustedMonthlySeries.filter((row) => row.fiscalYearNumber === year);
      const baseMonths = baselineMonthlySeries.filter((row) => row.fiscalYearNumber === year);
      const sum = (rows) =>
        rows.reduce(
          (acc, row) => {
            const split = splitNiiFtp(row, ftpRate, 12);
            return {
              lendingMargin: acc.lendingMargin + split.lendingMargin,
              depositMargin: acc.depositMargin + split.depositMargin,
              mismatch: acc.mismatch + split.mismatch,
              nii: acc.nii + row.nii,
              netIncome: acc.netIncome + row.netIncome
            };
          },
          { lendingMargin: 0, depositMargin: 0, mismatch: 0, nii: 0, netIncome: 0 }
        );
      return { year: `FY${year}`, plan: sum(months), baseline: sum(baseMonths) };
    });
    return { ftpRate, byYear };
  }, [adjustedMonthlySeries, baselineMonthlySeries, activeMacro.sofr]);

  const centralSettingsChanged = Boolean(
    workspaceState.centralSettingsUpdatedAt &&
    centralSettings?.updatedAt &&
    workspaceState.centralSettingsUpdatedAt !== centralSettings.updatedAt
  );
  // Planning-context gate removed: treat context as always satisfied so it never
  // blocks submission or drags workflow readiness/impact scoring.
  const contextAcknowledged = true;
  const regionalStages = useMemo(() => [
    {
      id: 'baseline', label: 'Baseline',
      systemAction: 'Reconciles actuals, source freshness, and the inherited forecast path.',
      userDecision: 'Confirm the baseline is fit to plan from, or flag a specific data issue.',
      exitCriteria: 'Baseline confirmed or a data issue recorded for resolution.',
      status: workspaceState.baselineDecision === 'flagged' ? WORKSPACE_READINESS.NEEDS_ATTENTION
        : workspaceState.baselineDecision === 'confirmed' ? WORKSPACE_READINESS.READY
          : WORKSPACE_READINESS.IN_PROGRESS,
      issueCount: workspaceState.baselineDecision === 'flagged' ? 1 : 0,
      issueSummary: workspaceState.baselineDecision ? null
        : 'Confirm the baseline or flag a data issue — the decision buttons are at the bottom of the stage.'
    },
    {
      id: 'context', label: 'Planning Context',
      systemAction: 'Combines the macro case with centrally controlled FP&A assumptions.',
      userDecision: 'Acknowledge the planning backdrop and challenge only authorized settings.',
      exitCriteria: 'Planning backdrop reviewed; no acknowledgement required.',
      status: WORKSPACE_READINESS.READY,
      issueCount: 0
    },
    {
      id: 'levers', label: 'Business Levers',
      systemAction: 'Prioritizes off-trend signals and calculates the effect of each proposed lever.',
      userDecision: 'Accept, edit, or decline assumptions using local business evidence.',
      exitCriteria: 'Material signals reviewed and intended overrides captured.',
      status: leverAttentionCount > 0 ? WORKSPACE_READINESS.NEEDS_ATTENTION
        : totalOverrideCount > 0 ? WORKSPACE_READINESS.IN_PROGRESS : WORKSPACE_READINESS.NOT_STARTED,
      issueCount: leverAttentionCount,
      issueSummary: leverAttentionCount > 0
        ? `${leverAttentionCount} off-trend signal(s) to review in the radar — reviewing clears this; signals never block submission.`
        : null
    },
    {
      id: 'workforce', label: 'Workforce',
      systemAction: 'Carries forward roster economics and models staffing changes over time.',
      userDecision: 'Validate existing-workforce assumptions and specify justified new hires.',
      exitCriteria: 'Workforce assumptions and hiring plan reflect the operating plan.',
      // Visiting the stage marks it reviewed (workforceReviewedAt) — same
      // no-acknowledge philosophy as Planning Context; edits alone show progress.
      status: workspaceState.workforceReviewedAt ? WORKSPACE_READINESS.READY
        : hireRows.length > 0 || Object.values(workforceInputs).some((lever) => lever.value !== lever.baseline)
          ? WORKSPACE_READINESS.IN_PROGRESS : WORKSPACE_READINESS.NOT_STARTED,
      issueCount: 0
    },
    {
      id: 'results', label: 'Impact & Results',
      systemAction: 'Recalculates the pro forma and explains baseline-to-plan financial movement.',
      userDecision: 'Assess whether the combined plan and its baseline variance are credible.',
      exitCriteria: 'Material financial impacts and their primary drivers are understood.',
      status: annualResults.length > 0 ? WORKSPACE_READINESS.READY : WORKSPACE_READINESS.IN_PROGRESS,
      issueCount: 0
    },
    {
      id: 'review', label: isLiaison ? 'Review & Submit' : isAggregate ? 'Review & Allocate' : 'Review & Load',
      systemAction: 'Validates the package, assembles lineage, and drafts decision commentary.',
      userDecision: 'Resolve blockers, edit the narrative, and authorize the next workflow state.',
      exitCriteria: 'Validation passes and the package is submitted, allocated, or loaded.',
      status: hasBlockingRows || workspaceBlockingIssues.length > 0 || missingNarratives.length > 0 || blockingQualityGates.length > 0
        ? WORKSPACE_READINESS.BLOCKED : WORKSPACE_READINESS.READY,
      issueCount: stagedRows.filter((row) => row.status === 'Blocked').length + workspaceBlockingIssues.length + missingNarratives.length + blockingQualityGates.length,
      issueSummary: [
        missingNarratives.length > 0 ? `${missingNarratives.length} required narrative(s) to write` : null,
        blockingQualityGates.map((gate) => gate.message).join('; ') || null,
        workspaceBlockingIssues.length > 0 ? `${workspaceBlockingIssues.length} validation issue(s)` : null,
        hasBlockingRows ? 'blocked staged rows' : null
      ].filter(Boolean).join(' · ') || null
    }
  ], [
    annualResults.length, blockingQualityGates.length,
    hasBlockingRows, hireRows.length, isAggregate,
    isLiaison, leverAttentionCount, missingNarratives.length, stagedRows, totalOverrideCount,
    workspaceBlockingIssues.length,
    workforceInputs, workspaceState.baselineDecision, workspaceState.workforceReviewedAt
  ]);
  const [activeWorkspaceStage, setActiveWorkspaceStage] = useWorkspaceStage(regionalStages, 'baseline');

  useEffect(() => {
    if (!hydrated) return;
    setWorkspaceState((previous) => {
      const visitedStages = Array.isArray(previous.visitedStages) ? previous.visitedStages : [];
      if (visitedStages.includes(activeWorkspaceStage)) return previous;
      const next = { ...previous, visitedStages: [...visitedStages, activeWorkspaceStage] };
      // Opening the Workforce stage counts as reviewing it — wires the
      // previously-unset workforceReviewedAt marker so the stage can reach READY
      // without a dedicated acknowledge button. Persists via the inputs autosave.
      if (activeWorkspaceStage === 'workforce' && !previous.workforceReviewedAt) {
        next.workforceReviewedAt = new Date().toISOString();
      }
      return next;
    });
  }, [activeWorkspaceStage, hydrated]);

  const inputProgress = useMemo(() => {
    const workforceChanged = hireRows.length > 0 || Object.values(workforceInputs)
      .some((lever) => lever.value !== lever.baseline);
    return calculateInputProgress({
      submissionStatus,
      baselineDecision: workspaceState.baselineDecision,
      contextAcknowledged,
      leverAttentionCount,
      totalOverrideCount,
      workforceChanged,
      workforceReviewed: Boolean(workspaceState.workforceReviewedAt),
      resultsValid: annualResults.length > 0 && !hasBlockingRows,
      reviewNarrativesComplete: missingNarratives.length === 0,
      reviewIssueCount: reviewValidationIssues.length
    });
  }, [
    annualResults.length, contextAcknowledged, hasBlockingRows, hireRows.length, leverAttentionCount,
    missingNarratives.length, reviewValidationIssues.length, submissionStatus, totalOverrideCount,
    workforceInputs, workspaceState.baselineDecision, workspaceState.workforceReviewedAt
  ]);

  const liveImpactSummary = useMemo(() => {
    // "Next Qtr" starts with the first unmodeled month after actuals and rolls
    // into FY1 when the remainder-year bridge contains fewer than three months.
    const impactAdjustedMonthly = [...adjustedRemainderSeries, ...adjustedMonthlySeries];
    const impactBaselineMonthly = [...baselineRemainderSeries, ...baselineMonthlySeries];
    return calculateHydratedLiveImpactSummary({
      hydrated,
      baselineHydrated,
      annualResults,
      baselineAnnualSeries,
      adjustedMonthlySeries: impactAdjustedMonthly,
      baselineMonthlySeries: impactBaselineMonthly,
      basePortfolio
    });
  }, [
    adjustedMonthlySeries, adjustedRemainderSeries, annualResults, baselineAnnualSeries,
    baselineHydrated, baselineMonthlySeries, baselineRemainderSeries, basePortfolio, hydrated
  ]);

  useEffect(() => {
    const panel = document.getElementById(`regional-stage-${activeWorkspaceStage}`);
    const heading = panel?.querySelector('h2');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  }, [activeWorkspaceStage]);

  useUnsavedChangesWarning(['pending', 'saving', 'error'].includes(saveState));

  const approveLoad = () => {
    if (!hasBlockingRows) {
      setLoadState('approved');
      setLoadResult(null);
    }
  };

  const writeForecast = async () => {
    if (!loadApproved || hasBlockingRows || isWritingForecast) return;

    setIsWritingForecast(true);
    setLoadResult(null);
    try {
      const result = await apiService.loadRegionalForecast({
        scenarioVersion,
        submissionId,
        versionRaw: 'RFM',
        planningEntity: selectedMarket.planningEntity,
        market: selectedMarket.market,
        region: selectedMarket.region,
        dataSource: FORECAST_DATA_SOURCE,
        createdBy: activeActor,
        rows: forecastLoadRows,
        summaryRows: stagedRows,
        metadata: {
          modelVersion: MODEL_VERSION,
          scenario,
          macroMode,
          activeUser: activeActor,
          forecastYears: FORECAST_YEARS,
          validationCounts,
          marketInputs,
          workforceInputs,
          expenseInputs,
          shocks,
          baselineSourceStatus: baselineSource.status
        }
      });
      setLoadResult(result);
      setLoadState('loaded');
    } catch (error) {
      // B5: surface a load:write permission denial (403) with its message +
      // required permission through the existing load-result error notice.
      const data = error?.response?.data;
      const denied = error?.response?.status === 403;
      setLoadResult({
        status: 'error',
        message: denied
          ? `${data?.error || 'Load denied'}${data?.requiredPermission ? ` (requires ${data.requiredPermission})` : ''}`
          : (data?.error || error.message || 'Forecast load failed')
      });
      setLoadState('approved');
    } finally {
      setIsWritingForecast(false);
    }
  };



  let activeStageContent = null;
  switch (activeWorkspaceStage) {
    case 'baseline':
      activeStageContent = <RegionalBaselineStage vm={{ Bar, CartesianGrid, ComposedChart, Database, ForecastGrowthMatrix, ForecastReadinessBadge, ForecastTimeMatrix, History, Legend, Line, ResponsiveContainer, Tooltip, WORKSPACE_READINESS, XAxis, YAxis, activeWorkspaceStage, baseForecastRows, baselineAnnualSeries, baselineSource, canEditSubmission, chartAxisColor, chartGridColor, formatMillions, growthMatrixColumns, op01AnnualSeries, op01CurrentYearRow, op01MonthlySeries, setWorkspaceState, tooltipStyle, workspaceState }} />;
      break;
    case 'context':
      activeStageContent = <RegionalContextStage vm={{ AlertTriangle, CartesianGrid, ComposedChart, Database, DollarSign, FORECAST_YEARS, Legend, Line, MACRO_SETS, NumericInput, RefreshCw, ResponsiveContainer, ShieldCheck, SlidersHorizontal, Tooltip, WaterfallChart, XAxis, YAxis, activeMacro, activeWorkspaceStage, bridgeDomain, bridgeRows, centralPlanRows, centralSaveError, centralSaveState, centralSavedAt, centralSettings, centralSettingsChanged, chartAxisColor, chartGridColor, contextAcknowledged, formatMillions, formatPercent, isCentral, macroAnnualPath, macroDivergenceRows, macroMode, model, rateFeed, refreshRates, retryCentralSettingSave, setMacroMode, setWorkspaceState, tooltipStyle, updateCentralSetting, macroStaleness, macroChangeDiffRows, macroReanchorMode, macroStaleDialogOpen, setMacroStaleDialogOpen, runWorkspaceReanchor, reanchorBusy, reanchorError, reanchorResult }} />;
      break;
    case 'levers':
      activeStageContent = <RegionalLeversStage vm={{ Download, ForecastOp03Suite, Gauge, MACRO_SENSITIVITY_COEFFICIENTS, NumericInput, RefreshCw, SlidersHorizontal, SourceBadge, activeExpenseLeverCount, activeProfile, activeShockCount, activeWorkspaceStage, adjustedMonthlySeries, baselineMonthlySeries, canEditSubmission: canEditEffective, expenseInputs, feeDrivers, formatLeverValue, formatPercent, isAggregate, latestActualMonth, macroCase: macroMode, quarterlyGrowth, remainderYear, resetExpenseInputs, resetMacroLevers, selectedMarket, setYearLeverAll, shocks, submission, submissionId, updateExpenseInput, updateFeeDriver, updateLeverAttentionCount, updateQuarterlyGrowth, updateShock, updateYearLever, yearAdjustments }} />;
      break;
    case 'workforce':
      activeStageContent = <RegionalWorkforceStage vm={{ ADJUSTMENT_YEARS, Briefcase, ForecastHireRows, ForecastHrRoster, MARKETS, NumericInput, OFFICER_TYPES, RefreshCw, User, WORKFORCE_YEAR_DEFS, activeWorkspaceStage, canEditSubmission, formatLeverValue, formatMillions, formatPercent, hireRows, hrPlan, hrRoster, hrTransfers, lineage, model, outgoingTransferCount, resetWorkforceInputs, setWorkforceTab, submissionId, updateHireRows, updateHrPlan, updateMarketInput, updateWorkforceInput, updateWorkforceYear, workforceInputs, workforceTab, workforceYearInputs }} />;
      break;
    case 'results':
      activeStageContent = <RegionalResultsStage vm={{ Activity, Area, AreaChart, Bar, Calculator, CartesianGrid, ComposedChart, ForecastNiiBuildup, Gauge, Legend, Line, OFFICER_TYPES, ResponsiveContainer, SourceBadge, Tooltip, WaterfallChart, XAxis, YAxis, activeMacro, activeWorkspaceStage, adjustedAnnualSeries, adjustedMonthlySeries, annualResults, annualVarianceWaterfallDomain, annualVarianceWaterfalls, basePortfolio, chartAxisColor, chartGridColor, driverViewAnchor, driverViewDeltas, driverViewDisclosureDeltas, executiveBridgeDomain, executiveBridgeRows, focusedResult, forecastSeries, formatMetricValue, formatMillions, formatPercent, ftpAnnual, marketInputs, model, multiYearWaterfallRows, newProductionMonthly, resultsBasis, resultsFocusYear, resultsView, setResultsBasis, setResultsFocusYear, setResultsView, shocks, tooltipStyle, varianceLineRows }} />;
      break;
    case 'review':
      activeStageContent = <RegionalReviewStage vm={{ Calculator, CheckCircle2, Database, FORECAST_DATA_SOURCE, ForecastAllocation, ForecastChangeDrawer, ForecastValidationList, History, Lock, MODEL_VERSION, RefreshCw, STATUS_LABELS, ShieldCheck, UploadCloud, User, activeUser, activeWorkspaceStage, aggregateUnits, aiSummary, aiSummaryBusy, annualResults, approveLoad, calculationRows, canEditSubmission, commentaryRequired, constraintSummary, expenseInputs, feeDrivers, forecastLoadRows, formatLineageValue, formatMillions, generateAiSummary, hasBlockingRows, hireRows, isAggregate, isLiaison, isTransitioning, isWritingForecast, lineage, lineageHistoryEntries, loadApproved, loadCompleted, loadResult, loadState, macroMode, marketInputs, missingNarratives, narrativeTriggers, narratives, navigateToValidationIssue, recallSubmission, recentLineage, reviewValidationIssues, scenario, scenarioVersion, selectedMarket, setLoadState, setNarratives, shocks, stagedRows, submission, submissionId, submissionStatus, submitToFpa, totalOverrideCount, validationCounts, workforceInputs, workforceYearInputs, workspaceBlockingIssues, writeForecast, yearAdjustments, qualityGates, blockingQualityGates, qualityOverrides, setQualityOverrides, gateBlockers, isFpa }} />;
      break;
    default:
      break;
  }

  return (
    <ForecastWorkspaceShell
      eyebrow={<><Activity size={14} aria-hidden="true" /> Forecast Console / 60-Month Plan</>}
      title={isAggregate
        ? `Aggregate Forecast · ${aggregateUnits.length || aggregateUnitIds.length} Units`
        : submission ? `${submission.division} · ${submission.market}` : 'Regional Bank Forecasting'}
      description="Review the inherited baseline and planning context, adjust controllable assumptions, and submit an auditable forecast package."
      backTo={submissionId ? '/forecasting' : undefined}
      backLabel="Cycle Home"
      status={submissionId ? STATUS_LABELS[submissionStatus] || submissionStatus : isAggregate ? 'Aggregate' : 'Working Forecast'}
      saveState={saveState === 'saving' || saveState === 'pending' ? 'Saving…'
        : saveState === 'error' ? 'Save Failed' : canEditSubmission ? 'Saved' : 'Read-Only'}
      metadata={[
        { label: 'Cycle', value: submission?.cycle_code || 'FY2027 Budget' },
        { label: 'Planning Entity', value: selectedMarket.planningEntity },
        { label: 'Forecast Version', value: scenario },
        { label: 'Baseline Source', value: baselineSource.status }
      ]}
      stages={regionalStages}
      activeStage={activeWorkspaceStage}
      impactItems={[
        {
          id: 'progress', label: 'Input Progress', progress: inputProgress.percent,
          progressLabel: 'Business unit forecast input completion',
          tone: inputProgress.percent === 100 ? 'positive' : 'neutral', detail: inputProgress.detail
        },
        {
          id: 'net-income', label: 'Net Income',
          metrics: [
            {
              id: 'next-quarter', label: 'Next Qtr',
              value: liveImpactSummary.quarterNetIncome !== null ? formatMillions(liveImpactSummary.quarterNetIncome) : '—',
              comparison: liveImpactSummary.comparisonHydrated && Number.isFinite(liveImpactSummary.quarterNetIncomeVariance) ? {
                value: formatSignedPercent(liveImpactSummary.quarterNetIncomeVariance),
                label: 'vs baseline forecast',
                tone: varianceTone(liveImpactSummary.quarterNetIncomeVariance)
              } : null,
              tone: varianceTone(liveImpactSummary.quarterNetIncomeVariance)
            },
            {
              id: 'fy1', label: 'FY1',
              value: liveImpactSummary.fy1NetIncome !== null ? formatMillions(liveImpactSummary.fy1NetIncome) : '—',
              comparison: liveImpactSummary.comparisonHydrated && Number.isFinite(liveImpactSummary.fy1NetIncomeVariance) ? {
                value: formatSignedPercent(liveImpactSummary.fy1NetIncomeVariance),
                label: 'vs baseline forecast',
                tone: varianceTone(liveImpactSummary.fy1NetIncomeVariance)
              } : null,
              tone: varianceTone(liveImpactSummary.fy1NetIncomeVariance)
            }
          ]
        },
        {
          id: 'loan-growth', label: 'Loan Balance Growth',
          metrics: [
            {
              id: 'next-quarter', label: 'Next Qtr',
              value: liveImpactSummary.quarterLoanGrowth !== null ? formatPercent(liveImpactSummary.quarterLoanGrowth) : '—',
              comparison: liveImpactSummary.comparisonHydrated && Number.isFinite(liveImpactSummary.quarterLoanGrowthVariance) ? {
                value: formatSignedPoints(liveImpactSummary.quarterLoanGrowthVariance),
                label: 'vs baseline forecast',
                tone: varianceTone(liveImpactSummary.quarterLoanGrowthVariance)
              } : null,
              tone: varianceTone(liveImpactSummary.quarterLoanGrowthVariance)
            },
            {
              id: 'fy1', label: 'FY1',
              value: liveImpactSummary.fy1LoanGrowth !== null ? formatPercent(liveImpactSummary.fy1LoanGrowth) : '—',
              comparison: liveImpactSummary.comparisonHydrated && Number.isFinite(liveImpactSummary.fy1LoanGrowthVariance) ? {
                value: formatSignedPoints(liveImpactSummary.fy1LoanGrowthVariance),
                label: 'vs baseline forecast',
                tone: varianceTone(liveImpactSummary.fy1LoanGrowthVariance)
              } : null,
              tone: varianceTone(liveImpactSummary.fy1LoanGrowthVariance)
            }
          ]
        },
        {
          id: 'deposit-growth', label: 'Deposit Balance Growth',
          metrics: [
            {
              id: 'next-quarter', label: 'Next Qtr',
              value: liveImpactSummary.quarterDepositGrowth !== null ? formatPercent(liveImpactSummary.quarterDepositGrowth) : '—',
              comparison: liveImpactSummary.comparisonHydrated && Number.isFinite(liveImpactSummary.quarterDepositGrowthVariance) ? {
                value: formatSignedPoints(liveImpactSummary.quarterDepositGrowthVariance),
                label: 'vs baseline forecast',
                tone: varianceTone(liveImpactSummary.quarterDepositGrowthVariance)
              } : null,
              tone: varianceTone(liveImpactSummary.quarterDepositGrowthVariance)
            },
            {
              id: 'fy1', label: 'FY1',
              value: liveImpactSummary.fy1DepositGrowth !== null ? formatPercent(liveImpactSummary.fy1DepositGrowth) : '—',
              comparison: liveImpactSummary.comparisonHydrated && Number.isFinite(liveImpactSummary.fy1DepositGrowthVariance) ? {
                value: formatSignedPoints(liveImpactSummary.fy1DepositGrowthVariance),
                label: 'vs baseline forecast',
                tone: varianceTone(liveImpactSummary.fy1DepositGrowthVariance)
              } : null,
              tone: varianceTone(liveImpactSummary.fy1DepositGrowthVariance)
            }
          ]
        },
        {
          id: 'planning-inputs', label: 'Planning Inputs', compact: true,
          metrics: [
            { id: 'modified', label: 'Modified', value: String(totalOverrideCount) },
            { id: 'case', label: 'Planning Case', value: macroMode }
          ]
        }
      ]}
      planningTools={!isAggregate ? <RegionalAiPlanningTools vm={{ ForecastContextNotes, ForecastPromptBox, activeProfile, applyPromptChangeset, canEditSubmission: canEditEffective, isAggregate, macroCase: macroMode, macroMode, promptCurrentAnnual, promptLeverSensitivity, refinePromptLever, selectedMarket, simulatePromptChangeset, submission, submissionId, undoPromptChangeset, yearAdjustments, fpaAdjustMode, fpaNetEffect, fpaSaveState, saveFpaAdjustment, discardFpaAdjustment }} /> : null}
      secondaryActions={submissionId ? <>
        <input
          ref={excelImportRef}
          type="file"
          accept=".xlsx"
          aria-label="Import forecast workbook"
          style={{ display: 'none' }}
          onChange={(event) => previewForecastWorkbookImport(event.target.files?.[0])}
        />
        <button type="button" className="forecast-secondary-button compact" disabled={excelBusy !== null} onClick={exportForecastWorkbook}>
          <Download size={15} aria-hidden="true" /> {excelBusy === 'export' ? 'Exporting…' : 'Export Excel'}
        </button>
        <button type="button" className="forecast-secondary-button compact" disabled={excelBusy !== null || !canEditSubmission} onClick={() => excelImportRef.current?.click()}>
          <UploadCloud size={15} aria-hidden="true" /> {excelBusy === 'preview' ? 'Reading…' : 'Import Excel'}
        </button>
      </> : null}
    >

      {canEditEffective && activeWorkspaceStage === 'levers' && (
        <div className="forecast-submit-chips" style={{ marginBottom: 12 }}>
          <label className="forecast-health-chip neutral" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={autoApplyLeverPreview}
              onChange={(event) => setAutoApplyLeverPreview(event.target.checked)}
            />
            Auto-apply without preview
          </label>
        </div>
      )}

      {pendingLeverPreview && (
        <section className="forecast-panel forecast-sticky-section" id="forecast-manual-impact-preview">
          <div className="forecast-panel-head">
            <div>
              <span className="forecast-section-code">OP_03I · Manual impact preview</span>
              <h3>
                {pendingLeverPreview.mode === 'applied' ? 'Last lever change' : 'Review before applying'} · {pendingLeverPreview.title}
              </h3>
              {pendingLeverPreview.detail && (
                <p className="forecast-submit-note">{pendingLeverPreview.detail}</p>
              )}
            </div>
            <Calculator size={18} aria-hidden="true" />
          </div>
          <div className="forecast-submit-chips">
            <span className={`forecast-health-chip ${pendingLeverPreview.mode === 'applied' ? 'green' : 'gold'}`}>
              {pendingLeverPreview.mode === 'applied' ? 'Applied to model' : 'Pending model change'}
            </span>
            <span className="forecast-health-chip neutral">
              Deterministic model diff
            </span>
          </div>
          <ForecastImpactPreviewTable
            diffLines={pendingLeverPreview.diffLines}
            years={ADJUSTMENT_YEARS}
            classifyLine={classifyPendingPreviewLine}
            emptyMessage="No material line movement from this lever change."
          />
          <div className="forecast-prompt-actions">
            {pendingLeverPreview.mode !== 'applied' && (
              <button type="button" className="forecast-primary-button compact" onClick={commitPendingLeverPreview}>
                Apply
              </button>
            )}
            <button type="button" className="forecast-secondary-button compact" onClick={undoPendingLeverPreview}>
              Undo
            </button>
          </div>
        </section>
      )}

      <RegionalWorkspaceNotices vm={{ AlertTriangle, CheckCircle2, UploadCloud, XCircle, applyForecastWorkbookImport, canEditSubmission, excelBusy, excelImportPreview, excelNotice, lastSavedAt, returnedComment, retrySave, saveError, saveState, setExcelImportPreview, workflowError }} />
      {activeStageContent}

    </ForecastWorkspaceShell>
  );
};

export default RegionalForecastingModule;
